/**
 * ShareCard.js — Canvas-based share image generator for Sirat
 * item shape: { kind: 'verse'|'hadith'|'jumuah'|'eid'|'innalillahi'|'nikah'|'hajj'|'newbaby'|'birthday',
 *               ar, en, ref, narrator?, message?, extra?,
 *               recipient?, greetingAr?, greetingTitle?, hijri?, eidKind?,
 *               eidType?, deceasedName?, groomName?, brideName?,
 *               hajjType?, pilgrimName?, babyName?, babyGender?, birthDate?, weight?, parentNames?,
 *               recipientName? }
 * extra (jumuah only): { ar, en, ref } — Friday/Eid hadith shown beneath the message
 * style: 'paper' (Paper & Ink) | 'night' (Night Sky)
 */

export async function generateShareCardBlob(item, style = 'paper', sharedBy = '') {
  if (item.kind === 'jumuah') {
    const drawJumuah = style === 'night' ? drawJumuahCard_night : drawJumuahCard_paper;
    const name = (sharedBy || '').trim() || 'Friend';
    const recipient = (item.recipient || '').trim();
    const canvas = drawJumuah({
      userName: name,
      userNameIsArabic: item.userNameIsArabic || hasArabic(name),
      userNameAr: (item.userNameAr || '').trim(),
      message: (item.message || '').trim() || 'May Allah accept your duas and bless your day.',
      extra: item.extra || null,
      recipient,
      recipientIsArabic: item.recipientIsArabic || hasArabic(recipient),
      recipientAr: (item.recipientAr || '').trim(),
      greetingAr: (item.greetingAr || '').trim() || 'جُمُعَةٌ مُبَارَكَةٌ',
      greetingTitle: (item.greetingTitle || '').trim() || "Jumu'ah Mubarak",
      hijri: (item.hijri || '').trim(),
      eidKind: item.eidKind || null,
    });
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  }
  if (item.kind === 'eid') {
    await ensureEidFontsLoaded();
    const themeId = item.eidTheme || (style === 'night' ? 'obsidian' : 'parchment');
    const canvas = drawEidCard({
      themeId,
      name: (sharedBy || '').trim() || 'Friend',
      blessing: (item.message || '').trim() || EID_DEFAULT_BLESSINGS[item.eidType || 'fitr'],
      personalNote: (item.personalNote || '').trim(),
      eidType: item.eidType || 'fitr',
    });
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  }
  if (item.kind === 'innalillahi') {
    const draw = style === 'night' ? drawInnaLillahiCard_night : drawInnaLillahiCard_paper;
    const canvas = draw({
      userName: (sharedBy || '').trim() || 'Friend',
      deceasedName: (item.deceasedName || '').trim(),
      deceasedNameIsArabic: hasArabic((item.deceasedName || '').trim()),
      message: (item.message || '').trim() || 'Please remember them and their family in your du\'as.',
      hijri: (item.hijri || '').trim(),
    });
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  }
  if (item.kind === 'nikah') {
    const draw = style === 'night' ? drawNikahCard_night : drawNikahCard_paper;
    const canvas = draw({
      groomName: (item.groomName || '').trim(),
      groomNameIsArabic: hasArabic((item.groomName || '').trim()),
      brideName: (item.brideName || '').trim(),
      brideNameIsArabic: hasArabic((item.brideName || '').trim()),
      message: (item.message || '').trim() || 'May Allah bless this union and fill your home with love, mercy and barakah.',
      hijri: (item.hijri || '').trim(),
      userName: (sharedBy || '').trim() || 'Friend',
    });
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  }
  if (item.kind === 'hajj') {
    const draw = style === 'night' ? drawHajjCard_night : drawHajjCard_paper;
    const canvas = draw({
      pilgrimName: (item.pilgrimName || sharedBy || '').trim() || 'Friend',
      pilgrimNameIsArabic: hasArabic((item.pilgrimName || sharedBy || '').trim()),
      hajjType: item.hajjType || 'hajj',
      message: (item.message || '').trim() || 'Please make du\'a for my safe journey and accepted pilgrimage.',
      departureDate: (item.departureDate || '').trim(),
      hijri: (item.hijri || '').trim(),
    });
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  }
  if (item.kind === 'newbaby') {
    const draw = style === 'night' ? drawNewBabyCard_night : drawNewBabyCard_paper;
    const canvas = draw({
      babyName: (item.babyName || '').trim(),
      babyNameIsArabic: hasArabic((item.babyName || '').trim()),
      babyGender: item.babyGender || 'boy',
      birthDate: (item.birthDate || '').trim(),
      weight: (item.weight || '').trim(),
      parentNames: (item.parentNames || sharedBy || '').trim() || 'The Family',
      parentNamesIsArabic: hasArabic((item.parentNames || '').trim()),
      message: (item.message || '').trim(),
    });
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  }
  if (item.kind === 'khatm-progress') {
    const draw = style === 'night' ? drawKhatmProgressCard_night : drawKhatmProgressCard_paper;
    const canvas = draw({
      marhoumName: (item.marhoumName || '').trim(),
      surahName: (item.surahName || '').trim(),
      surahArabic: (item.surahArabic || '').trim(),
      totalRead: item.totalRead || 0,
      target: item.target || null,
      openEnded: !!item.openEnded,
      pct: item.pct || 0,
      participants: item.participants || [],
      honoureeStatus: item.honoureeStatus || 'living',
      sharedBy: (sharedBy || '').trim(),
    });
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  }
  if (item.kind === 'khatm-close') {
    const draw = style === 'night' ? drawKhatmCloseCard_night : drawKhatmCloseCard_paper;
    const canvas = draw({
      marhoumName: (item.marhoumName || '').trim(),
      surahName: (item.surahName || '').trim(),
      surahArabic: (item.surahArabic || '').trim(),
      totalRead: item.totalRead || 0,
      participants: item.participants || [],
      honoureeStatus: item.honoureeStatus || 'living',
      sharedBy: (sharedBy || '').trim(),
    });
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  }
  if (item.kind === 'shifa') {
    const draw = style === 'night' ? drawShifaCard_night : drawShifaCard_paper;
    const canvas = draw({
      patientName: (item.patientName || '').trim(),
      patientNameIsArabic: hasArabic((item.patientName || '').trim()),
      message: (item.message || '').trim() || 'May Allah grant you a swift and complete recovery.',
      sharedBy: (sharedBy || '').trim(),
    });
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  }
  if (item.kind === 'birthday') {
    const draw = style === 'night' ? drawBirthdayCard_night : drawBirthdayCard_paper;
    const canvas = draw({
      recipientName: (item.recipientName || '').trim(),
      recipientNameIsArabic: hasArabic((item.recipientName || '').trim()),
      message: (item.message || '').trim() || 'May Allah bless you with many more years of health, happiness and imaan.',
      sharedBy: (sharedBy || '').trim(),
    });
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  }
  if (item.kind === 'dhulhijjah') {
    const draw = style === 'night' ? drawDhulHijjahCard_night : drawDhulHijjahCard_paper;
    const canvas = draw({
      message: (item.message || '').trim() || 'May Allah accept your fasts, duas and good deeds in these blessed days.',
      sharedBy: (sharedBy || '').trim(),
    });
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  }
  if (item.kind === 'congratulations') {
    const draw = style === 'night' ? drawCongratulationsCard_night : drawCongratulationsCard_paper;
    const canvas = draw({
      recipientName: (item.recipientName || '').trim(),
      recipientNameIsArabic: hasArabic((item.recipientName || '').trim()),
      achievement: (item.achievement || '').trim(),
      message: (item.message || '').trim() || 'May Allah bless your efforts and grant you barakah in your knowledge.',
      sharedBy: (sharedBy || '').trim(),
    });
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  }
  const drawFn = style === 'night' ? drawShareCard_night : drawShareCard_paper;
  const canvas = drawFn({
    kind: item.kind,
    arabic: item.ar || '',
    english: item.en || '',
    source: item.ref || '',
    narrator: item.narrator || null,
    sharedBy,
  });
  return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
}

// ─────────────────────────────────────────────────────────────
// Helper — word-wrap for any font
// ─────────────────────────────────────────────────────────────
function wrap(ctx, text, font, maxW){
  ctx.font = font;
  const words = text.split(' ');
  const lines = []; let line = '';
  for (const w of words){
    const test = line ? line + ' ' + w : w;
    if (ctx.measureText(test).width > maxW){ if(line) lines.push(line); line = w; }
    else line = test;
  }
  if (line) lines.push(line);
  return lines;
}

// Fit a chunk of text to a max vertical space by scaling font size down
function fitLines(ctx, text, fontFn, maxW, maxH, lineGapMul, startSize){
  let size = startSize;
  while (size > 26){
    const font = fontFn(size);
    const lines = wrap(ctx, text, font, maxW);
    const lineH = size * lineGapMul;
    if (lines.length * lineH <= maxH) return { lines, size, lineH, font };
    size -= 2;
  }
  return { lines: [text], size, lineH: size*lineGapMul, font: fontFn(size) };
}

// Footer: "Shared by <name>" + "GET THE APP · SIRAT.CO.ZA"
function drawSharedByStrip(ctx, W, H, { nameColor, urlColor, subColor, y, sharedBy }){
  ctx.textAlign = 'center';
  if (sharedBy && sharedBy.trim()){
    ctx.font = "700 17px 'JetBrains Mono', monospace";
    ctx.fillStyle = subColor;
    ctx.fillText('S H A R E D   B Y', W/2, y - 64);
    ctx.font = "italic 600 36px 'Instrument Serif','Georgia',serif";
    ctx.fillStyle = nameColor;
    ctx.fillText(sharedBy.trim(), W/2, y - 24);
  }
  // "GET THE APP" cue
  ctx.font = "700 18px 'JetBrains Mono', monospace";
  ctx.fillStyle = subColor;
  ctx.fillText('G E T   T H E   A P P', W/2, y);
  // URL — large, bold, unmissable. WhatsApp recipients can't scan a QR on their own
  // screen, so we make the URL easy to read and type instead.
  ctx.font = "800 44px 'JetBrains Mono', monospace";
  ctx.fillStyle = urlColor;
  ctx.fillText('sirat.co.za', W/2, y + 44);
}

// Helper — detect any Arabic Unicode in a string (for calligraphy auto-switch)
function hasArabic(s){ return typeof s === 'string' && /[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF]/.test(s); }

// ═════════════════════════════════════════════════════════════
// OPTION B — PAPER & INK
// Warm cream paper, ink-black Arabic, dark gold accents.
// Editorial and timeless — reads like a quiet manuscript page.
// ═════════════════════════════════════════════════════════════
function drawShareCard_paper({ kind, arabic, english, source, narrator, sharedBy }){
  const W = 1080, H = 1080, pad = 64;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');

  // Warm cream paper
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#f4ead4');
  bg.addColorStop(1, '#e8d9b0');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Paper grain
  ctx.fillStyle = 'rgba(56,42,20,0.06)';
  for (let i=0; i<1400; i++){
    const x = Math.random()*W, y = Math.random()*H;
    ctx.fillRect(x, y, 1, 1);
  }

  // 8-point star ornament (faint) — top centered
  ctx.save();
  ctx.translate(W/2, pad + 24);
  ctx.strokeStyle = 'rgba(56,42,20,0.18)';
  ctx.lineWidth = 1;
  const r = 28;
  for (let i=0; i<8; i++){
    ctx.save();
    ctx.rotate((Math.PI*2/8)*i);
    ctx.beginPath();
    ctx.moveTo(0, -r);
    ctx.lineTo(r*0.3, 0);
    ctx.lineTo(0, r);
    ctx.lineTo(-r*0.3, 0);
    ctx.closePath();
    ctx.stroke();
    ctx.restore();
  }
  ctx.restore();

  // Eyebrow
  ctx.textAlign = 'center';
  ctx.font = "700 26px 'JetBrains Mono', monospace";
  ctx.fillStyle = '#8c6b1f';
  const eyebrow = kind==='verse' ? 'V E R S E   O F   T H E   D A Y' : 'H A D I T H   O F   T H E   D A Y';
  ctx.fillText(eyebrow, W/2, pad + 76);

  // Content area — bottom pulled up to leave room for source/narrator + enlarged CTA strip
  const contentTop = pad + 110;
  const contentBottom = H - pad - 230;
  const contentH = contentBottom - contentTop;
  const maxW = W - pad*2 - 20;

  const arFit = fitLines(ctx, arabic, s => `700 ${s}px 'Scheherazade New','Amiri',serif`, maxW, contentH*0.52, 1.55, 98);
  const enFit = fitLines(ctx, english, s => `italic 400 ${s}px 'Instrument Serif','Georgia',serif`, maxW - 60, contentH*0.42, 1.4, 56);

  const totalH = arFit.lines.length*arFit.lineH + 44 + enFit.lines.length*enFit.lineH;
  let y = contentTop + (contentH - totalH)/2 + arFit.size*0.78;

  // Arabic — ink
  ctx.font = arFit.font;
  ctx.fillStyle = '#1a1208';
  for (const line of arFit.lines){ ctx.fillText(line, W/2, y); y += arFit.lineH; }

  // Divider — gold rule flanked by small dots
  y += 18;
  ctx.fillStyle = '#8c6b1f';
  ctx.fillRect(W*0.35, y, W*0.3, 1);
  ctx.beginPath(); ctx.arc(W*0.35 - 8, y+0.5, 2, 0, Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.arc(W*0.65 + 8, y+0.5, 2, 0, Math.PI*2); ctx.fill();
  y += 34;

  // English — italic, dark
  ctx.font = enFit.font;
  ctx.fillStyle = 'rgba(26,18,8,0.78)';
  for (const line of enFit.lines){ ctx.fillText(line, W/2, y); y += enFit.lineH; }

  // Source + narrator — positioned above the enlarged CTA strip so they never overlap
  // 'Shared by' / 'GET THE APP' / 'sirat.co.za'.
  const srcY = H - pad - 190;
  ctx.font = "700 21px 'JetBrains Mono', monospace";
  ctx.fillStyle = '#8c6b1f';
  ctx.fillText(source.toUpperCase(), W/2, srcY);
  if (narrator){
    ctx.font = "italic 400 18px 'Instrument Serif',serif";
    ctx.fillStyle = 'rgba(56,42,20,0.55)';
    ctx.fillText(narrator, W/2, srcY + 22);
  }

  // Shared-by + CTA strip — dark on cream
  drawSharedByStrip(ctx, W, H, {
    nameColor:'#1a1208',
    urlColor:'#8c6b1f',
    subColor:'rgba(26,18,8,0.6)',
    y: H - pad - 70, sharedBy
  });

  return c;
}

// ═════════════════════════════════════════════════════════════
// OPTION C — NIGHT SKY
// Near-black; English translation is the typographic hero.
// Arabic is a smaller gold opener above. Modern, IG-native.
// ═════════════════════════════════════════════════════════════
function drawShareCard_night({ kind, arabic, english, source, narrator, sharedBy }){
  const W = 1080, H = 1080, pad = 74;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');

  // Near-black with subtle vignette
  ctx.fillStyle = '#07080b';
  ctx.fillRect(0, 0, W, H);
  const vig = ctx.createRadialGradient(W/2, H/2, W*0.3, W/2, H/2, W*0.8);
  vig.addColorStop(0, 'rgba(255,255,255,0)');
  vig.addColorStop(1, 'rgba(0,0,0,0.6)');
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, W, H);

  // Top-left gold index line
  ctx.strokeStyle = 'rgba(197,155,58,0.9)';
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(pad, pad + 20); ctx.lineTo(pad + 80, pad + 20); ctx.stroke();

  // Kind label + date (left)
  ctx.textAlign = 'left';
  ctx.font = "700 19px 'JetBrains Mono', monospace";
  ctx.fillStyle = 'rgba(197,155,58,0.85)';
  const eyebrow = kind==='verse' ? 'VERSE' : 'HADITH';
  const today = new Date().toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}).toUpperCase();
  ctx.fillText(`${eyebrow}  \u00B7  ${today}`, pad + 100, pad + 26);

  // Reference (right)
  ctx.textAlign = 'right';
  ctx.font = "700 19px 'JetBrains Mono', monospace";
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.fillText(source.toUpperCase(), W - pad, pad + 26);

  // Arabic — smaller gold, top-third, as a subtle opener
  const maxW = W - pad*2;
  const arFit = fitLines(ctx, arabic, s => `700 ${s}px 'Scheherazade New','Amiri',serif`, maxW, 300, 1.5, 68);
  ctx.textAlign = 'center';
  ctx.font = arFit.font;
  const arGrad = ctx.createLinearGradient(0, pad+140, 0, pad+140+arFit.lines.length*arFit.lineH);
  arGrad.addColorStop(0, '#e8c468');
  arGrad.addColorStop(1, '#a07d26');
  ctx.fillStyle = arGrad;
  let ay = pad + 160 + arFit.size*0.4;
  for (const line of arFit.lines){ ctx.fillText(line, W/2, ay); ay += arFit.lineH; }

  // Horizontal rule
  const ruleY = ay + 44;
  ctx.fillStyle = 'rgba(197,155,58,0.3)';
  ctx.fillRect(W/2 - 30, ruleY, 60, 1);

  // English — THE HERO. Large serif, slightly off-white.
  const enMaxW = W - pad*2 - 20;
  const enSpace = H - ruleY - 260;
  const enFit = fitLines(ctx, `\u201C${english}\u201D`, s => `400 ${s}px 'Instrument Serif','Georgia',serif`, enMaxW, enSpace, 1.18, 96);
  ctx.font = enFit.font;
  ctx.fillStyle = '#f0e6cf';
  let ey = ruleY + 70 + enFit.size*0.6;
  for (const line of enFit.lines){ ctx.fillText(line, W/2, ey); ey += enFit.lineH; }

  // Narrator (if present) — sits well above the enlarged CTA strip
  if (narrator){
    ctx.font = "italic 400 20px 'Instrument Serif',serif";
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.fillText(narrator, W/2, H - pad - 150);
  }

  // Shared-by + CTA strip
  drawSharedByStrip(ctx, W, H, {
    nameColor:'#f0e6cf',
    urlColor:'#c59b3a',
    subColor:'rgba(255,255,255,0.55)',
    y: H - pad - 70, sharedBy
  });

  return c;
}

// ═════════════════════════════════════════════════════════════
// JUMU'AH MUBARAK — PAPER & INK
// Warm cream paper, ink-black Arabic, user's name in cursive gold.
// The user's name is the hero of the card.
// ═════════════════════════════════════════════════════════════
function drawJumuahCard_paper({ userName, userNameIsArabic, userNameAr, message, extra, recipient, recipientIsArabic, recipientAr, greetingAr, greetingTitle, hijri, eidKind }){
  const nameFont = (size) => userNameIsArabic ? `700 ${size}px 'Scheherazade New','Amiri',serif` : `700 ${size}px 'Dancing Script', cursive`;
  const forFont = (size) => recipientIsArabic ? `700 ${size}px 'Scheherazade New','Amiri',serif` : `700 ${size}px 'Dancing Script', cursive`;
  const W = 1080, H = 1080, pad = 64;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');

  // Warm cream paper
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#f4ead4');
  bg.addColorStop(1, '#e8d9b0');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Paper grain
  ctx.fillStyle = 'rgba(56,42,20,0.06)';
  for (let i=0; i<1400; i++){
    const x = Math.random()*W, y = Math.random()*H;
    ctx.fillRect(x, y, 1, 1);
  }

  // Ornament — 8-point star (or crescent for Eid)
  ctx.save();
  ctx.translate(W/2, pad + 24);
  ctx.strokeStyle = 'rgba(56,42,20,0.22)';
  ctx.lineWidth = 1.4;
  if (eidKind) {
    // Crescent for Eid
    ctx.beginPath();
    ctx.arc(0, 0, 26, Math.PI*0.25, Math.PI*1.75);
    ctx.stroke();
  } else {
    const r = 28;
    for (let i=0; i<8; i++){
      ctx.save();
      ctx.rotate((Math.PI*2/8)*i);
      ctx.beginPath();
      ctx.moveTo(0, -r); ctx.lineTo(r*0.3, 0); ctx.lineTo(0, r); ctx.lineTo(-r*0.3, 0);
      ctx.closePath();
      ctx.stroke();
      ctx.restore();
    }
  }
  ctx.restore();

  // Eyebrow — Jumu'ah or Eid title in spaced mono
  ctx.textAlign = 'center';
  ctx.font = "700 22px 'JetBrains Mono', monospace";
  ctx.fillStyle = '#8c6b1f';
  ctx.fillText(greetingTitle.toUpperCase().split('').join(' '), W/2, pad + 70);

  // Arabic greeting (variable) — large ink
  ctx.font = "700 84px 'Scheherazade New','Amiri',serif";
  ctx.fillStyle = '#1a1208';
  let y = pad + 180;
  ctx.fillText(greetingAr, W/2, y);

  // Gold divider — above the first role label
  y += 44;
  ctx.fillStyle = '#8c6b1f';
  ctx.fillRect(W*0.38, y, W*0.24, 1);
  ctx.beginPath(); ctx.arc(W*0.38 - 8, y+0.5, 2, 0, Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.arc(W*0.62 + 8, y+0.5, 2, 0, Math.PI*2); ctx.fill();

  // Optional "For {recipient}" block
  if (recipient) {
    y += 28;
    ctx.font = "700 18px 'JetBrains Mono', monospace";
    ctx.fillStyle = 'rgba(140,107,31,0.72)';
    ctx.fillText('F O R', W/2, y);
    y += 14;
    let forSize = 78;
    const forMaxW = W - pad*2 - 60;
    while (forSize > 44) {
      ctx.font = forFont(forSize);
      if (ctx.measureText(recipient).width <= forMaxW) break;
      forSize -= 4;
    }
    ctx.font = forFont(forSize);
    ctx.fillStyle = '#a07d26';
    ctx.fillText(recipient, W/2, y + forSize*0.85);
    y += forSize*0.85 + 4;
    // Arabic form of the recipient's name(s)
    if (recipientAr && !recipientIsArabic) {
      ctx.font = "700 36px 'Scheherazade New','Amiri',serif";
      ctx.fillStyle = 'rgba(160,125,38,0.82)';
      ctx.fillText(recipientAr, W/2, y + 26);
      y += 36;
    }
    y += 10;
  }

  // "From" label
  y += 22;
  ctx.font = "700 18px 'JetBrains Mono', monospace";
  ctx.fillStyle = 'rgba(140,107,31,0.72)';
  ctx.fillText('F R O M', W/2, y);

  // Hero: user's name — auto-fit down
  y += 14;
  let nameSize = recipient ? 120 : 150;
  const nameMaxW = W - pad*2 - 40;
  while (nameSize > 72) {
    ctx.font = nameFont(nameSize);
    if (ctx.measureText(userName).width <= nameMaxW) break;
    nameSize -= 6;
  }
  ctx.font = nameFont(nameSize);
  ctx.fillStyle = '#8c6b1f';
  ctx.shadowColor = 'rgba(140,107,31,0.22)';
  ctx.shadowBlur = 14;
  ctx.fillText(userName, W/2, y + nameSize*0.85);
  ctx.shadowBlur = 0;
  y = y + nameSize*0.85 + 6;

  // Arabic transliteration of the user's name (Yusuf → يوسف)
  if (userNameAr && !userNameIsArabic) {
    ctx.font = "700 52px 'Scheherazade New','Amiri',serif";
    ctx.fillStyle = 'rgba(140,107,31,0.88)';
    ctx.fillText(userNameAr, W/2, y + 40);
    y += 56;
  }

  // Hijri stamp (signed-letter feel)
  if (hijri) {
    ctx.font = "600 16px 'JetBrains Mono', monospace";
    ctx.fillStyle = 'rgba(140,107,31,0.66)';
    ctx.fillText(hijri.toUpperCase().split('').join(' '), W/2, y + 14);
    y += 22;
  }

  // Gold divider below name
  y += 18;
  ctx.fillStyle = '#8c6b1f';
  ctx.fillRect(W*0.38, y, W*0.24, 1);
  ctx.beginPath(); ctx.arc(W*0.38 - 8, y+0.5, 2, 0, Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.arc(W*0.62 + 8, y+0.5, 2, 0, Math.PI*2); ctx.fill();

  // Personal message
  y += 36;
  const footerReserve = extra ? 260 : 120;
  const msgMaxW = W - pad*2 - 20;
  const msgMaxH = H - pad - footerReserve - y;
  const msgFit = fitLines(ctx, message, s => `italic 400 ${s}px 'Instrument Serif','Georgia',serif`, msgMaxW, Math.max(80, msgMaxH), 1.3, 34);
  ctx.font = msgFit.font;
  ctx.fillStyle = 'rgba(26,18,8,0.78)';
  let my = y + msgFit.size*0.75;
  for (const line of msgFit.lines){ ctx.fillText(line, W/2, my); my += msgFit.lineH; }
  y = my + 4;

  // Friday/Eid hadith/verse footer block
  if (extra) {
    y += 10;
    ctx.save();
    ctx.setLineDash([4, 6]);
    ctx.strokeStyle = 'rgba(140,107,31,0.35)';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(W*0.22, y); ctx.lineTo(W*0.78, y); ctx.stroke();
    ctx.restore();
    y += 30;

    const extraArFit = fitLines(ctx, extra.ar, s => `700 ${s}px 'Scheherazade New','Amiri',serif`, W - pad*2 - 40, 120, 1.4, 40);
    ctx.font = extraArFit.font;
    ctx.fillStyle = '#1a1208';
    let ay = y + extraArFit.size*0.78;
    for (const line of extraArFit.lines){ ctx.fillText(line, W/2, ay); ay += extraArFit.lineH; }
    y = ay + 4;

    const extraEnFit = fitLines(ctx, `"${extra.en}"`, s => `italic 400 ${s}px 'Instrument Serif','Georgia',serif`, W - pad*2 - 40, 80, 1.35, 26);
    ctx.font = extraEnFit.font;
    ctx.fillStyle = 'rgba(26,18,8,0.68)';
    let ey = y + extraEnFit.size*0.75;
    for (const line of extraEnFit.lines){ ctx.fillText(line, W/2, ey); ey += extraEnFit.lineH; }
    y = ey + 8;

    // Pin the reference to a fixed y above the CTA strip so it never overlaps
    // 'GET THE APP' or 'sirat.co.za' when the Arabic/English block is tall.
    ctx.font = "700 16px 'JetBrains Mono', monospace";
    ctx.fillStyle = '#8c6b1f';
    ctx.fillText(`— ${extra.ref.toUpperCase()}`, W/2, H - pad - 100);
  }

  // Footer — app URL
  drawSharedByStrip(ctx, W, H, {
    nameColor:'#1a1208', urlColor:'#8c6b1f', subColor:'rgba(26,18,8,0.6)',
    y: H - pad - 70, sharedBy: ''
  });

  return c;
}

// Night Sky variant — near-black, user's name in cursive gold hero.
function drawJumuahCard_night({ userName, userNameIsArabic, userNameAr, message, extra, recipient, recipientIsArabic, recipientAr, greetingAr, greetingTitle, hijri, eidKind }){
  const nameFont = (size) => userNameIsArabic ? `700 ${size}px 'Scheherazade New','Amiri',serif` : `700 ${size}px 'Dancing Script', cursive`;
  const forFont = (size) => recipientIsArabic ? `700 ${size}px 'Scheherazade New','Amiri',serif` : `700 ${size}px 'Dancing Script', cursive`;
  const W = 1080, H = 1080, pad = 74;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');

  // Near-black with vignette
  ctx.fillStyle = '#07080b';
  ctx.fillRect(0, 0, W, H);
  const vig = ctx.createRadialGradient(W/2, H/2, W*0.3, W/2, H/2, W*0.8);
  vig.addColorStop(0, 'rgba(255,255,255,0)');
  vig.addColorStop(1, 'rgba(0,0,0,0.6)');
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, W, H);

  // Crescent ornament for Eid (subtle gold outline)
  if (eidKind) {
    ctx.save();
    ctx.translate(W/2, pad + 30);
    ctx.strokeStyle = 'rgba(232,196,104,0.55)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, 22, Math.PI*0.25, Math.PI*1.75);
    ctx.stroke();
    ctx.restore();
  }

  // Eyebrow (spaced title)
  ctx.textAlign = 'center';
  ctx.font = "700 18px 'JetBrains Mono', monospace";
  ctx.fillStyle = 'rgba(197,155,58,0.9)';
  ctx.fillText(greetingTitle.toUpperCase().split('').join(' '), W/2, pad + (eidKind ? 70 : 38));

  // Arabic — gold gradient
  ctx.font = "700 76px 'Scheherazade New','Amiri',serif";
  const arGrad = ctx.createLinearGradient(0, pad+160, 0, pad+260);
  arGrad.addColorStop(0, '#e8c468');
  arGrad.addColorStop(1, '#a07d26');
  ctx.fillStyle = arGrad;
  let y = pad + (eidKind ? 200 : 180);
  ctx.fillText(greetingAr, W/2, y);

  // Thin gold rule
  y += 48;
  ctx.fillStyle = 'rgba(197,155,58,0.5)';
  ctx.fillRect(W/2 - 40, y, 80, 1);

  // Optional "For {recipient}"
  if (recipient) {
    y += 28;
    ctx.font = "700 16px 'JetBrains Mono', monospace";
    ctx.fillStyle = 'rgba(197,155,58,0.72)';
    ctx.fillText('F O R', W/2, y);
    y += 12;
    let forSize = 72;
    const forMaxW = W - pad*2 - 60;
    while (forSize > 40) {
      ctx.font = forFont(forSize);
      if (ctx.measureText(recipient).width <= forMaxW) break;
      forSize -= 4;
    }
    ctx.font = forFont(forSize);
    ctx.fillStyle = '#d4aa55';
    ctx.fillText(recipient, W/2, y + forSize*0.85);
    y += forSize*0.85 + 4;
    if (recipientAr && !recipientIsArabic) {
      ctx.font = "700 32px 'Scheherazade New','Amiri',serif";
      ctx.fillStyle = 'rgba(232,196,104,0.75)';
      ctx.fillText(recipientAr, W/2, y + 24);
      y += 30;
    }
    y += 8;
  }

  // "From" label
  y += 28;
  ctx.font = "700 18px 'JetBrains Mono', monospace";
  ctx.fillStyle = 'rgba(197,155,58,0.75)';
  ctx.fillText('F R O M', W/2, y);

  // Hero: user's name
  y += 14;
  let nameSize = recipient ? 118 : 148;
  const nameMaxW = W - pad*2 - 40;
  while (nameSize > 72) {
    ctx.font = nameFont(nameSize);
    if (ctx.measureText(userName).width <= nameMaxW) break;
    nameSize -= 6;
  }
  ctx.font = nameFont(nameSize);
  ctx.fillStyle = '#e8c468';
  ctx.shadowColor = 'rgba(232,196,104,0.4)';
  ctx.shadowBlur = 26;
  ctx.fillText(userName, W/2, y + nameSize*0.85);
  ctx.shadowBlur = 0;
  y = y + nameSize*0.85 + 6;

  // Arabic transliteration of the user's name
  if (userNameAr && !userNameIsArabic) {
    ctx.font = "700 48px 'Scheherazade New','Amiri',serif";
    ctx.fillStyle = '#e8c468';
    ctx.fillText(userNameAr, W/2, y + 36);
    y += 50;
  }

  // Thin gold rule
  y += 4;

  // Hijri stamp (signed-letter feel)
  if (hijri) {
    ctx.font = "600 14px 'JetBrains Mono', monospace";
    ctx.fillStyle = 'rgba(232,196,104,0.65)';
    ctx.fillText(hijri.toUpperCase().split('').join(' '), W/2, y + 14);
    y += 22;
  }

  y += 14;
  ctx.fillStyle = 'rgba(197,155,58,0.5)';
  ctx.fillRect(W/2 - 40, y, 80, 1);

  // Message
  y += 32;
  const footerReserve = extra ? 250 : 120;
  const msgMaxW = W - pad*2 - 20;
  const msgMaxH = H - pad - footerReserve - y;
  const msgFit = fitLines(ctx, message, s => `italic 400 ${s}px 'Instrument Serif','Georgia',serif`, msgMaxW, Math.max(80, msgMaxH), 1.28, 36);
  ctx.font = msgFit.font;
  ctx.fillStyle = '#f0e6cf';
  let my = y + msgFit.size*0.75;
  for (const line of msgFit.lines){ ctx.fillText(line, W/2, my); my += msgFit.lineH; }
  y = my + 4;

  // Friday hadith/verse footer block
  if (extra) {
    y += 10;
    ctx.save();
    ctx.setLineDash([4, 6]);
    ctx.strokeStyle = 'rgba(197,155,58,0.35)';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(W*0.22, y); ctx.lineTo(W*0.78, y); ctx.stroke();
    ctx.restore();
    y += 30;

    const extraArFit = fitLines(ctx, extra.ar, s => `700 ${s}px 'Scheherazade New','Amiri',serif`, W - pad*2 - 40, 130, 1.4, 40);
    ctx.font = extraArFit.font;
    ctx.fillStyle = '#e0c680';
    let ay = y + extraArFit.size*0.78;
    for (const line of extraArFit.lines){ ctx.fillText(line, W/2, ay); ay += extraArFit.lineH; }
    y = ay + 4;

    const extraEnFit = fitLines(ctx, `“${extra.en}”`, s => `italic 400 ${s}px 'Instrument Serif','Georgia',serif`, W - pad*2 - 40, 80, 1.35, 26);
    ctx.font = extraEnFit.font;
    ctx.fillStyle = 'rgba(240,230,207,0.75)';
    let ey = y + extraEnFit.size*0.75;
    for (const line of extraEnFit.lines){ ctx.fillText(line, W/2, ey); ey += extraEnFit.lineH; }
    y = ey + 8;

    // Pin the reference to a fixed y above the CTA strip so it never overlaps
    // 'GET THE APP' or 'sirat.co.za' when the Arabic/English block is tall.
    ctx.font = "700 16px 'JetBrains Mono', monospace";
    ctx.fillStyle = '#c59b3a';
    ctx.fillText(`— ${extra.ref.toUpperCase()}`, W/2, H - pad - 100);
  }

  // Footer
  drawSharedByStrip(ctx, W, H, {
    nameColor:'#f0e6cf',
    urlColor:'#c59b3a',
    subColor:'rgba(255,255,255,0.55)',
    y: H - pad - 70, sharedBy: ''
  });

  return c;
}
// Shared helpers for new card types
// ─────────────────────────────────────────────────────────────
function paperBg(ctx, W, H) {
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#f4ead4'); bg.addColorStop(1, '#e8d9b0');
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = 'rgba(56,42,20,0.06)';
  for (let i = 0; i < 1400; i++) { const x = Math.random()*W, y = Math.random()*H; ctx.fillRect(x, y, 1, 1); }
}
function nightBg(ctx, W, H) {
  ctx.fillStyle = '#07080b'; ctx.fillRect(0, 0, W, H);
  const vig = ctx.createRadialGradient(W/2, H/2, W*0.3, W/2, H/2, W*0.8);
  vig.addColorStop(0, 'rgba(255,255,255,0)'); vig.addColorStop(1, 'rgba(0,0,0,0.6)');
  ctx.fillStyle = vig; ctx.fillRect(0, 0, W, H);
}
function goldRule(ctx, W, y, paper) {
  const c = paper ? '#8c6b1f' : 'rgba(197,155,58,0.5)';
  ctx.fillStyle = c; ctx.fillRect(W*0.35, y, W*0.3, 1);
  if (paper) { ctx.beginPath(); ctx.arc(W*0.35-8, y+.5, 2, 0, Math.PI*2); ctx.fill(); ctx.beginPath(); ctx.arc(W*0.65+8, y+.5, 2, 0, Math.PI*2); ctx.fill(); }
}
function eyebrow(ctx, W, text, yPos, paper) {
  ctx.textAlign = 'center';
  ctx.font = "700 22px 'JetBrains Mono', monospace";
  ctx.fillStyle = paper ? '#8c6b1f' : 'rgba(197,155,58,0.9)';
  ctx.fillText(text.toUpperCase().split('').join(' '), W/2, yPos);
}
function nameHero(ctx, W, y, name, isArabic, size, paper) {
  const font = isArabic ? `700 ${size}px 'Scheherazade New','Amiri',serif` : `700 ${size}px 'Dancing Script', cursive`;
  ctx.font = font; ctx.textAlign = 'center';
  ctx.fillStyle = paper ? '#8c6b1f' : '#e8c468';
  ctx.shadowColor = paper ? 'rgba(140,107,31,0.22)' : 'rgba(232,196,104,0.4)';
  ctx.shadowBlur = paper ? 14 : 26;
  ctx.fillText(name, W/2, y + size*0.85);
  ctx.shadowBlur = 0;
  return y + size*0.85 + 6;
}
function bigArabic(ctx, W, y, text, paper) {
  ctx.font = "700 72px 'Scheherazade New','Amiri',serif"; ctx.textAlign = 'center';
  if (paper) {
    ctx.fillStyle = '#1a1208';
  } else {
    const g = ctx.createLinearGradient(0, y-60, 0, y+10);
    g.addColorStop(0, '#e8c468'); g.addColorStop(1, '#a07d26');
    ctx.fillStyle = g;
  }
  ctx.fillText(text, W/2, y);
}
function smallArabicLabel(ctx, W, y, text, paper) {
  ctx.font = "700 40px 'Scheherazade New','Amiri',serif"; ctx.textAlign = 'center';
  ctx.fillStyle = paper ? 'rgba(140,107,31,0.85)' : 'rgba(232,196,104,0.82)';
  ctx.fillText(text, W/2, y);
}
function message(ctx, W, y, text, maxH, paper) {
  const fit = fitLines(ctx, text, s => `italic 400 ${s}px 'Instrument Serif','Georgia',serif`, W-128-20, Math.max(60, maxH), 1.3, 32);
  ctx.font = fit.font; ctx.textAlign = 'center';
  ctx.fillStyle = paper ? 'rgba(26,18,8,0.78)' : '#f0e6cf';
  let my = y + fit.size*0.75;
  for (const line of fit.lines) { ctx.fillText(line, W/2, my); my += fit.lineH; }
  return my + 4;
}
function roleLabel(ctx, W, y, text, paper) {
  ctx.font = "700 17px 'JetBrains Mono', monospace"; ctx.textAlign = 'center';
  ctx.fillStyle = paper ? 'rgba(140,107,31,0.72)' : 'rgba(197,155,58,0.72)';
  ctx.fillText(text, W/2, y);
}
function autoFitName(ctx, name, isArabic, maxW, startSize, minSize) {
  const fontFn = s => isArabic ? `700 ${s}px 'Scheherazade New','Amiri',serif` : `700 ${s}px 'Dancing Script', cursive`;
  let sz = startSize;
  while (sz > minSize) { ctx.font = fontFn(sz); if (ctx.measureText(name).width <= maxW) break; sz -= 4; }
  return { font: fontFn(sz), size: sz };
}

// ═════════════════════════════════════════════════════════════
// EID MUBARAK — 6-theme designer (port of Claude Design output)
// 1080×1080 square. Themes: obsidian, parchment, desert, midnight,
// fajr, emerald. Ornaments + borders drawn directly on canvas.
// ═════════════════════════════════════════════════════════════

export const EID_THEMES = [
  {
    id: 'obsidian', name: 'Obsidian', sub: '& Gold',
    swatchBg: '#131005', swatchAccent: '#C8941A',
    bgStops: [['#0E0D06', 0], ['#1C1A08', 0.55], ['#0A0A06', 1]], bgAngle: 158,
    borderType: 'geometric', borderColor: '#C8941A',
    topEl: 'star', bottomEl: null,
    occasionColor: '#9E7214', arabicColor: '#E8C050',
    arabicGlow: { color: 'rgba(232,192,80,0.55)', blur: 24 },
    dividerColor: '#C8941A', fromColor: '#9E7214',
    nameColor: '#EDD070', nameGlow: { color: 'rgba(237,208,112,0.55)', blur: 22 },
    blessingColor: '#F0E4C0', noteColor: 'rgba(240,228,192,0.55)',
    brandLabelColor: 'rgba(200,148,26,0.4)', brandUrlColor: 'rgba(200,148,26,0.65)',
    hasStarField: false, hasGridTexture: true,
  },
  {
    id: 'parchment', name: 'Parchment', sub: '& Ink',
    swatchBg: '#F5EDCF', swatchAccent: '#5C3D11',
    bgStops: [['#F7EDD2', 0], ['#EDD9A4', 0.45], ['#E5C87E', 1]], bgAngle: 148,
    borderType: 'arabesque', borderColor: '#5C3D11',
    topEl: 'arch', bottomEl: null,
    occasionColor: '#7A5218', arabicColor: '#3C2408', arabicGlow: null,
    dividerColor: '#8B6520', fromColor: '#7A5218',
    nameColor: '#5A3008', nameGlow: null,
    blessingColor: '#4A3018', noteColor: 'rgba(74,48,24,0.6)',
    brandLabelColor: 'rgba(92,61,17,0.38)', brandUrlColor: 'rgba(92,61,17,0.6)',
    hasStarField: false, hasGridTexture: false,
  },
  {
    id: 'desert', name: 'Desert', sub: 'Dusk',
    swatchBg: '#C47A1E', swatchAccent: '#3D1A00',
    bgStops: [['#E8A020', 0], ['#C47A1E', 0.28], ['#8B4513', 0.58], ['#3D1A00', 1]], bgAngle: 180,
    borderType: 'lines', borderColor: 'rgba(255,220,130,0.5)',
    topEl: 'crescent', bottomEl: 'mosque',
    occasionColor: 'rgba(255,224,140,0.78)', arabicColor: '#FFF4D0',
    arabicGlow: { color: 'rgba(255,244,208,0.4)', blur: 18 },
    dividerColor: 'rgba(255,220,130,0.65)', fromColor: 'rgba(255,220,130,0.72)',
    nameColor: '#FFE870', nameGlow: { color: 'rgba(255,232,112,0.5)', blur: 20 },
    blessingColor: 'rgba(255,244,208,0.92)', noteColor: 'rgba(255,244,208,0.55)',
    brandLabelColor: 'rgba(255,220,130,0.32)', brandUrlColor: 'rgba(255,220,130,0.55)',
    hasStarField: false, hasGridTexture: false,
  },
  {
    id: 'midnight', name: 'Midnight', sub: 'Crescent',
    swatchBg: '#0C1A3A', swatchAccent: '#8AAAC8',
    bgStops: [['#05101F', 0], ['#0C1A3A', 0.5], ['#162848', 1]], bgAngle: 140,
    borderType: 'geometric', borderColor: '#4A6888',
    topEl: 'crescentStars', bottomEl: null,
    occasionColor: '#4A6888', arabicColor: '#C8D8E8',
    arabicGlow: { color: 'rgba(200,216,232,0.35)', blur: 20 },
    dividerColor: '#3A5878', fromColor: '#4A6888',
    nameColor: '#D8ECF8', nameGlow: { color: 'rgba(216,236,248,0.45)', blur: 20 },
    blessingColor: '#8AAABE', noteColor: 'rgba(138,170,190,0.55)',
    brandLabelColor: 'rgba(74,104,136,0.38)', brandUrlColor: 'rgba(74,104,136,0.62)',
    hasStarField: true, hasGridTexture: false,
  },
  {
    id: 'fajr', name: 'Fajr', sub: 'Dawn',
    swatchBg: '#3A1250', swatchAccent: '#E8A850',
    bgStops: [['#080416', 0], ['#220840', 0.18], ['#561A48', 0.42], ['#B85A50', 0.64], ['#E8A040', 1]], bgAngle: 180,
    borderType: 'lines', borderColor: 'rgba(240,205,140,0.42)',
    topEl: 'starDawn', bottomEl: 'mosqueFajr',
    occasionColor: 'rgba(240,205,140,0.72)', arabicColor: '#FFE8C0',
    arabicGlow: { color: 'rgba(255,232,192,0.5)', blur: 22 },
    dividerColor: 'rgba(240,205,140,0.5)', fromColor: 'rgba(240,205,140,0.68)',
    nameColor: '#FFD870', nameGlow: { color: 'rgba(255,216,112,0.5)', blur: 22 },
    blessingColor: 'rgba(255,236,196,0.88)', noteColor: 'rgba(255,236,196,0.52)',
    brandLabelColor: 'rgba(240,205,140,0.3)', brandUrlColor: 'rgba(240,205,140,0.5)',
    hasStarField: false, hasGridTexture: false,
  },
  {
    id: 'emerald', name: 'Emerald', sub: 'Garden',
    swatchBg: '#0A2E1A', swatchAccent: '#C8941A',
    bgStops: [['#061610', 0], ['#0A2E1A', 0.52], ['#0E3A20', 1]], bgAngle: 148,
    borderType: 'arabesque', borderColor: '#B8840E',
    topEl: 'lantern', bottomEl: null,
    occasionColor: '#9A7018', arabicColor: '#E8C870',
    arabicGlow: { color: 'rgba(232,200,112,0.45)', blur: 22 },
    dividerColor: '#A07818', fromColor: '#9A7018',
    nameColor: '#F0D880', nameGlow: { color: 'rgba(240,216,128,0.5)', blur: 22 },
    blessingColor: '#C8E0C4', noteColor: 'rgba(200,224,196,0.58)',
    brandLabelColor: 'rgba(184,132,14,0.38)', brandUrlColor: 'rgba(184,132,14,0.6)',
    hasStarField: false, hasGridTexture: false,
  },
];

export const EID_DEFAULT_BLESSINGS = {
  adha: 'May Allah accept our ibadah and the sacrifice of the Hujjaj, and grant us all a blessed Eid.',
  fitr: 'Taqabbal Allahu minna wa minkum. May Allah accept our fasts and grant us all a joyful Eid.',
};

export const EID_BLESSING_PRESETS = {
  adha: [
    'May Allah accept our ibadah and the sacrifice of the Hujjaj, and grant us all a blessed Eid.',
    'Taqabbal Allahu minna wa minkum. May Allah accept from us and from you.',
    'May the spirit of sacrifice and devotion fill your home with blessings this Eid.',
    'May this Eid bring you closer to Allah and fill your heart with gratitude and peace.',
    'Wishing you and your loved ones a blessed and joyful Eid ul-Adha. Eid Mubarak!',
  ],
  fitr: [
    'Taqabbal Allahu minna wa minkum. May Allah accept our fasts and grant us all a joyful Eid.',
    'May this Eid mark the end of your fasts and the beginning of abundant blessings.',
    'After a blessed Ramadan, may this Eid bring joy, peace, and mercy to you and your family.',
    'May Allah accept your fasts, forgive your sins, and shower your home with His mercy.',
    'Wishing you and your loved ones a beautiful and blessed Eid ul-Fitr. Eid Mubarak!',
  ],
};

let _eidFontsLoaded = false;
async function ensureEidFontsLoaded() {
  if (_eidFontsLoaded) return;
  if (typeof document === 'undefined' || !document.fonts) { _eidFontsLoaded = true; return; }
  try {
    await Promise.all([
      document.fonts.load("700 110px 'Amiri'"),
      document.fonts.load("400 28px 'Amiri'"),
      document.fonts.load("700 180px 'Tangerine'"),
      document.fonts.load("italic 400 48px 'Cormorant Garamond'"),
      document.fonts.load("italic 400 36px 'Cormorant Garamond'"),
      document.fonts.load("500 32px 'Raleway'"),
      document.fonts.load("700 24px 'Raleway'"),
    ]);
  } catch (_) { /* fall through */ }
  _eidFontsLoaded = true;
}

// ─── Ornaments ───────────────────────────────────────────────
function eidFillGradient(ctx, W, H, theme) {
  const ang = (theme.bgAngle - 90) * Math.PI / 180;
  const dx = Math.cos(ang), dy = Math.sin(ang);
  const cx = W / 2, cy = H / 2;
  const half = Math.abs(dx) * W / 2 + Math.abs(dy) * H / 2;
  const g = ctx.createLinearGradient(cx - dx * half, cy - dy * half, cx + dx * half, cy + dy * half);
  theme.bgStops.forEach(([col, pos]) => g.addColorStop(pos, col));
  return g;
}

function drawEidStar8(ctx, cx, cy, size, color) {
  const outer = size * 0.46, inner = size * 0.185;
  ctx.beginPath();
  for (let i = 0; i < 16; i++) {
    const a = (i * Math.PI) / 8 - Math.PI / 2;
    const r = i % 2 === 0 ? outer : inner;
    const x = cx + r * Math.cos(a), y = cy + r * Math.sin(a);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fillStyle = color; ctx.fill();
}

function drawEidCrescent(ctx, cx, cy, size, color) {
  // 44-unit base SVG mapped to size
  const s = size / 44;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(s, s);
  ctx.translate(-22, -22);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(22, 22, 17, 0, Math.PI * 2);
  ctx.arc(29, 20, 13.5, 0, Math.PI * 2, true);
  ctx.fill('evenodd');
  ctx.restore();
}

function drawEidLantern(ctx, cx, cy, size, color) {
  // SVG viewBox 46×76 — scale so vertical fits `size * 1.65`
  const targetH = size * 1.65;
  const s = targetH / 76;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(s, s);
  ctx.translate(-23, -38);
  const stroke = (w, op = 1, fn) => { ctx.strokeStyle = color; ctx.globalAlpha = op; ctx.lineWidth = w; fn(); ctx.globalAlpha = 1; };
  const fill = (op = 1, fn) => { ctx.fillStyle = color; ctx.globalAlpha = op; fn(); ctx.globalAlpha = 1; };
  stroke(1.5, 1, () => { ctx.beginPath(); ctx.moveTo(23, 0); ctx.lineTo(23, 9); ctx.stroke(); });
  fill(1, () => { ctx.beginPath(); ctx.moveTo(13, 9); ctx.quadraticCurveTo(23, 6, 33, 9); ctx.lineTo(31, 16); ctx.quadraticCurveTo(23, 13, 15, 16); ctx.closePath(); ctx.fill(); });
  stroke(1.6, 1, () => { ctx.strokeRect(14, 16, 18, 36); });
  stroke(0.75, 0.55, () => { ctx.beginPath(); ctx.moveTo(20, 16); ctx.lineTo(20, 52); ctx.moveTo(26, 16); ctx.lineTo(26, 52); ctx.moveTo(14, 28); ctx.lineTo(32, 28); ctx.moveTo(14, 40); ctx.lineTo(32, 40); ctx.stroke(); });
  fill(0.13, () => { ctx.fillRect(16, 18, 14, 32); });
  fill(1, () => { ctx.beginPath(); ctx.moveTo(15, 52); ctx.quadraticCurveTo(23, 55, 31, 52); ctx.lineTo(30, 59); ctx.quadraticCurveTo(23, 62, 16, 59); ctx.closePath(); ctx.fill(); });
  stroke(1.5, 1, () => { ctx.beginPath(); ctx.moveTo(23, 59); ctx.lineTo(23, 67); ctx.stroke(); });
  fill(1, () => { ctx.beginPath(); ctx.ellipse(23, 70, 3, 4, 0, 0, Math.PI * 2); ctx.fill(); });
  ctx.restore();
}

function drawEidArch(ctx, cx, cy, width, color) {
  // SVG viewBox 80×32 scaled to width
  const s = width / 80;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(s, s);
  ctx.translate(-40, -16);
  ctx.strokeStyle = color; ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(4, 32); ctx.lineTo(4, 16); ctx.quadraticCurveTo(4, 4, 16, 4); ctx.lineTo(64, 4); ctx.quadraticCurveTo(76, 4, 76, 16); ctx.lineTo(76, 32);
  ctx.stroke();
  ctx.globalAlpha = 0.55; ctx.lineWidth = 0.9;
  ctx.beginPath();
  ctx.moveTo(10, 32); ctx.lineTo(10, 17); ctx.quadraticCurveTo(10, 10, 19, 10); ctx.lineTo(61, 10); ctx.quadraticCurveTo(70, 10, 70, 17); ctx.lineTo(70, 32);
  ctx.stroke();
  ctx.globalAlpha = 0.7; ctx.fillStyle = color;
  ctx.beginPath(); ctx.arc(40, 4, 3, 0, Math.PI * 2); ctx.fill();
  ctx.globalAlpha = 1;
  ctx.restore();
}

function drawEidDivider(ctx, cx, cy, width, color) {
  const w = width, half = w / 2;
  ctx.save();
  ctx.translate(cx - half, cy);
  const setAlpha = (op) => { ctx.globalAlpha = op; };
  ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineWidth = 0.9;
  setAlpha(0.8);
  ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(half - 22, 0); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(half + 32, 0); ctx.lineTo(w, 0); ctx.stroke();
  // diamonds
  const diamond = (cxd, scale, op) => {
    setAlpha(op);
    ctx.beginPath();
    ctx.moveTo(cxd - 4 * scale, 0);
    ctx.lineTo(cxd, -4 * scale);
    ctx.lineTo(cxd + 4 * scale, 0);
    ctx.lineTo(cxd, 4 * scale);
    ctx.closePath(); ctx.fill();
  };
  const c = half;
  diamond(c - 14, 1, 0.85);
  diamond(c - 3, 0.85, 0.75);
  diamond(c + 2, 1.05, 1);
  diamond(c + 13, 0.85, 0.75);
  diamond(c + 24, 1, 0.85);
  ctx.globalAlpha = 1;
  ctx.restore();
}

function drawEidMosque(ctx, W, H, themeId) {
  const w = W, h = Math.round(W * 0.34);
  const yTop = H - h;
  const isFajr = themeId === 'fajr';
  const fill = isFajr ? 'rgba(0,0,0,0.28)' : 'rgba(255,255,255,0.075)';
  const s = w / 340;
  ctx.save();
  ctx.translate(0, yTop);
  ctx.scale(s, s);
  ctx.fillStyle = fill;
  // outer thin minarets
  const minaret = (x, w2, h2, capX, capR) => {
    ctx.fillRect(x, 62, w2, h2);
    ctx.beginPath(); ctx.moveTo(x, 62); ctx.lineTo(x + w2, 62); ctx.lineTo(x + w2 / 2, 62 - (w2 * 1.75)); ctx.closePath(); ctx.fill();
    ctx.beginPath(); ctx.arc(capX, capR, w2 / 2.3, 0, Math.PI * 2); ctx.fill();
  };
  minaret(12, 8, 54, 16, 45);
  minaret(320, 8, 54, 324, 45);
  // main minarets
  ctx.fillRect(52, 35, 13, 81);
  ctx.beginPath(); ctx.moveTo(52, 35); ctx.lineTo(65, 35); ctx.lineTo(58.5, 18); ctx.closePath(); ctx.fill();
  ctx.beginPath(); ctx.arc(58.5, 15, 4.5, 0, Math.PI * 2); ctx.fill();
  ctx.fillRect(275, 35, 13, 81);
  ctx.beginPath(); ctx.moveTo(275, 35); ctx.lineTo(288, 35); ctx.lineTo(281.5, 18); ctx.closePath(); ctx.fill();
  ctx.beginPath(); ctx.arc(281.5, 15, 4.5, 0, Math.PI * 2); ctx.fill();
  // flanking side domes
  const dome = (l, r) => { ctx.beginPath(); ctx.moveTo(l, 96); ctx.lineTo(l, 72); ctx.quadraticCurveTo(l, 52, (l + r) / 2, 52); ctx.quadraticCurveTo(r, 52, r, 72); ctx.lineTo(r, 96); ctx.closePath(); ctx.fill(); };
  dome(80, 116);
  dome(224, 260);
  // central hero dome
  ctx.beginPath(); ctx.moveTo(122, 96); ctx.lineTo(122, 62); ctx.quadraticCurveTo(122, 15, 170, 15); ctx.quadraticCurveTo(218, 15, 218, 62); ctx.lineTo(218, 96); ctx.closePath(); ctx.fill();
  // finial
  ctx.lineWidth = 2.5; ctx.strokeStyle = fill;
  ctx.beginPath(); ctx.moveTo(170, 15); ctx.lineTo(170, 5); ctx.stroke();
  ctx.beginPath(); ctx.arc(170, 3, 4.5, 0, Math.PI * 2); ctx.fill();
  // base wall
  ctx.fillRect(50, 96, 240, 20);
  // arches
  ctx.globalAlpha = 0.7;
  [65, 90, 115, 140, 165, 190, 215, 240, 265].forEach(x => {
    ctx.beginPath(); ctx.moveTo(x, 96); ctx.quadraticCurveTo(x + 11, 82, x + 22, 96); ctx.closePath(); ctx.fill();
  });
  ctx.globalAlpha = 1;
  ctx.restore();
}

// ─── Borders ─────────────────────────────────────────────────
function drawCornerStar(ctx, cx, cy, r, color, opacity) {
  ctx.save(); ctx.globalAlpha = opacity; ctx.fillStyle = color;
  ctx.beginPath();
  for (let i = 0; i < 16; i++) {
    const a = (i * Math.PI) / 8 - Math.PI / 4;
    const rad = i % 2 === 0 ? r : r * 0.4;
    const x = cx + rad * Math.cos(a), y = cy + rad * Math.sin(a);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.closePath(); ctx.fill(); ctx.restore();
}

function drawEidGeometricFrame(ctx, size, color) {
  const T = size * 22 / 340; // scale band thickness like SVG
  // outer edge
  ctx.save();
  ctx.strokeStyle = color;
  ctx.globalAlpha = 0.9; ctx.lineWidth = size * 1.3 / 340;
  ctx.strokeRect(3 * size / 340, 3 * size / 340, size - 6 * size / 340, size - 6 * size / 340);
  ctx.globalAlpha = 0.65; ctx.lineWidth = size * 0.75 / 340;
  ctx.strokeRect(T, T, size - T * 2, size - T * 2);
  ctx.globalAlpha = 0.38; ctx.lineWidth = size * 0.35 / 340;
  ctx.strokeRect(T + 5 * size / 340, T + 5 * size / 340, size - (T + 5 * size / 340) * 2, size - (T + 5 * size / 340) * 2);
  ctx.restore();
  // corner stars
  const cornerR = 9 * size / 340;
  [[T / 2, T / 2], [size - T / 2, T / 2], [T / 2, size - T / 2], [size - T / 2, size - T / 2]].forEach(([cx, cy]) => drawCornerStar(ctx, cx, cy, cornerR, color, 0.95));
  // mid-edge diamonds
  const midR = 5.5 * size / 340;
  [[size / 2, T / 2], [size / 2, size - T / 2], [T / 2, size / 2], [size - T / 2, size / 2]].forEach(([cx, cy]) => drawCornerStar(ctx, cx, cy, midR, color, 0.72));
  // dot trail
  ctx.save(); ctx.globalAlpha = 0.3; ctx.fillStyle = color;
  for (let n = 3; n <= 10; n++) {
    const t = n / 13;
    const v = T / 2 + t * (size - T);
    const r = 1.1 * size / 340;
    [[v, T / 2], [v, size - T / 2], [T / 2, v], [size - T / 2, v]].forEach(([x, y]) => { ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill(); });
  }
  ctx.restore();
}

function drawEidArabesqueFrame(ctx, size, color) {
  const s = size / 340; // SVG was authored at 340px reference
  ctx.save();
  ctx.strokeStyle = color;
  ctx.globalAlpha = 0.9; ctx.lineWidth = 1.8 * s;
  ctx.strokeRect(4 * s, 4 * s, size - 8 * s, size - 8 * s);
  ctx.globalAlpha = 0.55; ctx.lineWidth = 0.8 * s;
  ctx.strokeRect(10 * s, 10 * s, size - 20 * s, size - 20 * s);
  ctx.restore();
  const corners = [
    { tx: 0, ty: 0, rot: 0 },
    { tx: size, ty: 0, rot: 90 },
    { tx: size, ty: size, rot: 180 },
    { tx: 0, ty: size, rot: 270 },
  ];
  corners.forEach(({ tx, ty, rot }) => {
    ctx.save();
    ctx.translate(tx, ty); ctx.rotate(rot * Math.PI / 180); ctx.scale(s, s);
    // L bracket
    ctx.globalAlpha = 0.82; ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(4, 4); ctx.lineTo(52, 4); ctx.lineTo(52, 10); ctx.lineTo(12, 10); ctx.lineTo(12, 52); ctx.lineTo(4, 52); ctx.closePath();
    ctx.fill();
    // vine
    ctx.globalAlpha = 0.6; ctx.lineWidth = 1.1; ctx.strokeStyle = color;
    ctx.beginPath();
    ctx.moveTo(12, 12); ctx.quadraticCurveTo(38, 12, 38, 38); ctx.moveTo(38, 38); ctx.quadraticCurveTo(38, 12, 62, 12);
    ctx.stroke();
    // medallion
    ctx.globalAlpha = 0.68; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.arc(38, 28, 5, 0, Math.PI * 2); ctx.stroke();
    ctx.globalAlpha = 0.75; ctx.beginPath(); ctx.arc(38, 28, 2, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 0.45; ctx.lineWidth = 0.7;
    ctx.beginPath(); ctx.moveTo(38, 22); ctx.lineTo(38, 34); ctx.moveTo(32, 28); ctx.lineTo(44, 28); ctx.stroke();
    // flourishes
    ctx.globalAlpha = 0.5;
    ctx.beginPath(); ctx.arc(26, 10, 1.2, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(10, 26, 1.2, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  });
  ctx.globalAlpha = 1;
}

function drawEidLinesFrame(ctx, size, color) {
  const s = size / 340;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.6 * s;
  ctx.strokeRect(9 * s, 9 * s, size - 18 * s, size - 18 * s);
  ctx.globalAlpha = 0.65; ctx.lineWidth = 0.7 * s;
  ctx.strokeRect(15 * s, 15 * s, size - 30 * s, size - 30 * s);
  ctx.globalAlpha = 0.38; ctx.lineWidth = 0.35 * s;
  ctx.strokeRect(20 * s, 20 * s, size - 40 * s, size - 40 * s);
  ctx.restore();
  const drawDiamond = (cx, cy, half, op) => {
    ctx.save();
    ctx.globalAlpha = op; ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(cx - half, cy); ctx.lineTo(cx, cy - half); ctx.lineTo(cx + half, cy); ctx.lineTo(cx, cy + half);
    ctx.closePath(); ctx.fill();
    ctx.restore();
  };
  [[9 * s, 9 * s], [size - 9 * s, 9 * s], [9 * s, size - 9 * s], [size - 9 * s, size - 9 * s]].forEach(([cx, cy]) => drawDiamond(cx, cy, 9.9 * s, 0.92));
  [[size / 2, 9 * s], [size / 2, size - 9 * s], [9 * s, size / 2], [size - 9 * s, size / 2]].forEach(([cx, cy]) => drawDiamond(cx, cy, 6.4 * s, 0.7));
}

// Auto-fit Tangerine script so the name always fills available width without clipping
function fitTangerineName(ctx, name, maxW, maxSize) {
  let sz = maxSize;
  const minSz = maxSize * 0.4;
  ctx.font = `700 ${sz}px 'Tangerine', cursive`;
  let w = ctx.measureText(name).width;
  if (w <= maxW) return sz;
  // scale down proportionally then verify
  sz = Math.max(minSz, sz * (maxW / w));
  ctx.font = `700 ${sz}px 'Tangerine', cursive`;
  while (sz > minSz && ctx.measureText(name).width > maxW) {
    sz -= 2;
    ctx.font = `700 ${sz}px 'Tangerine', cursive`;
  }
  return sz;
}

function wrapText(ctx, text, font, maxW) {
  ctx.font = font;
  const words = text.split(/\s+/);
  const lines = []; let line = '';
  words.forEach(w => {
    const cand = line ? line + ' ' + w : w;
    if (ctx.measureText(cand).width > maxW && line) { lines.push(line); line = w; }
    else line = cand;
  });
  if (line) lines.push(line);
  return lines;
}

export function drawEidCard({ themeId, name, blessing, personalNote, eidType }) {
  const theme = EID_THEMES.find(t => t.id === themeId) || EID_THEMES[0];
  const W = 1080, H = 1080;
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  const pad = Math.round(W * 0.076);

  // Background gradient
  ctx.fillStyle = eidFillGradient(ctx, W, H, theme);
  ctx.fillRect(0, 0, W, H);

  // Grid texture (Obsidian)
  if (theme.hasGridTexture) {
    const rg = ctx.createRadialGradient(W * 0.5, H * 0.4, 0, W * 0.5, H * 0.4, W * 0.55);
    rg.addColorStop(0, 'rgba(200,148,26,0.05)');
    rg.addColorStop(1, 'rgba(200,148,26,0)');
    ctx.fillStyle = rg; ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = 'rgba(200,148,26,0.018)'; ctx.lineWidth = 1;
    const step = W * 40 / 340;
    for (let y = step; y < H; y += step) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
    for (let x = step; x < W; x += step) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
  }

  // Star field (Midnight)
  if (theme.hasStarField) {
    const stars = [
      [0.12, 0.09, 1.1], [0.38, 0.05, 0.8], [0.68, 0.11, 1.3], [0.88, 0.07, 0.9],
      [0.24, 0.19, 0.7], [0.55, 0.17, 1.0], [0.80, 0.22, 0.8], [0.06, 0.32, 0.9],
      [0.44, 0.27, 1.2], [0.76, 0.30, 0.75], [0.18, 0.43, 0.85], [0.62, 0.38, 1.1],
      [0.91, 0.40, 0.8], [0.33, 0.52, 0.7], [0.72, 0.48, 1.0], [0.50, 0.08, 0.9],
    ];
    ctx.fillStyle = 'white';
    stars.forEach(([xr, yr, r], i) => {
      ctx.globalAlpha = 0.12 + (i % 5) * 0.04;
      ctx.beginPath(); ctx.arc(xr * W, yr * H, r * (W / 340), 0, Math.PI * 2); ctx.fill();
    });
    ctx.globalAlpha = 1;
  }

  // Mosque silhouette
  if (theme.bottomEl === 'mosque' || theme.bottomEl === 'mosqueFajr') {
    drawEidMosque(ctx, W, H, theme.id);
  }

  // Border frame
  if (theme.borderType === 'geometric') drawEidGeometricFrame(ctx, W, theme.borderColor);
  else if (theme.borderType === 'arabesque') drawEidArabesqueFrame(ctx, W, theme.borderColor);
  else if (theme.borderType === 'lines') drawEidLinesFrame(ctx, W, theme.borderColor);

  // ── Content layout (mirrors the React prototype, percentages of W) ──
  let y = pad;
  ctx.textAlign = 'center';

  // Top ornament
  const starSize = Math.round(W * 0.094);
  if (theme.topEl === 'star' || theme.topEl === 'starDawn') {
    if (theme.topEl === 'starDawn') {
      // soft halo
      const halo = ctx.createRadialGradient(W / 2, y + starSize / 2, 0, W / 2, y + starSize / 2, starSize * 0.9);
      halo.addColorStop(0, theme.arabicColor + '22');
      halo.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = halo;
      ctx.fillRect(W / 2 - starSize, y - starSize / 4, starSize * 2, starSize * 1.5);
    }
    drawEidStar8(ctx, W / 2, y + starSize / 2, starSize, theme.arabicColor);
    y += starSize;
  } else if (theme.topEl === 'crescent') {
    drawEidCrescent(ctx, W / 2, y + starSize / 2, starSize, theme.arabicColor);
    y += starSize;
  } else if (theme.topEl === 'crescentStars') {
    const gap = 32;
    const miniR = 12 * W / 340;
    drawEidStar8(ctx, W / 2 - starSize / 2 - gap - miniR / 2, y + starSize / 2, miniR * 2, theme.occasionColor);
    drawEidCrescent(ctx, W / 2, y + starSize / 2, starSize, theme.arabicColor);
    drawEidStar8(ctx, W / 2 + starSize / 2 + gap + miniR / 2, y + starSize / 2, miniR * 2, theme.occasionColor);
    y += starSize;
  } else if (theme.topEl === 'lantern') {
    const lanternSize = Math.round(W * 0.082);
    drawEidLantern(ctx, W / 2, y + (lanternSize * 1.65) / 2, lanternSize, theme.arabicColor);
    y += lanternSize * 1.65;
  } else if (theme.topEl === 'arch') {
    const archW = W * 0.235;
    drawEidArch(ctx, W / 2, y + archW * 32 / 80 / 2, archW, theme.borderColor);
    y += archW * 32 / 80;
  }

  // Occasion label
  y += W * 0.030;
  const eidLabelEn = eidType === 'adha' ? 'EID UL-ADHA MUBARAK' : 'EID UL-FITR MUBARAK';
  ctx.font = `500 ${W * 0.031}px 'Raleway', sans-serif`;
  ctx.fillStyle = theme.occasionColor;
  // letter-spacing approximation: insert thin spaces
  drawSpacedText(ctx, eidLabelEn, W / 2, y, W * 0.031 * 0.16);
  y += W * 0.031;

  // Arabic hero
  y += W * 0.04;
  const arabicText = eidType === 'adha' ? 'عِيدُ الْأَضْحَى مُبَارَكٌ' : 'عِيدُ الْفِطْرِ مُبَارَكٌ';
  ctx.font = `700 ${W * 0.106}px 'Amiri', serif`;
  ctx.direction = 'rtl';
  ctx.fillStyle = theme.arabicColor;
  if (theme.arabicGlow) { ctx.shadowColor = theme.arabicGlow.color; ctx.shadowBlur = theme.arabicGlow.blur * (W / 340); }
  const arabicAscent = W * 0.106 * 0.85;
  ctx.fillText(arabicText, W / 2, y + arabicAscent);
  ctx.shadowBlur = 0; ctx.direction = 'ltr';
  y += W * 0.106 * 1.2;

  // Divider + FROM + script name + underline
  y += W * 0.022;
  drawEidDivider(ctx, W / 2, y, W * 0.62, theme.dividerColor);
  y += W * 0.024;

  ctx.font = `400 ${W * 0.026}px 'Raleway', sans-serif`;
  ctx.fillStyle = theme.fromColor;
  drawSpacedText(ctx, 'FROM', W / 2, y + W * 0.020, W * 0.026 * 0.24);
  y += W * 0.040;

  // Script name (auto-fit Tangerine)
  const maxNameW = W - pad * 2 - W * 0.05;
  const maxNameFz = W * 0.172;
  const nameFz = fitTangerineName(ctx, name || 'Your Name', maxNameW, maxNameFz);
  ctx.font = `700 ${nameFz}px 'Tangerine', cursive`;
  ctx.fillStyle = theme.nameColor;
  if (theme.nameGlow) { ctx.shadowColor = theme.nameGlow.color; ctx.shadowBlur = theme.nameGlow.blur * (W / 340); }
  ctx.fillText(name || 'Your Name', W / 2, y + nameFz * 0.78);
  ctx.shadowBlur = 0;
  y += nameFz * 0.92;

  // Underline gradient
  const ulW = W * 0.52;
  const ul = ctx.createLinearGradient(W / 2 - ulW / 2, 0, W / 2 + ulW / 2, 0);
  ul.addColorStop(0, 'rgba(0,0,0,0)');
  ul.addColorStop(0.5, hexToRgba(theme.dividerColor, 0.73));
  ul.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = ul;
  ctx.fillRect(W / 2 - ulW / 2, y, ulW, 2);
  y += 14;

  // Blessing
  y += W * 0.028;
  const blessFz = W * 0.042;
  ctx.font = `italic 400 ${blessFz}px 'Cormorant Garamond', serif`;
  ctx.fillStyle = theme.blessingColor;
  const blessLines = wrapText(ctx, blessing, ctx.font, W * 0.92 - pad * 0.4);
  const blessLH = blessFz * 1.55;
  blessLines.forEach((ln, i) => ctx.fillText(ln, W / 2, y + blessFz * 0.85 + i * blessLH));
  y += blessLines.length * blessLH;

  // Personal note (optional)
  if (personalNote && personalNote.trim()) {
    y += W * 0.014;
    const noteFz = W * 0.036;
    ctx.font = `italic 400 ${noteFz}px 'Cormorant Garamond', serif`;
    ctx.fillStyle = theme.noteColor;
    const noteLines = wrapText(ctx, '"' + personalNote + '"', ctx.font, W * 0.84 - pad * 0.4);
    const noteLH = noteFz * 1.4;
    noteLines.forEach((ln, i) => ctx.fillText(ln, W / 2, y + noteFz * 0.85 + i * noteLH));
  }

  // Branding pinned to bottom
  const brandUrlFz = W * 0.030;
  const brandLabelFz = W * 0.022;
  const brandBottom = H - pad - W * 0.006;
  ctx.font = `500 ${brandUrlFz}px 'Raleway', sans-serif`;
  ctx.fillStyle = theme.brandUrlColor;
  drawSpacedText(ctx, 'sirat.co.za', W / 2, brandBottom, brandUrlFz * 0.10);
  ctx.font = `400 ${brandLabelFz}px 'Raleway', sans-serif`;
  ctx.fillStyle = theme.brandLabelColor;
  drawSpacedText(ctx, 'GET THE APP', W / 2, brandBottom - brandUrlFz - W * 0.008, brandLabelFz * 0.22);

  return c;
}

// Letter-spaced fillText (canvas has no native letter-spacing in older WebKit)
function drawSpacedText(ctx, text, cx, y, spacing) {
  if (!spacing) { ctx.fillText(text, cx, y); return; }
  const chars = text.split('');
  const widths = chars.map(ch => ctx.measureText(ch).width);
  const total = widths.reduce((s, w) => s + w, 0) + spacing * (chars.length - 1);
  let x = cx - total / 2;
  chars.forEach((ch, i) => {
    ctx.textAlign = 'left';
    ctx.fillText(ch, x, y);
    x += widths[i] + spacing;
  });
  ctx.textAlign = 'center';
}

function hexToRgba(input, a) {
  if (!input) return `rgba(0,0,0,${a})`;
  if (input.startsWith('rgba') || input.startsWith('rgb')) return input;
  const h = input.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

// ═════════════════════════════════════════════════════════════
// INNA LILLAHI — Paper & Ink  (condolence)
// Subdued palette, sombre but dignified
// ═════════════════════════════════════════════════════════════
function drawInnaLillahiCard_paper({ userName, deceasedName, deceasedNameIsArabic, message: msg, hijri }) {
  const W = 1080, H = 1080, pad = 64;
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  // Subdued paper
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#ede4d0'); bg.addColorStop(1, '#ddd0b2');
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = 'rgba(56,42,20,0.05)';
  for (let i = 0; i < 1200; i++) { const x = Math.random()*W, y = Math.random()*H; ctx.fillRect(x, y, 1, 1); }
  // Simple crescent
  ctx.save(); ctx.translate(W/2, pad+24);
  ctx.strokeStyle = 'rgba(56,42,20,0.18)'; ctx.lineWidth = 1.4;
  ctx.beginPath(); ctx.arc(0, 0, 24, Math.PI*0.25, Math.PI*1.75); ctx.stroke();
  ctx.restore();
  eyebrow(ctx, W, 'I n n a   L i l l a h i', pad+68, true);
  let y = pad+170;
  // Main verse
  ctx.textAlign = 'center';
  ctx.font = "700 62px 'Scheherazade New','Amiri',serif"; ctx.fillStyle = '#1a1208';
  ctx.fillText('إِنَّا لِلَّهِ وَإِنَّا إِلَيْهِ رَاجِعُونَ', W/2, y); y += 56;
  ctx.font = "italic 400 26px 'Instrument Serif','Georgia',serif"; ctx.fillStyle = 'rgba(26,18,8,0.65)';
  ctx.fillText('Indeed, to Allah we belong and to Him we shall return.', W/2, y); y += 52;
  goldRule(ctx, W, y, true); y += 40;
  if (deceasedName) {
    ctx.font = "700 18px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(140,107,31,0.72)'; ctx.textAlign = 'center';
    ctx.fillText('W E   A R E   S A D D E N E D   B Y   T H E   P A S S I N G   O F', W/2, y); y += 28;
    const nf = autoFitName(ctx, deceasedName, deceasedNameIsArabic, W-pad*2-40, 110, 56);
    ctx.font = nf.font; ctx.fillStyle = '#1a1208'; ctx.shadowColor = 'rgba(26,18,8,0.1)'; ctx.shadowBlur = 8;
    ctx.fillText(deceasedName, W/2, y + nf.size*0.85); ctx.shadowBlur = 0; y += nf.size*0.85 + 16;
    smallArabicLabel(ctx, W, y, 'رَحِمَهُ اللَّهُ', true); y += 50;
  }
  goldRule(ctx, W, y, true); y += 36;
  const footerY = H - pad - 90;
  y = message(ctx, W, y, msg, footerY - y, true);
  if (hijri || userName) {
    ctx.font = "italic 400 19px 'Instrument Serif',serif"; ctx.fillStyle = 'rgba(56,42,20,0.5)'; ctx.textAlign = 'center';
    if (userName) ctx.fillText(`— ${userName}${hijri ? '  ·  ' + hijri : ''}`, W/2, H-pad-106);
  }
  drawSharedByStrip(ctx, W, H, { nameColor:'#1a1208', urlColor:'#8c6b1f', subColor:'rgba(26,18,8,0.6)', y: H-pad-70, sharedBy: '' });
  return c;
}
// ─────────────────────────────────────────────────────────────
// INNA LILLAHI — Night Sky
// ─────────────────────────────────────────────────────────────
function drawInnaLillahiCard_night({ userName, deceasedName, deceasedNameIsArabic, message: msg, hijri }) {
  const W = 1080, H = 1080, pad = 74;
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  nightBg(ctx, W, H);
  ctx.save(); ctx.translate(W/2, pad+28);
  ctx.strokeStyle = 'rgba(232,196,104,0.4)'; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.arc(0, 0, 20, Math.PI*0.25, Math.PI*1.75); ctx.stroke();
  ctx.restore();
  eyebrow(ctx, W, 'I n n a   L i l l a h i', pad+68, false);
  let y = pad+168;
  ctx.textAlign = 'center';
  ctx.font = "700 56px 'Scheherazade New','Amiri',serif";
  const arG = ctx.createLinearGradient(0, y-50, 0, y+10);
  arG.addColorStop(0, '#e8c468'); arG.addColorStop(1, '#a07d26');
  ctx.fillStyle = arG; ctx.fillText('إِنَّا لِلَّهِ وَإِنَّا إِلَيْهِ رَاجِعُونَ', W/2, y); y += 52;
  ctx.font = "italic 400 24px 'Instrument Serif','Georgia',serif"; ctx.fillStyle = 'rgba(240,230,207,0.72)';
  ctx.fillText('Indeed, to Allah we belong and to Him we shall return.', W/2, y); y += 50;
  goldRule(ctx, W, y, false); y += 38;
  if (deceasedName) {
    ctx.font = "700 16px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(197,155,58,0.72)'; ctx.textAlign = 'center';
    ctx.fillText('W E   A R E   S A D D E N E D   B Y   T H E   P A S S I N G   O F', W/2, y); y += 26;
    const nf = autoFitName(ctx, deceasedName, deceasedNameIsArabic, W-pad*2-40, 106, 54);
    ctx.font = nf.font; ctx.fillStyle = '#f0e6cf'; ctx.shadowColor = 'rgba(232,196,104,0.3)'; ctx.shadowBlur = 18;
    ctx.fillText(deceasedName, W/2, y + nf.size*0.85); ctx.shadowBlur = 0; y += nf.size*0.85 + 14;
    ctx.font = "700 36px 'Scheherazade New','Amiri',serif"; ctx.fillStyle = 'rgba(232,196,104,0.72)';
    ctx.fillText('رَحِمَهُ اللَّهُ', W/2, y + 28); y += 48;
  }
  goldRule(ctx, W, y, false); y += 34;
  const footerY = H - pad - 90;
  y = message(ctx, W, y, msg, footerY - y, false);
  if (userName) { ctx.font = "italic 400 18px 'Instrument Serif',serif"; ctx.fillStyle = 'rgba(255,255,255,0.35)'; ctx.textAlign = 'center'; ctx.fillText(`— ${userName}${hijri ? '  ·  ' + hijri : ''}`, W/2, H-pad-106); }
  drawSharedByStrip(ctx, W, H, { nameColor:'#f0e6cf', urlColor:'#c59b3a', subColor:'rgba(255,255,255,0.55)', y: H-pad-70, sharedBy: '' });
  return c;
}

// ═════════════════════════════════════════════════════════════
// NIKAH MUBARAK — Paper & Ink
// ═════════════════════════════════════════════════════════════
function drawNikahCard_paper({ groomName, groomNameIsArabic, brideName, brideNameIsArabic, message: msg, hijri, userName }) {
  const W = 1080, H = 1080, pad = 64;
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  paperBg(ctx, W, H);
  // Geometric floral ornament
  ctx.save(); ctx.translate(W/2, pad+26);
  ctx.strokeStyle = 'rgba(56,42,20,0.2)'; ctx.lineWidth = 1.2;
  for (let i = 0; i < 6; i++) {
    ctx.save(); ctx.rotate((Math.PI*2/6)*i);
    ctx.beginPath(); ctx.arc(18, 0, 12, 0, Math.PI*2); ctx.stroke();
    ctx.restore();
  }
  ctx.beginPath(); ctx.arc(0, 0, 10, 0, Math.PI*2); ctx.stroke();
  ctx.restore();
  eyebrow(ctx, W, 'Nikah Mubarak', pad+70, true);
  let y = pad+148;
  ctx.textAlign = 'center'; ctx.font = "700 56px 'Scheherazade New','Amiri',serif"; ctx.fillStyle = '#1a1208';
  ctx.fillText('نِكَاحٌ مُبَارَكٌ', W/2, y); y += 52;
  goldRule(ctx, W, y, true); y += 36;
  // Groom
  if (groomName) {
    ctx.font = "700 16px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(140,107,31,0.72)'; ctx.textAlign = 'center';
    ctx.fillText('G R O O M', W/2, y); y += 12;
    const gf = autoFitName(ctx, groomName, groomNameIsArabic, W-pad*2-60, 90, 50);
    y = nameHero(ctx, W, y, groomName, groomNameIsArabic, gf.size, true) + 6;
  }
  // Heart divider
  ctx.font = "700 26px 'Instrument Serif',serif"; ctx.fillStyle = '#8c6b1f'; ctx.textAlign = 'center';
  ctx.fillText('❤', W/2, y + 10); y += 36;
  // Bride
  if (brideName) {
    ctx.font = "700 16px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(140,107,31,0.72)'; ctx.textAlign = 'center';
    ctx.fillText('B R I D E', W/2, y); y += 12;
    const bf = autoFitName(ctx, brideName, brideNameIsArabic, W-pad*2-60, 90, 50);
    y = nameHero(ctx, W, y, brideName, brideNameIsArabic, bf.size, true) + 6;
  }
  y += 10; goldRule(ctx, W, y, true); y += 30;
  // Nikah dua
  ctx.font = "700 28px 'Scheherazade New','Amiri',serif"; ctx.fillStyle = 'rgba(140,107,31,0.88)'; ctx.textAlign = 'center';
  ctx.fillText('بَارَكَ اللَّهُ لَكُمَا وَبَارَكَ عَلَيْكُمَا وَجَمَعَ بَيْنَكُمَا فِي خَيْرٍ', W/2, y + 28); y += 52;
  const footerY = H - pad - 110;
  if (msg) y = message(ctx, W, y + 8, msg, footerY - y - 8, true);
  if (userName || hijri) { ctx.font = "italic 400 19px 'Instrument Serif',serif"; ctx.fillStyle = 'rgba(56,42,20,0.5)'; ctx.textAlign = 'center'; ctx.fillText(`${userName ? '— ' + userName : ''}${hijri ? '  ·  ' + hijri : ''}`, W/2, H-pad-106); }
  drawSharedByStrip(ctx, W, H, { nameColor:'#1a1208', urlColor:'#8c6b1f', subColor:'rgba(26,18,8,0.6)', y: H-pad-70, sharedBy: '' });
  return c;
}
// ─────────────────────────────────────────────────────────────
// NIKAH MUBARAK — Night Sky
// ─────────────────────────────────────────────────────────────
function drawNikahCard_night({ groomName, groomNameIsArabic, brideName, brideNameIsArabic, message: msg, hijri, userName }) {
  const W = 1080, H = 1080, pad = 74;
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  nightBg(ctx, W, H);
  ctx.save(); ctx.translate(W/2, pad+26);
  ctx.strokeStyle = 'rgba(232,196,104,0.45)'; ctx.lineWidth = 1.2;
  for (let i = 0; i < 6; i++) { ctx.save(); ctx.rotate((Math.PI*2/6)*i); ctx.beginPath(); ctx.arc(16, 0, 10, 0, Math.PI*2); ctx.stroke(); ctx.restore(); }
  ctx.beginPath(); ctx.arc(0, 0, 9, 0, Math.PI*2); ctx.stroke();
  ctx.restore();
  eyebrow(ctx, W, 'Nikah Mubarak', pad+68, false);
  let y = pad+146;
  ctx.textAlign = 'center'; ctx.font = "700 50px 'Scheherazade New','Amiri',serif";
  const arG = ctx.createLinearGradient(0, y-44, 0, y+6); arG.addColorStop(0, '#e8c468'); arG.addColorStop(1, '#a07d26');
  ctx.fillStyle = arG; ctx.fillText('نِكَاحٌ مُبَارَكٌ', W/2, y); y += 50;
  goldRule(ctx, W, y, false); y += 34;
  if (groomName) {
    ctx.font = "700 14px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(197,155,58,0.72)'; ctx.textAlign = 'center';
    ctx.fillText('G R O O M', W/2, y); y += 12;
    const gf = autoFitName(ctx, groomName, groomNameIsArabic, W-pad*2-60, 86, 46);
    y = nameHero(ctx, W, y, groomName, groomNameIsArabic, gf.size, false) + 4;
  }
  ctx.font = "700 22px 'Instrument Serif',serif"; ctx.fillStyle = '#c59b3a'; ctx.textAlign = 'center';
  ctx.fillText('❤', W/2, y + 8); y += 32;
  if (brideName) {
    ctx.font = "700 14px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(197,155,58,0.72)'; ctx.textAlign = 'center';
    ctx.fillText('B R I D E', W/2, y); y += 12;
    const bf = autoFitName(ctx, brideName, brideNameIsArabic, W-pad*2-60, 86, 46);
    y = nameHero(ctx, W, y, brideName, brideNameIsArabic, bf.size, false) + 4;
  }
  y += 8; goldRule(ctx, W, y, false); y += 28;
  ctx.font = "700 26px 'Scheherazade New','Amiri',serif"; ctx.fillStyle = 'rgba(232,196,104,0.82)'; ctx.textAlign = 'center';
  ctx.fillText('بَارَكَ اللَّهُ لَكُمَا وَبَارَكَ عَلَيْكُمَا وَجَمَعَ بَيْنَكُمَا فِي خَيْرٍ', W/2, y + 26); y += 50;
  const footerY = H - pad - 110;
  if (msg) y = message(ctx, W, y + 8, msg, footerY - y - 8, false);
  if (userName) { ctx.font = "italic 400 18px 'Instrument Serif',serif"; ctx.fillStyle = 'rgba(255,255,255,0.35)'; ctx.textAlign = 'center'; ctx.fillText(`${userName ? '— ' + userName : ''}${hijri ? '  ·  ' + hijri : ''}`, W/2, H-pad-106); }
  drawSharedByStrip(ctx, W, H, { nameColor:'#f0e6cf', urlColor:'#c59b3a', subColor:'rgba(255,255,255,0.55)', y: H-pad-70, sharedBy: '' });
  return c;
}

// ═════════════════════════════════════════════════════════════
// HAJJ / UMRAH — Paper & Ink
// ═════════════════════════════════════════════════════════════
function drawHajjCard_paper({ pilgrimName, pilgrimNameIsArabic, hajjType, message: msg, departureDate, hijri }) {
  const W = 1080, H = 1080, pad = 64;
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  paperBg(ctx, W, H);
  // Kaaba silhouette
  ctx.save(); ctx.translate(W/2 - 24, pad + 8);
  ctx.fillStyle = 'rgba(56,42,20,0.15)';
  ctx.fillRect(0, 24, 48, 36); ctx.fillRect(-8, 36, 64, 24);
  ctx.fillRect(16, 36, 16, 24);
  ctx.restore();
  eyebrow(ctx, W, hajjType === 'umrah' ? 'Umrah Mubarak' : 'Hajj Mubarak', pad+76, true);
  let y = pad+168;
  ctx.textAlign = 'center'; ctx.font = "700 64px 'Scheherazade New','Amiri',serif"; ctx.fillStyle = '#1a1208';
  ctx.fillText('لَبَّيْكَ اللَّهُمَّ لَبَّيْكَ', W/2, y); y += 56;
  ctx.font = "italic 400 22px 'Instrument Serif','Georgia',serif"; ctx.fillStyle = 'rgba(26,18,8,0.6)';
  ctx.fillText('Here I am O Allah, here I am.', W/2, y); y += 48;
  goldRule(ctx, W, y, true); y += 36;
  ctx.font = "700 17px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(140,107,31,0.72)'; ctx.textAlign = 'center';
  ctx.fillText(hajjType === 'umrah' ? 'G O I N G   F O R   U M R A H' : 'G O I N G   F O R   H A J J', W/2, y); y += 14;
  const nf = autoFitName(ctx, pilgrimName, pilgrimNameIsArabic, W-pad*2-40, 130, 64);
  y = nameHero(ctx, W, y, pilgrimName, pilgrimNameIsArabic, nf.size, true) + 4;
  if (departureDate) { ctx.font = "700 16px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(140,107,31,0.66)'; ctx.textAlign = 'center'; ctx.fillText(departureDate.toUpperCase().split('').join(' '), W/2, y+10); y += 24; }
  if (hijri) { ctx.font = "600 15px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(140,107,31,0.55)'; ctx.textAlign = 'center'; ctx.fillText(hijri.toUpperCase().split('').join(' '), W/2, y+8); y += 22; }
  y += 18; goldRule(ctx, W, y, true); y += 34;
  const footerY = H - pad - 90;
  y = message(ctx, W, y, msg, footerY - y, true);
  drawSharedByStrip(ctx, W, H, { nameColor:'#1a1208', urlColor:'#8c6b1f', subColor:'rgba(26,18,8,0.6)', y: H-pad-70, sharedBy: '' });
  return c;
}
// ─────────────────────────────────────────────────────────────
// HAJJ / UMRAH — Night Sky
// ─────────────────────────────────────────────────────────────
function drawHajjCard_night({ pilgrimName, pilgrimNameIsArabic, hajjType, message: msg, departureDate, hijri }) {
  const W = 1080, H = 1080, pad = 74;
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  nightBg(ctx, W, H);
  ctx.save(); ctx.translate(W/2 - 20, pad + 6);
  ctx.fillStyle = 'rgba(232,196,104,0.18)';
  ctx.fillRect(0, 20, 40, 30); ctx.fillRect(-6, 30, 52, 20); ctx.fillRect(13, 30, 14, 20);
  ctx.restore();
  eyebrow(ctx, W, hajjType === 'umrah' ? 'Umrah Mubarak' : 'Hajj Mubarak', pad+76, false);
  let y = pad+166;
  ctx.textAlign = 'center'; ctx.font = "700 58px 'Scheherazade New','Amiri',serif";
  const arG = ctx.createLinearGradient(0, y-50, 0, y+8); arG.addColorStop(0, '#e8c468'); arG.addColorStop(1, '#a07d26');
  ctx.fillStyle = arG; ctx.fillText('لَبَّيْكَ اللَّهُمَّ لَبَّيْكَ', W/2, y); y += 50;
  ctx.font = "italic 400 20px 'Instrument Serif','Georgia',serif"; ctx.fillStyle = 'rgba(240,230,207,0.65)';
  ctx.fillText('Here I am O Allah, here I am.', W/2, y); y += 46;
  goldRule(ctx, W, y, false); y += 34;
  ctx.font = "700 15px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(197,155,58,0.72)'; ctx.textAlign = 'center';
  ctx.fillText(hajjType === 'umrah' ? 'G O I N G   F O R   U M R A H' : 'G O I N G   F O R   H A J J', W/2, y); y += 14;
  const nf = autoFitName(ctx, pilgrimName, pilgrimNameIsArabic, W-pad*2-40, 126, 62);
  y = nameHero(ctx, W, y, pilgrimName, pilgrimNameIsArabic, nf.size, false) + 4;
  if (departureDate) { ctx.font = "700 14px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(232,196,104,0.65)'; ctx.textAlign = 'center'; ctx.fillText(departureDate.toUpperCase().split('').join(' '), W/2, y+10); y += 22; }
  if (hijri) { ctx.font = "600 13px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(232,196,104,0.5)'; ctx.textAlign = 'center'; ctx.fillText(hijri.toUpperCase().split('').join(' '), W/2, y+8); y += 20; }
  y += 16; goldRule(ctx, W, y, false); y += 32;
  const footerY = H - pad - 90;
  y = message(ctx, W, y, msg, footerY - y, false);
  drawSharedByStrip(ctx, W, H, { nameColor:'#f0e6cf', urlColor:'#c59b3a', subColor:'rgba(255,255,255,0.55)', y: H-pad-70, sharedBy: '' });
  return c;
}

// ═════════════════════════════════════════════════════════════
// NEW BABY — Paper & Ink
// ═════════════════════════════════════════════════════════════
function drawNewBabyCard_paper({ babyName, babyNameIsArabic, babyGender, birthDate, weight, parentNames, parentNamesIsArabic, message: msg }) {
  const W = 1080, H = 1080, pad = 64;
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  paperBg(ctx, W, H);
  // Small crescent + star
  ctx.save(); ctx.translate(W/2, pad+26);
  ctx.strokeStyle = 'rgba(56,42,20,0.2)'; ctx.lineWidth = 1.4;
  ctx.beginPath(); ctx.arc(-8, 0, 20, Math.PI*0.3, Math.PI*1.7); ctx.stroke();
  ctx.beginPath(); ctx.arc(16, -14, 5, 0, Math.PI*2); ctx.stroke();
  ctx.restore();
  const genderLabel = babyGender === 'girl' ? 'A Baby Girl Has Arrived' : 'A Baby Boy Has Arrived';
  eyebrow(ctx, W, genderLabel, pad+70, true);
  let y = pad+156;
  ctx.textAlign = 'center'; ctx.font = "700 60px 'Scheherazade New','Amiri',serif"; ctx.fillStyle = '#1a1208';
  ctx.fillText(babyGender === 'girl' ? 'مَوْلُودَةٌ مُبَارَكَةٌ' : 'مَوْلُودٌ مُبَارَكٌ', W/2, y); y += 54;
  goldRule(ctx, W, y, true); y += 36;
  if (babyName) {
    ctx.font = "700 17px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(140,107,31,0.72)'; ctx.textAlign = 'center';
    ctx.fillText('W E L C O M E', W/2, y); y += 14;
    const nf = autoFitName(ctx, babyName, babyNameIsArabic, W-pad*2-40, 140, 70);
    y = nameHero(ctx, W, y, babyName, babyNameIsArabic, nf.size, true) + 6;
  }
  // Birth details
  if (birthDate || weight) {
    ctx.font = "700 16px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(140,107,31,0.66)'; ctx.textAlign = 'center';
    if (birthDate) { ctx.fillText(birthDate.toUpperCase().split('').join(' '), W/2, y+8); y += 24; }
    if (weight) { ctx.fillText(weight.toUpperCase().split('').join(' '), W/2, y+8); y += 24; }
  }
  if (parentNames) {
    y += 8;
    ctx.font = "700 15px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(140,107,31,0.72)'; ctx.textAlign = 'center';
    ctx.fillText('P A R E N T S', W/2, y); y += 12;
    const pf = autoFitName(ctx, parentNames, parentNamesIsArabic, W-pad*2-60, 70, 38);
    ctx.font = pf.font; ctx.fillStyle = '#8c6b1f'; ctx.textAlign = 'center';
    ctx.fillText(parentNames, W/2, y + pf.size*0.82); y += pf.size*0.82 + 8;
  }
  y += 14; goldRule(ctx, W, y, true); y += 32;
  // Dua
  ctx.font = "700 26px 'Scheherazade New','Amiri',serif"; ctx.fillStyle = 'rgba(140,107,31,0.85)'; ctx.textAlign = 'center';
  ctx.fillText('بَارَكَ اللَّهُ لَكَ فِي الْمَوْهُوبِ لَكَ وَشَكَرْتَ الْوَاهِبَ', W/2, y + 26); y += 52;
  const footerY = H - pad - 90;
  if (msg) y = message(ctx, W, y + 6, msg, footerY - y - 6, true);
  drawSharedByStrip(ctx, W, H, { nameColor:'#1a1208', urlColor:'#8c6b1f', subColor:'rgba(26,18,8,0.6)', y: H-pad-70, sharedBy: '' });
  return c;
}
// ─────────────────────────────────────────────────────────────
// NEW BABY — Night Sky
// ─────────────────────────────────────────────────────────────
function drawNewBabyCard_night({ babyName, babyNameIsArabic, babyGender, birthDate, weight, parentNames, parentNamesIsArabic, message: msg }) {
  const W = 1080, H = 1080, pad = 74;
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  nightBg(ctx, W, H);
  ctx.save(); ctx.translate(W/2, pad+26);
  ctx.strokeStyle = 'rgba(232,196,104,0.45)'; ctx.lineWidth = 1.4;
  ctx.beginPath(); ctx.arc(-6, 0, 18, Math.PI*0.3, Math.PI*1.7); ctx.stroke();
  ctx.beginPath(); ctx.arc(14, -12, 4.5, 0, Math.PI*2); ctx.stroke();
  ctx.restore();
  const genderLabel = babyGender === 'girl' ? 'A Baby Girl Has Arrived' : 'A Baby Boy Has Arrived';
  eyebrow(ctx, W, genderLabel, pad+68, false);
  let y = pad+154;
  ctx.textAlign = 'center'; ctx.font = "700 54px 'Scheherazade New','Amiri',serif";
  const arG = ctx.createLinearGradient(0, y-46, 0, y+8); arG.addColorStop(0, '#e8c468'); arG.addColorStop(1, '#a07d26');
  ctx.fillStyle = arG; ctx.fillText(babyGender === 'girl' ? 'مَوْلُودَةٌ مُبَارَكَةٌ' : 'مَوْلُودٌ مُبَارَكٌ', W/2, y); y += 50;
  goldRule(ctx, W, y, false); y += 34;
  if (babyName) {
    ctx.font = "700 15px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(197,155,58,0.72)'; ctx.textAlign = 'center';
    ctx.fillText('W E L C O M E', W/2, y); y += 14;
    const nf = autoFitName(ctx, babyName, babyNameIsArabic, W-pad*2-40, 136, 68);
    y = nameHero(ctx, W, y, babyName, babyNameIsArabic, nf.size, false) + 4;
  }
  if (birthDate || weight) {
    ctx.font = "700 14px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(232,196,104,0.65)'; ctx.textAlign = 'center';
    if (birthDate) { ctx.fillText(birthDate.toUpperCase().split('').join(' '), W/2, y+8); y += 22; }
    if (weight) { ctx.fillText(weight.toUpperCase().split('').join(' '), W/2, y+8); y += 22; }
  }
  if (parentNames) {
    y += 6; ctx.font = "700 13px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(197,155,58,0.72)'; ctx.textAlign = 'center';
    ctx.fillText('P A R E N T S', W/2, y); y += 12;
    const pf = autoFitName(ctx, parentNames, parentNamesIsArabic, W-pad*2-60, 66, 36);
    ctx.font = pf.font; ctx.fillStyle = '#e8c468'; ctx.textAlign = 'center';
    ctx.fillText(parentNames, W/2, y + pf.size*0.82); y += pf.size*0.82 + 6;
  }
  y += 12; goldRule(ctx, W, y, false); y += 30;
  ctx.font = "700 24px 'Scheherazade New','Amiri',serif"; ctx.fillStyle = 'rgba(232,196,104,0.82)'; ctx.textAlign = 'center';
  ctx.fillText('بَارَكَ اللَّهُ لَكَ فِي الْمَوْهُوبِ لَكَ وَشَكَرْتَ الْوَاهِبَ', W/2, y + 24); y += 50;
  const footerY = H - pad - 90;
  if (msg) y = message(ctx, W, y + 6, msg, footerY - y - 6, false);
  drawSharedByStrip(ctx, W, H, { nameColor:'#f0e6cf', urlColor:'#c59b3a', subColor:'rgba(255,255,255,0.55)', y: H-pad-70, sharedBy: '' });
  return c;
}

// ─────────────────────────────────────────────────────────────
// GET WELL / SHIFA — PAPER & INK
// Soft leaf-green on warm cream, healing dua, patient name in hero font
// ─────────────────────────────────────────────────────────────
function drawShifaCard_paper({ patientName, patientNameIsArabic, message: msg, sharedBy }) {
  const W = 1080, H = 1080, pad = 64;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');

  // Background — warm cream with green tint
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#edfaf3'); bg.addColorStop(1, '#dcf5e8');
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

  // Subtle grid vignette
  ctx.fillStyle = 'rgba(30,140,70,0.025)';
  for (let x = 0; x < W; x += 32) for (let y2 = 0; y2 < H; y2 += 32)
    ctx.fillRect(x, y2, 1, 1);

  // Top accent bar
  const barG = ctx.createLinearGradient(0, 0, W, 0);
  barG.addColorStop(0, 'rgba(30,140,70,0)'); barG.addColorStop(0.5, 'rgba(30,140,70,0.55)'); barG.addColorStop(1, 'rgba(30,140,70,0)');
  ctx.fillStyle = barG; ctx.fillRect(0, 0, W, 5);

  // Decorative leaf motif (top-right corner)
  ctx.save(); ctx.translate(W - pad - 20, pad + 20); ctx.strokeStyle = 'rgba(30,140,70,0.18)'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(0, 40); ctx.bezierCurveTo(-30, 20, -30, -10, 0, -20); ctx.bezierCurveTo(30, -10, 30, 20, 0, 40); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(0, -20); ctx.lineTo(0, 40); ctx.stroke();
  ctx.restore();

  // Eyebrow label
  eyebrow(ctx, W, 'DUA FOR SHIFA', pad + 52, true);

  // Main Arabic headline — شِفَاءٌ عَاجِلٌ
  let y = pad + 128;
  ctx.textAlign = 'center'; ctx.font = "700 76px 'Scheherazade New','Amiri',serif";
  const arG = ctx.createLinearGradient(0, y - 60, 0, y + 16);
  arG.addColorStop(0, '#1e8c46'); arG.addColorStop(1, '#145c2e');
  ctx.fillStyle = arG; ctx.fillText('شِفَاءٌ عَاجِلٌ', W / 2, y); y += 54;

  // "Swift Recovery" subtitle
  ctx.font = "italic 400 28px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(20,92,46,0.7)'; ctx.textAlign = 'center';
  ctx.fillText('Swift Recovery', W / 2, y); y += 44;

  // Green rule
  const gr = ctx.createLinearGradient(pad * 2, 0, W - pad * 2, 0);
  gr.addColorStop(0, 'rgba(30,140,70,0)'); gr.addColorStop(0.5, 'rgba(30,140,70,0.5)'); gr.addColorStop(1, 'rgba(30,140,70,0)');
  ctx.strokeStyle = gr; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(pad * 2, y); ctx.lineTo(W - pad * 2, y); ctx.stroke();
  y += 34;

  // Patient name hero
  if (patientName) {
    ctx.font = "700 14px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(30,140,70,0.65)'; ctx.textAlign = 'center';
    ctx.fillText('W I S H I N G   S H I F A   T O', W / 2, y); y += 14;
    const nf = autoFitName(ctx, patientName, patientNameIsArabic, W - pad * 2 - 40, 136, 68);
    y = nameHero(ctx, W, y, patientName, patientNameIsArabic, nf.size, true) + 8;
  }

  // Ruqyah dua in Arabic
  ctx.font = "700 26px 'Scheherazade New','Amiri',serif";
  ctx.fillStyle = 'rgba(20,92,46,0.75)'; ctx.textAlign = 'center';
  ctx.fillText('اللَّهُمَّ اشْفِهِ وَعَافِهِ', W / 2, y + 24); y += 56;

  // Transliteration
  ctx.font = "italic 400 20px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(20,92,46,0.55)'; ctx.textAlign = 'center';
  ctx.fillText('Allāhumma ashfihi wa ʿāfihi', W / 2, y); y += 38;

  // Rule before message
  ctx.strokeStyle = gr; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(pad * 2, y); ctx.lineTo(W - pad * 2, y); ctx.stroke();
  y += 28;

  // Personal message
  const footerY = H - pad - 90;
  if (msg) {
    const msgLines = wrap(ctx, msg, "italic 400 28px 'Instrument Serif','Georgia',serif", W - pad * 2 - 40);
    ctx.font = "italic 400 28px 'Instrument Serif','Georgia',serif";
    ctx.fillStyle = 'rgba(20,92,46,0.72)'; ctx.textAlign = 'center';
    const lineH = 38;
    const maxLines = Math.min(msgLines.length, Math.floor((footerY - y - 10) / lineH));
    for (let i = 0; i < maxLines; i++) { ctx.fillText(msgLines[i], W / 2, y + lineH * (i + 1)); }
    y += lineH * maxLines + 16;
  }

  // Footer strip
  drawSharedByStrip(ctx, W, H, { nameColor: '#1e3a28', urlColor: '#1e8c46', subColor: 'rgba(20,92,46,0.55)', y: H - pad - 70, sharedBy });
  return c;
}

// ─────────────────────────────────────────────────────────────
// GET WELL / SHIFA — NIGHT SKY
// Dark forest green, emerald accents, glowing Arabic
// ─────────────────────────────────────────────────────────────
function drawShifaCard_night({ patientName, patientNameIsArabic, message: msg, sharedBy }) {
  const W = 1080, H = 1080, pad = 64;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');

  // Dark background
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#041a10'); bg.addColorStop(1, '#071e14');
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

  // Subtle dot grid
  ctx.fillStyle = 'rgba(80,200,120,0.03)';
  for (let x = 0; x < W; x += 32) for (let y2 = 0; y2 < H; y2 += 32)
    ctx.fillRect(x, y2, 1, 1);

  // Top accent bar
  const barG = ctx.createLinearGradient(0, 0, W, 0);
  barG.addColorStop(0, 'rgba(80,200,120,0)'); barG.addColorStop(0.5, 'rgba(80,200,120,0.6)'); barG.addColorStop(1, 'rgba(80,200,120,0)');
  ctx.fillStyle = barG; ctx.fillRect(0, 0, W, 5);

  // Radial glow behind Arabic text
  const glow = ctx.createRadialGradient(W / 2, pad + 110, 20, W / 2, pad + 110, 280);
  glow.addColorStop(0, 'rgba(80,200,120,0.1)'); glow.addColorStop(1, 'rgba(80,200,120,0)');
  ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H);

  // Decorative leaf motif (top-right corner)
  ctx.save(); ctx.translate(W - pad - 20, pad + 20); ctx.strokeStyle = 'rgba(80,200,120,0.22)'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(0, 40); ctx.bezierCurveTo(-30, 20, -30, -10, 0, -20); ctx.bezierCurveTo(30, -10, 30, 20, 0, 40); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(0, -20); ctx.lineTo(0, 40); ctx.stroke();
  ctx.restore();

  // Eyebrow label
  eyebrow(ctx, W, 'DUA FOR SHIFA', pad + 52, false);

  // Main Arabic headline
  let y = pad + 128;
  ctx.textAlign = 'center'; ctx.font = "700 76px 'Scheherazade New','Amiri',serif";
  const arG = ctx.createLinearGradient(0, y - 60, 0, y + 16);
  arG.addColorStop(0, '#50c878'); arG.addColorStop(1, '#2e8b57');
  ctx.fillStyle = arG; ctx.fillText('شِفَاءٌ عَاجِلٌ', W / 2, y); y += 54;

  // "Swift Recovery" subtitle
  ctx.font = "italic 400 28px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(80,200,120,0.65)'; ctx.textAlign = 'center';
  ctx.fillText('Swift Recovery', W / 2, y); y += 44;

  // Green rule
  const gr = ctx.createLinearGradient(pad * 2, 0, W - pad * 2, 0);
  gr.addColorStop(0, 'rgba(80,200,120,0)'); gr.addColorStop(0.5, 'rgba(80,200,120,0.5)'); gr.addColorStop(1, 'rgba(80,200,120,0)');
  ctx.strokeStyle = gr; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(pad * 2, y); ctx.lineTo(W - pad * 2, y); ctx.stroke();
  y += 34;

  // Patient name hero
  if (patientName) {
    ctx.font = "700 14px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(80,200,120,0.55)'; ctx.textAlign = 'center';
    ctx.fillText('W I S H I N G   S H I F A   T O', W / 2, y); y += 14;
    const nf = autoFitName(ctx, patientName, patientNameIsArabic, W - pad * 2 - 40, 136, 68);
    y = nameHero(ctx, W, y, patientName, patientNameIsArabic, nf.size, false) + 8;
  }

  // Ruqyah dua in Arabic
  ctx.font = "700 26px 'Scheherazade New','Amiri',serif";
  ctx.fillStyle = 'rgba(80,200,120,0.72)'; ctx.textAlign = 'center';
  ctx.fillText('اللَّهُمَّ اشْفِهِ وَعَافِهِ', W / 2, y + 24); y += 56;

  // Transliteration
  ctx.font = "italic 400 20px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(80,200,120,0.45)'; ctx.textAlign = 'center';
  ctx.fillText('Allāhumma ashfihi wa ʿāfihi', W / 2, y); y += 38;

  // Rule before message
  ctx.strokeStyle = gr; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(pad * 2, y); ctx.lineTo(W - pad * 2, y); ctx.stroke();
  y += 28;

  // Personal message
  const footerY = H - pad - 90;
  if (msg) {
    const msgLines = wrap(ctx, msg, "italic 400 28px 'Instrument Serif','Georgia',serif", W - pad * 2 - 40);
    ctx.font = "italic 400 28px 'Instrument Serif','Georgia',serif";
    ctx.fillStyle = 'rgba(80,200,120,0.65)'; ctx.textAlign = 'center';
    const lineH = 38;
    const maxLines = Math.min(msgLines.length, Math.floor((footerY - y - 10) / lineH));
    for (let i = 0; i < maxLines; i++) { ctx.fillText(msgLines[i], W / 2, y + lineH * (i + 1)); }
  }

  // Footer strip
  drawSharedByStrip(ctx, W, H, { nameColor: '#a8e8c0', urlColor: '#50c878', subColor: 'rgba(80,200,120,0.5)', y: H - pad - 70, sharedBy });
  return c;
}

// ─────────────────────────────────────────────────────────────
// HAPPY BIRTHDAY — PAPER & INK
// Warm ivory, amber-orange accents, barakah dua
// ─────────────────────────────────────────────────────────────
function drawBirthdayCard_paper({ recipientName, recipientNameIsArabic, message: msg, sharedBy }) {
  const W = 1080, H = 1080, pad = 64;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');

  // Background — warm cream with amber tint
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#fff8ee'); bg.addColorStop(1, '#fff0d0');
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

  // Subtle dot grid
  ctx.fillStyle = 'rgba(210,100,0,0.025)';
  for (let x = 0; x < W; x += 32) for (let y2 = 0; y2 < H; y2 += 32)
    ctx.fillRect(x, y2, 1, 1);

  // Top accent bar
  const barG = ctx.createLinearGradient(0, 0, W, 0);
  barG.addColorStop(0, 'rgba(210,100,0,0)'); barG.addColorStop(0.5, 'rgba(210,100,0,0.55)'); barG.addColorStop(1, 'rgba(210,100,0,0)');
  ctx.fillStyle = barG; ctx.fillRect(0, 0, W, 5);

  // Decorative crescent motif (top-right corner)
  ctx.save(); ctx.translate(W - pad - 10, pad + 30); ctx.strokeStyle = 'rgba(210,100,0,0.18)'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.arc(0, 0, 22, 0.4, Math.PI * 2 - 0.4); ctx.stroke();
  ctx.beginPath(); ctx.arc(10, -4, 18, 0.6, Math.PI * 2 - 0.2); ctx.strokeStyle = 'rgba(255,255,240,0.9)'; ctx.lineWidth = 16; ctx.stroke();
  ctx.restore();

  // — Proportional layout — key Y positions as fractions of H
  const P = (f) => Math.round(H * f);
  const gr = ctx.createLinearGradient(pad * 2, 0, W - pad * 2, 0);
  gr.addColorStop(0, 'rgba(210,100,0,0)'); gr.addColorStop(0.5, 'rgba(210,100,0,0.5)'); gr.addColorStop(1, 'rgba(210,100,0,0)');
  const rule = (y) => { ctx.strokeStyle = gr; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(pad * 2, y); ctx.lineTo(W - pad * 2, y); ctx.stroke(); };

  eyebrow(ctx, W, 'HAPPY BIRTHDAY', P(0.107), true);

  // Main Arabic dua
  const mainY = P(0.225);
  ctx.textAlign = 'center'; ctx.font = "700 80px 'Scheherazade New','Amiri',serif";
  const arG = ctx.createLinearGradient(0, mainY - 72, 0, mainY + 16);
  arG.addColorStop(0, '#d26400'); arG.addColorStop(1, '#8c4000');
  ctx.fillStyle = arG; ctx.fillText('بَارَكَ اللهُ فِي عُمُرِكَ', W / 2, mainY);

  ctx.font = "italic 400 26px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(140,64,0,0.6)'; ctx.textAlign = 'center';
  ctx.fillText('Bārak-Allāhu fī ʿumrik  ·  May Allah bless your years', W / 2, P(0.312));

  rule(P(0.362));

  // Name hero (optional)
  let secDuaY;
  if (recipientName) {
    ctx.font = "700 13px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(210,100,0,0.6)'; ctx.textAlign = 'center';
    ctx.fillText('W I S H I N G   A   B L E S S E D   B I R T H D A Y   T O', W / 2, P(0.385));
    const nf = autoFitName(ctx, recipientName, recipientNameIsArabic, W - pad * 2 - 40, 136, 68);
    const nameEndY = nameHero(ctx, W, P(0.405), recipientName, recipientNameIsArabic, nf.size, true) + 6;
    rule(nameEndY + 14);
    secDuaY = Math.max(nameEndY + 72, P(0.565));
  } else {
    secDuaY = P(0.505); // anchor secondary dua at ~50% without a name
  }

  // Secondary dua — large
  ctx.textAlign = 'center'; ctx.font = "700 60px 'Scheherazade New','Amiri',serif";
  const arG2 = ctx.createLinearGradient(0, secDuaY - 60, 0, secDuaY + 10);
  arG2.addColorStop(0, '#c85a00'); arG2.addColorStop(1, '#7a3600');
  ctx.fillStyle = arG2; ctx.fillText('أَطَالَ اللهُ عُمُرَكَ فِي طَاعَتِهِ', W / 2, secDuaY);

  const transY = secDuaY + 46;
  ctx.font = "italic 400 22px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(140,64,0,0.5)'; ctx.textAlign = 'center';
  ctx.fillText('Aṭāla-llāhu ʿumraka fī ṭāʿatihi', W / 2, transY);

  const msgRuleY = transY + 38;
  rule(msgRuleY);

  // Message — vertically centered in remaining space
  const msgAreaTop = msgRuleY + 28;
  const footerTopY = H - pad - 88;
  if (msg) {
    const msgLines = wrap(ctx, msg, "italic 400 30px 'Instrument Serif','Georgia',serif", W - pad * 2 - 40);
    ctx.font = "italic 400 30px 'Instrument Serif','Georgia',serif";
    ctx.fillStyle = 'rgba(140,64,0,0.72)'; ctx.textAlign = 'center';
    const lineH = 42;
    const maxLines = Math.min(msgLines.length, Math.floor((footerTopY - msgAreaTop - 10) / lineH));
    const totalH = maxLines * lineH;
    const msgFirstLine = msgAreaTop + Math.round((footerTopY - msgAreaTop - totalH) / 2);
    for (let i = 0; i < maxLines; i++) { ctx.fillText(msgLines[i], W / 2, msgFirstLine + lineH * (i + 1)); }
  }

  drawSharedByStrip(ctx, W, H, { nameColor: '#3a1800', urlColor: '#d26400', subColor: 'rgba(140,64,0,0.55)', y: H - pad - 70, sharedBy });
  return c;
}

// ─────────────────────────────────────────────────────────────
// HAPPY BIRTHDAY — NIGHT SKY
// Deep dark, warm amber glow, golden Arabic
// ─────────────────────────────────────────────────────────────
function drawBirthdayCard_night({ recipientName, recipientNameIsArabic, message: msg, sharedBy }) {
  const W = 1080, H = 1080, pad = 64;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');

  // Dark background
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#140600'); bg.addColorStop(1, '#0a0400');
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

  // Subtle dot grid
  ctx.fillStyle = 'rgba(255,140,30,0.03)';
  for (let x = 0; x < W; x += 32) for (let y2 = 0; y2 < H; y2 += 32)
    ctx.fillRect(x, y2, 1, 1);

  // Top accent bar
  const barG = ctx.createLinearGradient(0, 0, W, 0);
  barG.addColorStop(0, 'rgba(255,140,30,0)'); barG.addColorStop(0.5, 'rgba(255,140,30,0.6)'); barG.addColorStop(1, 'rgba(255,140,30,0)');
  ctx.fillStyle = barG; ctx.fillRect(0, 0, W, 5);

  // Radial glow behind Arabic text
  const glow = ctx.createRadialGradient(W / 2, pad + 110, 20, W / 2, pad + 110, 280);
  glow.addColorStop(0, 'rgba(255,140,30,0.1)'); glow.addColorStop(1, 'rgba(255,140,30,0)');
  ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H);

  // — Proportional layout —
  const P = (f) => Math.round(H * f);
  const gr = ctx.createLinearGradient(pad * 2, 0, W - pad * 2, 0);
  gr.addColorStop(0, 'rgba(255,140,30,0)'); gr.addColorStop(0.5, 'rgba(255,140,30,0.5)'); gr.addColorStop(1, 'rgba(255,140,30,0)');
  const rule = (y) => { ctx.strokeStyle = gr; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(pad * 2, y); ctx.lineTo(W - pad * 2, y); ctx.stroke(); };

  eyebrow(ctx, W, 'HAPPY BIRTHDAY', P(0.107), false);

  // Main Arabic dua
  const mainY = P(0.225);
  ctx.textAlign = 'center'; ctx.font = "700 80px 'Scheherazade New','Amiri',serif";
  const arG = ctx.createLinearGradient(0, mainY - 72, 0, mainY + 16);
  arG.addColorStop(0, '#ff8c1e'); arG.addColorStop(1, '#c85a00');
  ctx.fillStyle = arG; ctx.fillText('بَارَكَ اللهُ فِي عُمُرِكَ', W / 2, mainY);

  ctx.font = "italic 400 26px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(255,140,30,0.55)'; ctx.textAlign = 'center';
  ctx.fillText('Bārak-Allāhu fī ʿumrik  ·  May Allah bless your years', W / 2, P(0.312));

  rule(P(0.362));

  // Name hero (optional)
  let secDuaY;
  if (recipientName) {
    ctx.font = "700 13px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(255,140,30,0.5)'; ctx.textAlign = 'center';
    ctx.fillText('W I S H I N G   A   B L E S S E D   B I R T H D A Y   T O', W / 2, P(0.385));
    const nf = autoFitName(ctx, recipientName, recipientNameIsArabic, W - pad * 2 - 40, 136, 68);
    const nameEndY = nameHero(ctx, W, P(0.405), recipientName, recipientNameIsArabic, nf.size, false) + 6;
    rule(nameEndY + 14);
    secDuaY = Math.max(nameEndY + 72, P(0.565));
  } else {
    secDuaY = P(0.505);
  }

  // Secondary dua — large
  ctx.textAlign = 'center'; ctx.font = "700 60px 'Scheherazade New','Amiri',serif";
  const arG2 = ctx.createLinearGradient(0, secDuaY - 60, 0, secDuaY + 10);
  arG2.addColorStop(0, '#ff8c1e'); arG2.addColorStop(1, '#a04800');
  ctx.fillStyle = arG2; ctx.fillText('أَطَالَ اللهُ عُمُرَكَ فِي طَاعَتِهِ', W / 2, secDuaY);

  const transY = secDuaY + 46;
  ctx.font = "italic 400 22px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(255,140,30,0.45)'; ctx.textAlign = 'center';
  ctx.fillText('Aṭāla-llāhu ʿumraka fī ṭāʿatihi', W / 2, transY);

  const msgRuleY = transY + 38;
  rule(msgRuleY);

  // Message — vertically centered in remaining space
  const msgAreaTop = msgRuleY + 28;
  const footerTopY = H - pad - 88;
  if (msg) {
    const msgLines = wrap(ctx, msg, "italic 400 30px 'Instrument Serif','Georgia',serif", W - pad * 2 - 40);
    ctx.font = "italic 400 30px 'Instrument Serif','Georgia',serif";
    ctx.fillStyle = 'rgba(255,140,30,0.65)'; ctx.textAlign = 'center';
    const lineH = 42;
    const maxLines = Math.min(msgLines.length, Math.floor((footerTopY - msgAreaTop - 10) / lineH));
    const totalH = maxLines * lineH;
    const msgFirstLine = msgAreaTop + Math.round((footerTopY - msgAreaTop - totalH) / 2);
    for (let i = 0; i < maxLines; i++) { ctx.fillText(msgLines[i], W / 2, msgFirstLine + lineH * (i + 1)); }
  }

  drawSharedByStrip(ctx, W, H, { nameColor: '#ffd4a0', urlColor: '#ff8c1e', subColor: 'rgba(255,140,30,0.5)', y: H - pad - 70, sharedBy });
  return c;
}

// ─────────────────────────────────────────────────────────────
// DHUL HIJJAH — PAPER & INK
// Deep forest green + warm gold — sacred, serene
// ─────────────────────────────────────────────────────────────
function drawDhulHijjahCard_paper({ message: msg, sharedBy }) {
  const W = 1080, H = 1080, pad = 64;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');

  // Background — deep forest green
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#eaf6ee'); bg.addColorStop(1, '#d4eedc');
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

  // Subtle geometric dot grid in gold
  ctx.fillStyle = 'rgba(20,130,55,0.03)';
  for (let x = 0; x < W; x += 32) for (let y2 = 0; y2 < H; y2 += 32)
    ctx.fillRect(x, y2, 1, 1);

  // Top accent bar
  const barG = ctx.createLinearGradient(0, 0, W, 0);
  barG.addColorStop(0, 'rgba(20,130,55,0)'); barG.addColorStop(0.5, 'rgba(20,130,55,0.5)'); barG.addColorStop(1, 'rgba(20,130,55,0)');
  ctx.fillStyle = barG; ctx.fillRect(0, 0, W, 5);

  // Kaaba silhouette hint — subtle gold arc in upper right
  ctx.save(); ctx.translate(W - pad - 20, pad + 40);
  ctx.strokeStyle = 'rgba(20,130,55,0.15)'; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.arc(0, 0, 30, 0, Math.PI * 2); ctx.stroke();
  ctx.beginPath(); ctx.arc(0, 0, 20, 0, Math.PI * 2); ctx.stroke();
  ctx.restore();

  const P = (f) => Math.round(H * f);
  const gr = ctx.createLinearGradient(pad * 2, 0, W - pad * 2, 0);
  gr.addColorStop(0, 'rgba(20,130,55,0)'); gr.addColorStop(0.5, 'rgba(20,130,55,0.4)'); gr.addColorStop(1, 'rgba(20,130,55,0)');
  const rule = (y) => { ctx.strokeStyle = gr; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(pad * 2, y); ctx.lineTo(W - pad * 2, y); ctx.stroke(); };

  // Eyebrow
  eyebrow(ctx, W, 'DHU AL-HIJJAH  ·  الْعَشْرُ الْمُبَارَكَة', P(0.097), true);

  // Quranic verse — Surah al-Fajr 89:1-2
  ctx.textAlign = 'center'; ctx.font = "700 68px 'Scheherazade New','Amiri',serif";
  const arG = ctx.createLinearGradient(0, P(0.18) - 60, 0, P(0.18) + 10);
  arG.addColorStop(0, '#148237'); arG.addColorStop(1, '#0a5220');
  ctx.fillStyle = arG;
  ctx.fillText('وَالْفَجْرِ ۝ وَلَيَالٍ عَشْرٍ', W / 2, P(0.18));

  ctx.font = "italic 400 24px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(20,100,40,0.65)'; ctx.textAlign = 'center';
  ctx.fillText('"By the dawn — and by the Ten Nights"  ·  Sūrah al-Fajr, 89:1–2', W / 2, P(0.245));

  rule(P(0.29));

  // Central motif: big Arabic — عَشْرٌ مُبَارَكَةٌ
  ctx.textAlign = 'center'; ctx.font = "700 96px 'Scheherazade New','Amiri',serif";
  const mainG = ctx.createLinearGradient(0, P(0.365) - 88, 0, P(0.365) + 12);
  mainG.addColorStop(0, '#1a9440'); mainG.addColorStop(1, '#0e5c28');
  ctx.fillStyle = mainG;
  ctx.fillText('عَشْرٌ مُبَارَكَةٌ', W / 2, P(0.395));

  ctx.font = "italic 400 28px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(20,100,40,0.6)'; ctx.textAlign = 'center';
  ctx.fillText('ʿAshr Mubārakah  ·  Blessed Ten Days', W / 2, P(0.46));

  rule(P(0.505));

  // Hadith line
  ctx.textAlign = 'center'; ctx.font = "700 52px 'Scheherazade New','Amiri',serif";
  const arG2 = ctx.createLinearGradient(0, P(0.57) - 50, 0, P(0.57) + 8);
  arG2.addColorStop(0, '#18863a'); arG2.addColorStop(1, '#0a5220');
  ctx.fillStyle = arG2;
  ctx.fillText('مَا مِنْ أَيَّامٍ الْعَمَلُ الصَّالِحُ فِيهَا أَحَبُّ', W / 2, P(0.575));
  ctx.fillText('إِلَى اللهِ مِنْ هَذِهِ الْأَيَّامِ الْعَشْرِ', W / 2, P(0.635));

  ctx.font = "italic 400 20px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(20,100,40,0.5)'; ctx.textAlign = 'center';
  ctx.fillText('"No days are more beloved to Allah than these ten days"  ·  Bukhārī', W / 2, P(0.685));

  const msgRuleY = P(0.71);
  rule(msgRuleY);

  const msgAreaTop = msgRuleY + 28;
  const footerTopY = H - pad - 88;
  if (msg) {
    const msgLines = wrap(ctx, msg, "italic 400 28px 'Instrument Serif','Georgia',serif", W - pad * 2 - 40);
    ctx.font = "italic 400 28px 'Instrument Serif','Georgia',serif";
    ctx.fillStyle = 'rgba(20,100,40,0.7)'; ctx.textAlign = 'center';
    const lineH = 40;
    const maxLines = Math.min(msgLines.length, Math.floor((footerTopY - msgAreaTop - 10) / lineH));
    const totalH = maxLines * lineH;
    const msgFirstLine = msgAreaTop + Math.round((footerTopY - msgAreaTop - totalH) / 2);
    for (let i = 0; i < maxLines; i++) { ctx.fillText(msgLines[i], W / 2, msgFirstLine + lineH * (i + 1)); }
  }

  drawSharedByStrip(ctx, W, H, { nameColor: '#0a3d1a', urlColor: '#148237', subColor: 'rgba(20,100,40,0.55)', y: H - pad - 70, sharedBy });
  return c;
}

// ─────────────────────────────────────────────────────────────
// DHUL HIJJAH — NIGHT SKY
// Deep night, emerald glow, gold Arabic
// ─────────────────────────────────────────────────────────────
function drawDhulHijjahCard_night({ message: msg, sharedBy }) {
  const W = 1080, H = 1080, pad = 64;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');

  // Deep night background
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#03140a'); bg.addColorStop(1, '#061a0e');
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

  // Subtle dot grid
  ctx.fillStyle = 'rgba(60,200,100,0.025)';
  for (let x = 0; x < W; x += 32) for (let y2 = 0; y2 < H; y2 += 32)
    ctx.fillRect(x, y2, 1, 1);

  // Top accent bar
  const barG = ctx.createLinearGradient(0, 0, W, 0);
  barG.addColorStop(0, 'rgba(60,200,100,0)'); barG.addColorStop(0.5, 'rgba(60,200,100,0.55)'); barG.addColorStop(1, 'rgba(60,200,100,0)');
  ctx.fillStyle = barG; ctx.fillRect(0, 0, W, 5);

  // Radial glow behind main Arabic
  const glow = ctx.createRadialGradient(W / 2, pad + 160, 30, W / 2, pad + 160, 320);
  glow.addColorStop(0, 'rgba(60,200,100,0.1)'); glow.addColorStop(1, 'rgba(60,200,100,0)');
  ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H);

  // Stars — scattered dots
  ctx.fillStyle = 'rgba(180,255,200,0.35)';
  [[W * 0.08, H * 0.04], [W * 0.88, H * 0.03], [W * 0.15, H * 0.12], [W * 0.82, H * 0.1],
   [W * 0.05, H * 0.22], [W * 0.93, H * 0.2], [W * 0.75, H * 0.06]].forEach(([sx, sy]) => {
    ctx.beginPath(); ctx.arc(sx, sy, 1.5, 0, Math.PI * 2); ctx.fill();
  });

  const P = (f) => Math.round(H * f);
  const gr = ctx.createLinearGradient(pad * 2, 0, W - pad * 2, 0);
  gr.addColorStop(0, 'rgba(60,200,100,0)'); gr.addColorStop(0.5, 'rgba(60,200,100,0.45)'); gr.addColorStop(1, 'rgba(60,200,100,0)');
  const rule = (y) => { ctx.strokeStyle = gr; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(pad * 2, y); ctx.lineTo(W - pad * 2, y); ctx.stroke(); };

  eyebrow(ctx, W, 'DHU AL-HIJJAH  ·  الْعَشْرُ الْمُبَارَكَة', P(0.097), false);

  // Quranic verse
  ctx.textAlign = 'center'; ctx.font = "700 68px 'Scheherazade New','Amiri',serif";
  const arG = ctx.createLinearGradient(0, P(0.18) - 60, 0, P(0.18) + 10);
  arG.addColorStop(0, '#3cc864'); arG.addColorStop(1, '#1a8c42');
  ctx.fillStyle = arG;
  ctx.fillText('وَالْفَجْرِ ۝ وَلَيَالٍ عَشْرٍ', W / 2, P(0.18));

  ctx.font = "italic 400 24px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(100,230,140,0.5)'; ctx.textAlign = 'center';
  ctx.fillText('"By the dawn — and by the Ten Nights"  ·  Sūrah al-Fajr, 89:1–2', W / 2, P(0.245));

  rule(P(0.29));

  // Central Arabic
  ctx.textAlign = 'center'; ctx.font = "700 96px 'Scheherazade New','Amiri',serif";
  const mainG = ctx.createLinearGradient(0, P(0.365) - 88, 0, P(0.365) + 12);
  mainG.addColorStop(0, '#4edc7a'); mainG.addColorStop(1, '#1c9444');
  ctx.fillStyle = mainG;
  ctx.fillText('عَشْرٌ مُبَارَكَةٌ', W / 2, P(0.395));

  ctx.font = "italic 400 28px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(100,230,140,0.5)'; ctx.textAlign = 'center';
  ctx.fillText('ʿAshr Mubārakah  ·  Blessed Ten Days', W / 2, P(0.46));

  rule(P(0.505));

  // Hadith
  ctx.textAlign = 'center'; ctx.font = "700 52px 'Scheherazade New','Amiri',serif";
  const arG2 = ctx.createLinearGradient(0, P(0.57) - 50, 0, P(0.57) + 8);
  arG2.addColorStop(0, '#3cc864'); arG2.addColorStop(1, '#1a7a38');
  ctx.fillStyle = arG2;
  ctx.fillText('مَا مِنْ أَيَّامٍ الْعَمَلُ الصَّالِحُ فِيهَا أَحَبُّ', W / 2, P(0.575));
  ctx.fillText('إِلَى اللهِ مِنْ هَذِهِ الْأَيَّامِ الْعَشْرِ', W / 2, P(0.635));

  ctx.font = "italic 400 20px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(100,230,140,0.45)'; ctx.textAlign = 'center';
  ctx.fillText('"No days are more beloved to Allah than these ten days"  ·  Bukhārī', W / 2, P(0.685));

  const msgRuleY = P(0.71);
  rule(msgRuleY);

  const msgAreaTop = msgRuleY + 28;
  const footerTopY = H - pad - 88;
  if (msg) {
    const msgLines = wrap(ctx, msg, "italic 400 28px 'Instrument Serif','Georgia',serif", W - pad * 2 - 40);
    ctx.font = "italic 400 28px 'Instrument Serif','Georgia',serif";
    ctx.fillStyle = 'rgba(100,230,140,0.65)'; ctx.textAlign = 'center';
    const lineH = 40;
    const maxLines = Math.min(msgLines.length, Math.floor((footerTopY - msgAreaTop - 10) / lineH));
    const totalH = maxLines * lineH;
    const msgFirstLine = msgAreaTop + Math.round((footerTopY - msgAreaTop - totalH) / 2);
    for (let i = 0; i < maxLines; i++) { ctx.fillText(msgLines[i], W / 2, msgFirstLine + lineH * (i + 1)); }
  }

  drawSharedByStrip(ctx, W, H, { nameColor: '#b8f5cc', urlColor: '#3cc864', subColor: 'rgba(100,230,140,0.5)', y: H - pad - 70, sharedBy });
  return c;
}

// ─────────────────────────────────────────────────────────────
// CONGRATULATIONS — PAPER & INK
// Deep navy/sapphire + warm gold — academic, distinguished
// ─────────────────────────────────────────────────────────────
function drawCongratulationsCard_paper({ recipientName, recipientNameIsArabic, achievement, message: msg, sharedBy }) {
  const W = 1080, H = 1080, pad = 64;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');

  // Background — cool ivory with sapphire tint
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#eef3ff'); bg.addColorStop(1, '#dfe8ff');
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

  // Subtle dot grid
  ctx.fillStyle = 'rgba(40,80,200,0.025)';
  for (let x = 0; x < W; x += 32) for (let y2 = 0; y2 < H; y2 += 32)
    ctx.fillRect(x, y2, 1, 1);

  // Top accent bar
  const barG = ctx.createLinearGradient(0, 0, W, 0);
  barG.addColorStop(0, 'rgba(40,80,200,0)'); barG.addColorStop(0.5, 'rgba(40,80,200,0.5)'); barG.addColorStop(1, 'rgba(40,80,200,0)');
  ctx.fillStyle = barG; ctx.fillRect(0, 0, W, 5);

  // Corner star motifs
  [[pad + 10, pad + 22], [W - pad - 10, pad + 22]].forEach(([sx, sy], i) => {
    ctx.save(); ctx.translate(sx, sy); ctx.strokeStyle = 'rgba(40,80,200,0.15)'; ctx.lineWidth = 1.2;
    for (let a = 0; a < 6; a++) {
      const angle = (a / 6) * Math.PI * 2;
      ctx.beginPath(); ctx.moveTo(0, 0);
      ctx.lineTo(Math.cos(angle) * 16, Math.sin(angle) * 16); ctx.stroke();
    }
    ctx.restore();
  });

  const P = (f) => Math.round(H * f);
  const gr = ctx.createLinearGradient(pad * 2, 0, W - pad * 2, 0);
  gr.addColorStop(0, 'rgba(40,80,200,0)'); gr.addColorStop(0.5, 'rgba(40,80,200,0.35)'); gr.addColorStop(1, 'rgba(40,80,200,0)');
  const rule = (y) => { ctx.strokeStyle = gr; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(pad * 2, y); ctx.lineTo(W - pad * 2, y); ctx.stroke(); };

  eyebrow(ctx, W, 'M A B R O O K  ·  مَبْرُوكٌ', P(0.097), true);

  // Main Arabic — مَبْرُوكٌ
  const mainY = P(0.21);
  ctx.textAlign = 'center'; ctx.font = "700 96px 'Scheherazade New','Amiri',serif";
  const arG = ctx.createLinearGradient(0, mainY - 88, 0, mainY + 12);
  arG.addColorStop(0, '#2850c8'); arG.addColorStop(1, '#142880');
  ctx.fillStyle = arG; ctx.fillText('مَبْرُوكٌ', W / 2, mainY);

  ctx.font = "italic 400 26px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(40,80,200,0.55)'; ctx.textAlign = 'center';
  ctx.fillText('Mabrook  ·  Congratulations', W / 2, P(0.278));

  rule(P(0.325));

  // Recipient name hero
  let achieveY;
  if (recipientName) {
    ctx.font = "700 13px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(40,80,200,0.5)'; ctx.textAlign = 'center';
    ctx.fillText('W I S H I N G   A   B L E S S E D   J O U R N E Y   T O', W / 2, P(0.352));
    const nf = autoFitName(ctx, recipientName, recipientNameIsArabic, W - pad * 2 - 40, 130, 64);
    const nameEndY = nameHero(ctx, W, P(0.372), recipientName, recipientNameIsArabic, nf.size, true) + 6;
    rule(nameEndY + 14);
    achieveY = Math.max(nameEndY + 52, P(0.535));
  } else {
    achieveY = P(0.46);
  }

  // Achievement label
  if (achievement) {
    ctx.font = "700 13px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(40,80,200,0.45)'; ctx.textAlign = 'center';
    ctx.fillText('A C H I E V E M E N T', W / 2, achieveY);
    const achLines = wrap(ctx, achievement.toUpperCase(), "700 38px 'JetBrains Mono',monospace", W - pad * 2 - 40);
    ctx.font = "700 38px 'JetBrains Mono',monospace";
    const achG = ctx.createLinearGradient(0, achieveY, 0, achieveY + 60);
    achG.addColorStop(0, '#2850c8'); achG.addColorStop(1, '#1030a0');
    ctx.fillStyle = achG; ctx.textAlign = 'center';
    const achLineH = 50;
    achLines.slice(0, 2).forEach((l, i) => ctx.fillText(l, W / 2, achieveY + 46 + achLineH * i));
    achieveY = achieveY + 46 + achLineH * Math.min(achLines.length, 2);
  }

  // Secondary dua
  const duaY = Math.max(achieveY + 48, P(0.68));
  ctx.textAlign = 'center'; ctx.font = "700 52px 'Scheherazade New','Amiri',serif";
  const arG2 = ctx.createLinearGradient(0, duaY - 48, 0, duaY + 8);
  arG2.addColorStop(0, '#2850c8'); arG2.addColorStop(1, '#0e2080');
  ctx.fillStyle = arG2; ctx.fillText('بَارَكَ اللهُ لَكِ وَبَارَكَ عَلَيْكِ', W / 2, duaY);
  ctx.font = "italic 400 21px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(40,80,200,0.5)'; ctx.textAlign = 'center';
  ctx.fillText('Bārak-Allāhu laki wa bāraka ʿalayki  ·  May Allah bless you in this', W / 2, duaY + 40);

  const msgRuleY = duaY + 70;
  rule(msgRuleY);

  // Message
  const msgAreaTop = msgRuleY + 26;
  const footerTopY = H - pad - 88;
  if (msg && msgAreaTop < footerTopY - 40) {
    const msgLines = wrap(ctx, msg, "italic 400 28px 'Instrument Serif','Georgia',serif", W - pad * 2 - 40);
    ctx.font = "italic 400 28px 'Instrument Serif','Georgia',serif";
    ctx.fillStyle = 'rgba(40,80,200,0.65)'; ctx.textAlign = 'center';
    const lineH = 40;
    const maxLines = Math.min(msgLines.length, Math.floor((footerTopY - msgAreaTop - 10) / lineH));
    const totalH = maxLines * lineH;
    const msgFirstLine = msgAreaTop + Math.round((footerTopY - msgAreaTop - totalH) / 2);
    for (let i = 0; i < maxLines; i++) { ctx.fillText(msgLines[i], W / 2, msgFirstLine + lineH * (i + 1)); }
  }

  drawSharedByStrip(ctx, W, H, { nameColor: '#0a1850', urlColor: '#2850c8', subColor: 'rgba(40,80,200,0.55)', y: H - pad - 70, sharedBy });
  return c;
}

// ─────────────────────────────────────────────────────────────
// CONGRATULATIONS — NIGHT SKY
// Deep midnight navy + sapphire glow + gold Arabic
// ─────────────────────────────────────────────────────────────
function drawCongratulationsCard_night({ recipientName, recipientNameIsArabic, achievement, message: msg, sharedBy }) {
  const W = 1080, H = 1080, pad = 64;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');

  // Deep midnight background
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#080f1e'); bg.addColorStop(1, '#040a14');
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

  // Subtle dot grid
  ctx.fillStyle = 'rgba(100,160,255,0.025)';
  for (let x = 0; x < W; x += 32) for (let y2 = 0; y2 < H; y2 += 32)
    ctx.fillRect(x, y2, 1, 1);

  // Top accent bar
  const barG = ctx.createLinearGradient(0, 0, W, 0);
  barG.addColorStop(0, 'rgba(100,160,255,0)'); barG.addColorStop(0.5, 'rgba(100,160,255,0.55)'); barG.addColorStop(1, 'rgba(100,160,255,0)');
  ctx.fillStyle = barG; ctx.fillRect(0, 0, W, 5);

  // Radial glow
  const glow = ctx.createRadialGradient(W / 2, pad + 130, 30, W / 2, pad + 130, 340);
  glow.addColorStop(0, 'rgba(100,160,255,0.12)'); glow.addColorStop(1, 'rgba(100,160,255,0)');
  ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H);

  // Stars
  ctx.fillStyle = 'rgba(180,210,255,0.4)';
  [[W * 0.07, H * 0.04], [W * 0.9, H * 0.025], [W * 0.14, H * 0.11], [W * 0.84, H * 0.09],
   [W * 0.04, H * 0.2], [W * 0.94, H * 0.18], [W * 0.78, H * 0.05], [W * 0.22, H * 0.07]].forEach(([sx, sy]) => {
    ctx.beginPath(); ctx.arc(sx, sy, 1.5, 0, Math.PI * 2); ctx.fill();
  });

  const P = (f) => Math.round(H * f);
  const gr = ctx.createLinearGradient(pad * 2, 0, W - pad * 2, 0);
  gr.addColorStop(0, 'rgba(100,160,255,0)'); gr.addColorStop(0.5, 'rgba(100,160,255,0.45)'); gr.addColorStop(1, 'rgba(100,160,255,0)');
  const rule = (y) => { ctx.strokeStyle = gr; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(pad * 2, y); ctx.lineTo(W - pad * 2, y); ctx.stroke(); };

  eyebrow(ctx, W, 'M A B R O O K  ·  مَبْرُوكٌ', P(0.097), false);

  // Main Arabic
  const mainY = P(0.21);
  ctx.textAlign = 'center'; ctx.font = "700 96px 'Scheherazade New','Amiri',serif";
  const arG = ctx.createLinearGradient(0, mainY - 88, 0, mainY + 12);
  arG.addColorStop(0, '#90baff'); arG.addColorStop(1, '#3a6edc');
  ctx.fillStyle = arG; ctx.fillText('مَبْرُوكٌ', W / 2, mainY);

  ctx.font = "italic 400 26px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(140,190,255,0.55)'; ctx.textAlign = 'center';
  ctx.fillText('Mabrook  ·  Congratulations', W / 2, P(0.278));

  rule(P(0.325));

  // Recipient name hero
  let achieveY;
  if (recipientName) {
    ctx.font = "700 13px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(100,160,255,0.5)'; ctx.textAlign = 'center';
    ctx.fillText('W I S H I N G   A   B L E S S E D   J O U R N E Y   T O', W / 2, P(0.352));
    const nf = autoFitName(ctx, recipientName, recipientNameIsArabic, W - pad * 2 - 40, 130, 64);
    const nameEndY = nameHero(ctx, W, P(0.372), recipientName, recipientNameIsArabic, nf.size, false) + 6;
    rule(nameEndY + 14);
    achieveY = Math.max(nameEndY + 52, P(0.535));
  } else {
    achieveY = P(0.46);
  }

  // Achievement label
  if (achievement) {
    ctx.font = "700 13px 'JetBrains Mono',monospace"; ctx.fillStyle = 'rgba(100,160,255,0.4)'; ctx.textAlign = 'center';
    ctx.fillText('A C H I E V E M E N T', W / 2, achieveY);
    const achLines = wrap(ctx, achievement.toUpperCase(), "700 38px 'JetBrains Mono',monospace", W - pad * 2 - 40);
    ctx.font = "700 38px 'JetBrains Mono',monospace";
    const achG = ctx.createLinearGradient(0, achieveY, 0, achieveY + 60);
    achG.addColorStop(0, '#90baff'); achG.addColorStop(1, '#4878e8');
    ctx.fillStyle = achG; ctx.textAlign = 'center';
    const achLineH = 50;
    achLines.slice(0, 2).forEach((l, i) => ctx.fillText(l, W / 2, achieveY + 46 + achLineH * i));
    achieveY = achieveY + 46 + achLineH * Math.min(achLines.length, 2);
  }

  // Secondary dua
  const duaY = Math.max(achieveY + 48, P(0.68));
  ctx.textAlign = 'center'; ctx.font = "700 52px 'Scheherazade New','Amiri',serif";
  const arG2 = ctx.createLinearGradient(0, duaY - 48, 0, duaY + 8);
  arG2.addColorStop(0, '#90baff'); arG2.addColorStop(1, '#3050c0');
  ctx.fillStyle = arG2; ctx.fillText('بَارَكَ اللهُ لَكِ وَبَارَكَ عَلَيْكِ', W / 2, duaY);
  ctx.font = "italic 400 21px 'Instrument Serif','Georgia',serif";
  ctx.fillStyle = 'rgba(140,190,255,0.5)'; ctx.textAlign = 'center';
  ctx.fillText('Bārak-Allāhu laki wa bāraka ʿalayki  ·  May Allah bless you in this', W / 2, duaY + 40);

  const msgRuleY = duaY + 70;
  rule(msgRuleY);

  // Message
  const msgAreaTop = msgRuleY + 26;
  const footerTopY = H - pad - 88;
  if (msg && msgAreaTop < footerTopY - 40) {
    const msgLines = wrap(ctx, msg, "italic 400 28px 'Instrument Serif','Georgia',serif", W - pad * 2 - 40);
    ctx.font = "italic 400 28px 'Instrument Serif','Georgia',serif";
    ctx.fillStyle = 'rgba(140,190,255,0.65)'; ctx.textAlign = 'center';
    const lineH = 40;
    const maxLines = Math.min(msgLines.length, Math.floor((footerTopY - msgAreaTop - 10) / lineH));
    const totalH = maxLines * lineH;
    const msgFirstLine = msgAreaTop + Math.round((footerTopY - msgAreaTop - totalH) / 2);
    for (let i = 0; i < maxLines; i++) { ctx.fillText(msgLines[i], W / 2, msgFirstLine + lineH * (i + 1)); }
  }

  drawSharedByStrip(ctx, W, H, { nameColor: '#c0d8ff', urlColor: '#6aa0ff', subColor: 'rgba(140,190,255,0.5)', y: H - pad - 70, sharedBy });
  return c;
}

// ─────────────────────────────────────────────────────────────
// Helper: 2-column ranked participant grid — returns height used
// ─────────────────────────────────────────────────────────────
function _khatmGrid(ctx, participants, pad, y0, W, gold, goldMuted, text, isPaper) {
  if (!participants.length) return 0;
  const colGap = 24, rowH = 72, rankW = 54, countW = 96;
  const colW = (W - pad * 2 - colGap) / 2;
  const nameAvail = colW - rankW - countW - 8;
  participants.forEach((p, i) => {
    const col = i % 2, row = Math.floor(i / 2);
    const cx = pad + col * (colW + colGap);
    const bl = y0 + row * rowH + 48;
    // subtle gold bg highlight for rank #1
    if (i === 0) {
      ctx.fillStyle = isPaper ? 'rgba(140,107,31,0.07)' : 'rgba(197,155,58,0.09)';
      ctx.beginPath(); ctx.roundRect(cx - 6, y0 + row * rowH + 6, colW + 12, rowH - 12, 8); ctx.fill();
    }
    // rank ★ or number
    ctx.textAlign = 'left'; ctx.fillStyle = gold;
    ctx.font = `700 ${i === 0 ? 32 : 28}px 'JetBrains Mono',monospace`;
    ctx.fillText(i === 0 ? '★' : `${i + 1}.`, cx, bl);
    // name — truncate to fit
    ctx.font = `${i === 0 ? '700' : '600'} 36px 'Instrument Serif','Georgia',serif`;
    ctx.fillStyle = i === 0 ? gold : text;
    let name = p.name || '–';
    while (ctx.measureText(name).width > nameAvail && name.length > 2) name = name.slice(0, -1);
    if (name.length < (p.name || '').length) name = name.slice(0, -1) + '…';
    ctx.fillText(name, cx + rankW, bl);
    // count — right-aligned, gold for leader
    ctx.font = "600 28px 'JetBrains Mono',monospace";
    ctx.fillStyle = i === 0 ? gold : goldMuted; ctx.textAlign = 'right';
    ctx.fillText(p.count.toLocaleString(), cx + colW, bl);
  });
  return Math.ceil(participants.length / 2) * rowH;
}

// ═════════════════════════════════════════════════════════════
// KHATM PROGRESS CARD — Paper & Ink + Night Sky (shared logic)
// ═════════════════════════════════════════════════════════════
function drawKhatmProgressCard_paper(p) { return _drawKhatmProgress(p, true); }
function drawKhatmProgressCard_night(p) { return _drawKhatmProgress(p, false); }

function _drawKhatmProgress({ marhoumName, surahName, surahArabic, totalRead, target, openEnded, pct, participants, honoureeStatus, sharedBy }, isPaper) {
  const W = 1080, pad = 72, rowH = 72;

  // Pre-measure name for height planning
  const probe = document.createElement('canvas'); probe.width = W; probe.height = 300;
  const nf = autoFitName(probe.getContext('2d'), marhoumName, hasArabic(marhoumName), W - pad * 2 - 40, 96, 52);
  const nameH = nf.size + 48; // full line height + generous gap for script descenders

  // Simulate y to size canvas (surah is now 2 lines)
  let sy = pad + 64;
  sy += 44 + 36;                          // eyebrow + rule
  sy += 38 + nameH;                       // "for" label + name
  sy += 54 + (surahArabic ? 58 : 0);      // surah name + optional arabic line
  sy += 44 + 44 + 96 + 56 + 44;          // rule + bar + count + pct + rule
  if (participants.length) sy += 44 + 12 + Math.ceil(participants.length / 2) * rowH;
  sy += 32 + 44 + 112;                    // grid-gap + rule + dua
  const H = Math.max(1080, sy + 100 + 64);

  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  if (isPaper) paperBg(ctx, W, H); else nightBg(ctx, W, H);

  const gold  = isPaper ? '#8c6b1f' : '#e8c468';
  const goldM = isPaper ? 'rgba(140,107,31,0.55)' : 'rgba(197,155,58,0.55)';
  const goldF = isPaper ? 'rgba(140,107,31,0.12)' : 'rgba(197,155,58,0.10)';
  const txt   = isPaper ? '#1a1208' : '#f0e6cf';
  const txtM  = isPaper ? 'rgba(26,18,8,0.58)' : 'rgba(240,230,207,0.58)';

  // Thin inset decorative frame
  ctx.strokeStyle = isPaper ? 'rgba(140,107,31,0.18)' : 'rgba(197,155,58,0.15)';
  ctx.lineWidth = 1.5; ctx.strokeRect(18, 18, W - 36, H - 36);

  // Top gradient bar
  const tb = ctx.createLinearGradient(0, 0, W, 0);
  tb.addColorStop(0, 'rgba(0,0,0,0)');
  tb.addColorStop(0.5, isPaper ? 'rgba(140,107,31,0.6)' : 'rgba(197,155,58,0.45)');
  tb.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = tb; ctx.fillRect(0, 0, W, 6);

  let y = pad + 64;

  eyebrow(ctx, W, 'KHATM PROGRESS', y, isPaper); y += 44;
  goldRule(ctx, W, y, isPaper); y += 36;

  // "for" — elegant italic serif
  ctx.font = "italic 400 28px 'Instrument Serif','Georgia',serif"; ctx.fillStyle = goldM; ctx.textAlign = 'center';
  ctx.fillText('for', W / 2, y); y += 38;

  // Honouree name
  ctx.font = nf.font; ctx.textAlign = 'center';
  if (isPaper) {
    ctx.fillStyle = gold; ctx.shadowColor = 'rgba(140,107,31,0.22)'; ctx.shadowBlur = 18;
  } else {
    const ng = ctx.createLinearGradient(0, y, 0, y + nf.size);
    ng.addColorStop(0, '#e8c468'); ng.addColorStop(1, '#c59b3a');
    ctx.fillStyle = ng; ctx.shadowColor = 'rgba(232,196,104,0.4)'; ctx.shadowBlur = 28;
  }
  ctx.fillText(marhoumName, W / 2, y + Math.round(nf.size * 0.85));
  ctx.shadowBlur = 0; y += nameH;

  // Surah name — own line
  ctx.font = "400 44px 'Instrument Serif','Georgia',serif"; ctx.fillStyle = txt; ctx.textAlign = 'center';
  ctx.fillText(surahName, W / 2, y); y += 54;
  // Arabic — own line, slightly larger
  if (surahArabic) {
    ctx.font = "400 48px 'Scheherazade New','Amiri',serif"; ctx.fillStyle = txtM; ctx.textAlign = 'center';
    ctx.fillText(surahArabic, W / 2, y); y += 58;
  }

  goldRule(ctx, W, y, isPaper); y += 44;

  // Progress bar — full width
  const barX = pad, barW = W - pad * 2;
  if (!openEnded) {
    // Background track
    ctx.fillStyle = goldF; ctx.beginPath(); ctx.roundRect(barX, y, barW, 26, 13); ctx.fill();
    if (pct > 0) {
      const fg = ctx.createLinearGradient(barX, 0, barX + barW * pct, 0);
      fg.addColorStop(0, isPaper ? '#c59b3a' : '#e8c468'); fg.addColorStop(1, gold);
      ctx.fillStyle = fg; ctx.beginPath(); ctx.roundRect(barX, y, barW * pct, 26, 13); ctx.fill();
    }
  } else {
    // Open-ended: gentle full-width gold gradient + ∞ centred
    const oeG = ctx.createLinearGradient(barX, 0, barX + barW, 0);
    oeG.addColorStop(0, isPaper ? 'rgba(140,107,31,0.06)' : 'rgba(197,155,58,0.06)');
    oeG.addColorStop(0.5, isPaper ? 'rgba(140,107,31,0.18)' : 'rgba(197,155,58,0.16)');
    oeG.addColorStop(1, isPaper ? 'rgba(140,107,31,0.06)' : 'rgba(197,155,58,0.06)');
    ctx.fillStyle = oeG; ctx.beginPath(); ctx.roundRect(barX, y, barW, 26, 13); ctx.fill();
    ctx.strokeStyle = isPaper ? 'rgba(140,107,31,0.22)' : 'rgba(197,155,58,0.22)'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.roundRect(barX, y, barW, 26, 13); ctx.stroke();
    ctx.font = "400 17px 'JetBrains Mono',monospace"; ctx.fillStyle = goldM; ctx.textAlign = 'center';
    ctx.fillText('∞   ∞   ∞', W / 2, y + 18);
  }
  y += 44;

  // Big count stat
  ctx.textAlign = 'center';
  if (!isPaper) {
    const cg = ctx.createLinearGradient(0, y, 0, y + 80); cg.addColorStop(0, '#e8c468'); cg.addColorStop(1, '#c59b3a'); ctx.fillStyle = cg;
  } else { ctx.fillStyle = gold; }
  ctx.font = "700 80px 'JetBrains Mono',monospace";
  ctx.fillText(openEnded ? totalRead.toLocaleString() : `${totalRead} / ${target}`, W / 2, y + 70);
  y += 96;

  // Subline — no redundant repeat of count for open-ended
  ctx.font = "400 36px 'Instrument Serif','Georgia',serif"; ctx.fillStyle = txtM; ctx.textAlign = 'center';
  ctx.fillText(openEnded ? 'ongoing ∞' : `${Math.round(pct * 100)}% complete`, W / 2, y);
  y += 56;

  goldRule(ctx, W, y, isPaper); y += 44;

  // Participant grid — all participants
  if (participants.length) {
    ctx.font = "700 22px 'JetBrains Mono',monospace"; ctx.fillStyle = goldM; ctx.textAlign = 'center';
    ctx.fillText('P A R T I C I P A N T S', W / 2, y); y += 44;
    ctx.fillStyle = isPaper ? 'rgba(140,107,31,0.15)' : 'rgba(197,155,58,0.15)';
    ctx.fillRect(pad, y, W - pad * 2, 1); y += 12;
    y += _khatmGrid(ctx, participants, pad, y, W, gold, goldM, txt, isPaper);
  }
  y += 32;

  goldRule(ctx, W, y, isPaper); y += 44;

  // Dua
  const duaAr = honoureeStatus === 'departed'
    ? 'اللَّهُمَّ اغْفِرْ لَهُمْ وَارْحَمْهُمْ'
    : 'اللَّهُمَّ اشْفِهِمْ شِفَاءً عَاجِلاً';
  ctx.font = "700 56px 'Scheherazade New','Amiri',serif"; ctx.textAlign = 'center';
  if (isPaper) {
    ctx.fillStyle = gold; ctx.shadowColor = 'rgba(140,107,31,0.2)'; ctx.shadowBlur = 12;
  } else {
    const dg = ctx.createLinearGradient(0, y, 0, y + 64); dg.addColorStop(0, '#e8c468'); dg.addColorStop(1, '#c59b3a'); ctx.fillStyle = dg;
  }
  ctx.fillText(duaAr, W / 2, y + 54); ctx.shadowBlur = 0;

  drawSharedByStrip(ctx, W, H, { nameColor: gold, urlColor: gold, subColor: goldM, y: H - 64 - 80, sharedBy });
  return c;
}

// ═════════════════════════════════════════════════════════════
// KHATM CLOSE CARD — Paper & Ink + Night Sky (shared logic)
// ═════════════════════════════════════════════════════════════
function drawKhatmCloseCard_paper(p) { return _drawKhatmClose(p, true); }
function drawKhatmCloseCard_night(p) { return _drawKhatmClose(p, false); }

function _drawKhatmClose({ marhoumName, surahName, surahArabic, totalRead, participants, honoureeStatus, sharedBy }, isPaper) {
  const W = 1080, pad = 72, rowH = 72;

  const probe = document.createElement('canvas'); probe.width = W; probe.height = 300;
  const nf = autoFitName(probe.getContext('2d'), marhoumName, hasArabic(marhoumName), W - pad * 2 - 40, 88, 48);
  const nameH = nf.size + 40; // full line height + gap for script descenders

  // Simulate y to size canvas (surah now 2 lines)
  let sy = pad + 56;
  sy += 40 + 32;                          // eyebrow + rule
  sy += 96 + 32;                          // آمِيْن + rule
  sy += 30 + nameH;                       // "dedicated to" + name
  sy += 54 + (surahArabic ? 58 : 0);      // surah name + optional arabic line
  sy += 92 + 44 + 44;                     // count + subline + rule
  if (participants.length) sy += 42 + 12 + Math.ceil(participants.length / 2) * rowH;
  sy += 32 + 44 + 112;                    // gap + rule + dua
  const H = Math.max(1080, sy + 100 + 64);

  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  if (isPaper) paperBg(ctx, W, H); else nightBg(ctx, W, H);

  const gold  = isPaper ? '#8c6b1f' : '#e8c468';
  const goldM = isPaper ? 'rgba(140,107,31,0.55)' : 'rgba(197,155,58,0.55)';
  const txt   = isPaper ? '#1a1208' : '#f0e6cf';
  const txtM  = isPaper ? 'rgba(26,18,8,0.58)' : 'rgba(240,230,207,0.58)';

  // Thin inset decorative frame
  ctx.strokeStyle = isPaper ? 'rgba(140,107,31,0.18)' : 'rgba(197,155,58,0.15)';
  ctx.lineWidth = 1.5; ctx.strokeRect(18, 18, W - 36, H - 36);

  const tb = ctx.createLinearGradient(0, 0, W, 0);
  tb.addColorStop(0, 'rgba(0,0,0,0)');
  tb.addColorStop(0.5, isPaper ? 'rgba(140,107,31,0.6)' : 'rgba(197,155,58,0.45)');
  tb.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = tb; ctx.fillRect(0, 0, W, 6);

  let y = pad + 56;

  eyebrow(ctx, W, 'KHATM COMPLETE · ALHAMDULILLAH', y, isPaper); y += 40;
  goldRule(ctx, W, y, isPaper); y += 32;

  // Big آمِيْن hero
  ctx.font = "700 92px 'Scheherazade New','Amiri',serif"; ctx.textAlign = 'center';
  if (isPaper) {
    ctx.fillStyle = gold; ctx.shadowColor = 'rgba(140,107,31,0.22)'; ctx.shadowBlur = 22;
  } else {
    const ag = ctx.createLinearGradient(0, y, 0, y + 100);
    ag.addColorStop(0, '#e8c468'); ag.addColorStop(1, '#c59b3a');
    ctx.fillStyle = ag; ctx.shadowColor = 'rgba(232,196,104,0.4)'; ctx.shadowBlur = 30;
  }
  ctx.fillText('آمِيْن', W / 2, y + 74); ctx.shadowBlur = 0; y += 96;

  goldRule(ctx, W, y, isPaper); y += 32;

  // "dedicated to" — italic serif
  ctx.font = "italic 400 28px 'Instrument Serif','Georgia',serif"; ctx.fillStyle = goldM; ctx.textAlign = 'center';
  ctx.fillText('dedicated to', W / 2, y); y += 30;

  ctx.font = nf.font; ctx.textAlign = 'center';
  if (isPaper) {
    ctx.fillStyle = gold; ctx.shadowColor = 'rgba(140,107,31,0.22)'; ctx.shadowBlur = 18;
  } else {
    const ng = ctx.createLinearGradient(0, y, 0, y + nf.size);
    ng.addColorStop(0, '#e8c468'); ng.addColorStop(1, '#c59b3a');
    ctx.fillStyle = ng; ctx.shadowColor = 'rgba(232,196,104,0.38)'; ctx.shadowBlur = 24;
  }
  ctx.fillText(marhoumName, W / 2, y + Math.round(nf.size * 0.85)); ctx.shadowBlur = 0; y += nameH;

  // Surah — own line
  ctx.font = "400 44px 'Instrument Serif','Georgia',serif"; ctx.fillStyle = txt; ctx.textAlign = 'center';
  ctx.fillText(surahName, W / 2, y); y += 54;
  if (surahArabic) {
    ctx.font = "400 48px 'Scheherazade New','Amiri',serif"; ctx.fillStyle = txtM; ctx.textAlign = 'center';
    ctx.fillText(surahArabic, W / 2, y); y += 58;
  }

  // Final count
  ctx.textAlign = 'center';
  if (!isPaper) {
    const cg = ctx.createLinearGradient(0, y, 0, y + 80); cg.addColorStop(0, '#e8c468'); cg.addColorStop(1, '#c59b3a'); ctx.fillStyle = cg;
  } else { ctx.fillStyle = gold; }
  ctx.font = "700 80px 'JetBrains Mono',monospace";
  ctx.fillText(`${totalRead.toLocaleString()} reads`, W / 2, y + 68); y += 92;

  ctx.font = "italic 400 34px 'Instrument Serif','Georgia',serif"; ctx.fillStyle = txtM; ctx.textAlign = 'center';
  ctx.fillText('May Allah accept from all of us', W / 2, y); y += 44;

  goldRule(ctx, W, y, isPaper); y += 44;

  // Participant grid
  if (participants.length) {
    ctx.font = "700 22px 'JetBrains Mono',monospace"; ctx.fillStyle = goldM; ctx.textAlign = 'center';
    ctx.fillText('J A Z A K A L L A H   K H A I R', W / 2, y); y += 42;
    ctx.fillStyle = isPaper ? 'rgba(140,107,31,0.15)' : 'rgba(197,155,58,0.15)';
    ctx.fillRect(pad, y, W - pad * 2, 1); y += 12;
    y += _khatmGrid(ctx, participants, pad, y, W, gold, goldM, txt, isPaper);
  }
  y += 32;

  goldRule(ctx, W, y, isPaper); y += 44;

  // Dua
  const duaAr = honoureeStatus === 'departed'
    ? 'اللَّهُمَّ اغْفِرْ لَهُمْ وَارْحَمْهُمْ وَعَافِهِمْ'
    : 'اللَّهُمَّ اشْفِهِمْ شِفَاءً كَامِلاً';
  ctx.font = "700 52px 'Scheherazade New','Amiri',serif"; ctx.textAlign = 'center';
  if (isPaper) {
    ctx.fillStyle = gold; ctx.shadowColor = 'rgba(140,107,31,0.2)'; ctx.shadowBlur = 12;
  } else {
    const dg = ctx.createLinearGradient(0, y, 0, y + 60); dg.addColorStop(0, '#e8c468'); dg.addColorStop(1, '#c59b3a'); ctx.fillStyle = dg;
  }
  ctx.fillText(duaAr, W / 2, y + 52); ctx.shadowBlur = 0;

  drawSharedByStrip(ctx, W, H, { nameColor: gold, urlColor: gold, subColor: goldM, y: H - 64 - 80, sharedBy });
  return c;
}

// ═════════════════════════════════════════════════════════════
// GHIDHA SUMMARY CARD — Ghidha Share Card v2 design
// ═════════════════════════════════════════════════════════════
export function drawGhidhaSummaryCard({
  date, calories, calorieGoal, protein, carbs, fat,
  proteinGoal, carbsGoal, fatGoal, steps, activeBurned, basalBurned,
  sleepMins, deepMins, remMins, water, waterGoal, sharedBy,
}) {
  const W = 1080, H = 1080, padX = 80;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');

  // Design tokens from v2 spec
  const ink    = '#1F1A12';
  const ink2   = '#3A3225';
  const ink3   = '#6E5E3F';
  const gold   = '#8A6A28';
  const gold2  = '#A87420';
  const gold3  = '#C8A046';
  const rust   = '#7A3A22';
  const paper  = '#EFE6CF';
  const paper2 = '#E6DCC0';
  const paper3 = '#DBCFAD';

  // ── BACKGROUND — radial warm parchment ──
  const bgGrad = ctx.createRadialGradient(W*0.5, H*0.3, 0, W*0.5, H*0.5, W*0.85);
  bgGrad.addColorStop(0,    '#F4ECD6');
  bgGrad.addColorStop(0.55, paper);
  bgGrad.addColorStop(1,    paper2);
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, W, H);

  // Paper grain
  ctx.fillStyle = 'rgba(56,42,20,0.055)';
  for (let i = 0; i < 2200; i++) ctx.fillRect(Math.random()*W, Math.random()*H, 1, 1);

  // Vignette
  const vig = ctx.createRadialGradient(W/2, H/2, W*0.35, W/2, H/2, W*0.75);
  vig.addColorStop(0, 'rgba(0,0,0,0)');
  vig.addColorStop(1, 'rgba(80,55,20,0.18)');
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, W, H);

  // ── CORNER ORNAMENTS ──
  const drawCorner = (ox, oy, fx, fy) => {
    ctx.save();
    ctx.translate(ox, oy);
    ctx.scale(fx, fy);
    ctx.strokeStyle = gold2;
    ctx.lineCap = 'round';
    // Outer L bracket
    ctx.lineWidth = 1.2;
    ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, 28); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(28, 0); ctx.stroke();
    // Inner L bracket
    ctx.lineWidth = 0.6;
    ctx.strokeStyle = 'rgba(168,116,32,0.5)';
    ctx.beginPath(); ctx.moveTo(6, 6); ctx.lineTo(6, 20); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(6, 6); ctx.lineTo(20, 6); ctx.stroke();
    // Corner dot
    ctx.fillStyle = gold2;
    ctx.beginPath(); ctx.arc(0, 0, 2.5, 0, Math.PI*2); ctx.fill();
    // Outer ticks
    ctx.strokeStyle = 'rgba(168,116,32,0.5)';
    ctx.lineWidth = 0.6;
    ctx.beginPath(); ctx.moveTo(0, 36); ctx.lineTo(0, 44); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(36, 0); ctx.lineTo(44, 0); ctx.stroke();
    ctx.restore();
  };
  drawCorner(36, 36, 1, 1);
  drawCorner(W-36, 36, -1, 1);
  drawCorner(36, H-36, 1, -1);
  drawCorner(W-36, H-36, -1, -1);

  // time formatter
  const fmt = m => { if (!m) return '—'; const h2 = Math.floor(m/60), n = m%60; return h2>0&&n>0 ? h2+'h '+n+'m' : h2>0 ? h2+'h' : m+'m'; };

  // ── STAR MEDALLION ──
  const medX = W/2, medY = 84, medR = 32;
  // Circle
  ctx.strokeStyle = gold2;
  ctx.lineWidth = 1.2;
  ctx.beginPath(); ctx.arc(medX, medY, medR, 0, Math.PI*2); ctx.stroke();
  ctx.fillStyle = '#F8F0D8';
  ctx.beginPath(); ctx.arc(medX, medY, medR-1, 0, Math.PI*2); ctx.fill();
  // 9-pointed star
  const pts = 9, srOut = 22, srIn = 9;
  ctx.strokeStyle = gold2;
  ctx.lineWidth = 1.1;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  for (let i = 0; i < pts*2; i++) {
    const r = i%2===0 ? srOut : srIn;
    const a = (i/(pts*2))*Math.PI*2 - Math.PI/2;
    i===0 ? ctx.moveTo(medX+r*Math.cos(a), medY+r*Math.sin(a)) : ctx.lineTo(medX+r*Math.cos(a), medY+r*Math.sin(a));
  }
  ctx.closePath(); ctx.stroke();
  ctx.fillStyle = gold2;
  ctx.beginPath(); ctx.arc(medX, medY, 2.4, 0, Math.PI*2); ctx.fill();

  // ── MASTHEAD ──
  ctx.textAlign = 'center';
  if (ctx.letterSpacing !== undefined) ctx.letterSpacing = '11px';
  ctx.font = "600 22px 'Fraunces', serif";
  ctx.fillStyle = gold2;
  ctx.fillText('GHIDHA', W/2, 148);
  if (ctx.letterSpacing !== undefined) ctx.letterSpacing = '0px';

  ctx.font = "500 11px 'JetBrains Mono', monospace";
  ctx.fillStyle = ink3;
  ctx.fillText('Daily Summary  ·  ' + (date||'').toUpperCase(), W/2, 168);

  // Double rule with diamond gem
  const ruleW = 280, ruleX = W/2 - ruleW/2;
  ctx.fillStyle = gold2;
  ctx.fillRect(ruleX, 182, ruleW, 0.6);
  ctx.fillRect(ruleX, 187, ruleW, 0.6);
  ctx.save();
  ctx.translate(W/2, 184.8);
  ctx.rotate(Math.PI/4);
  ctx.fillStyle = gold2;
  ctx.fillRect(-3.5, -3.5, 7, 7);
  ctx.restore();

  // ── HERO ──
  ctx.textAlign = 'center';
  ctx.font = "500 12px 'JetBrains Mono', monospace";
  ctx.fillStyle = gold2;
  ctx.fillText('ENERGY CONSUMED', W/2, 228);

  // Large Fraunces number
  const heroStr = (calories||0).toLocaleString();
  ctx.font = "300 200px 'Fraunces', serif";
  ctx.fillStyle = ink;
  ctx.shadowColor = 'rgba(255,255,255,0.6)';
  ctx.shadowOffsetX = 1; ctx.shadowOffsetY = 1; ctx.shadowBlur = 0;
  ctx.fillText(heroStr, W/2, 406);
  ctx.shadowColor = 'transparent'; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0;

  // "of X" overlay — positioned top-right of the number
  const numW = ctx.measureText(heroStr).width;
  ctx.font = "italic 400 28px 'Instrument Serif', serif";
  ctx.fillStyle = ink3;
  ctx.textAlign = 'left';
  ctx.fillText('of ' + (calorieGoal||0).toLocaleString(), W/2 + numW/2 + 10, 280);

  // Caption
  const calPct = Math.min((calories||0) / (calorieGoal||2000), 1);
  const pctStr = Math.round(calPct * 100) + '%';
  const remaining = Math.max(0, (calorieGoal||2000) - (calories||0));
  ctx.textAlign = 'center';
  ctx.font = "italic 400 21px 'Instrument Serif', serif";
  ctx.fillStyle = ink2;
  ctx.fillText(pctStr + " of today's measure  ·  " + remaining.toLocaleString() + ' kcal remain', W/2, 432);

  // Progress bar — 480px × 5px with gold border
  const barX = W/2 - 240, barW2 = 480;
  ctx.fillStyle = paper3;
  ctx.fillRect(barX, 444, barW2, 5);
  ctx.strokeStyle = gold2;
  ctx.lineWidth = 0.6;
  ctx.strokeRect(barX, 444, barW2, 5);
  if (calPct > 0) {
    const barFill = ctx.createLinearGradient(barX, 0, barX + barW2, 0);
    barFill.addColorStop(0, gold2);
    barFill.addColorStop(1, gold3);
    ctx.fillStyle = barFill;
    ctx.fillRect(barX + 0.5, 444.5, Math.round(barW2 * calPct) - 1, 4);
  }

  // Bar graduation marks: 0  500  NOW  1.5K  2K
  const markLabels = ['0', '500', 'NOW', '1.5K', '2K'];
  const markFracs  = [0, 0.25, calPct, 0.75, 1.0];
  for (let i = 0; i < markLabels.length; i++) {
    const mx = barX + markFracs[i] * barW2;
    ctx.textAlign = i===0 ? 'left' : i===markLabels.length-1 ? 'right' : 'center';
    ctx.font = markLabels[i]==='NOW' ? "600 10px 'JetBrains Mono', monospace" : "500 10px 'JetBrains Mono', monospace";
    ctx.fillStyle = markLabels[i]==='NOW' ? gold2 : ink3;
    ctx.fillText(markLabels[i], mx, 464);
  }

  // ── MACROS — 3 columns with gold borders ──
  const macroY = 500, macroH = 118, macroW = (W - 2*padX) / 3;
  ctx.fillStyle = gold2;
  ctx.fillRect(padX, macroY, W-2*padX, 0.6);

  const mData = [
    { label:'Protein',      val:protein||0, goal:proteinGoal||150 },
    { label:'Carbohydrate', val:carbs||0,   goal:carbsGoal||225   },
    { label:'Fat',          val:fat||0,     goal:fatGoal||65       },
  ];
  for (let i = 0; i < 3; i++) {
    const mx = padX + i*macroW;
    const m  = mData[i];
    const over = m.val > m.goal;
    const mpct = Math.min(m.val/(m.goal||1), 1);

    if (i > 0) { ctx.fillStyle = gold2; ctx.fillRect(mx, macroY, 0.6, macroH); }

    // Tag
    ctx.textAlign = 'center';
    ctx.font = "500 11px 'JetBrains Mono', monospace";
    ctx.fillStyle = gold2;
    ctx.fillText(m.label.toUpperCase(), mx + macroW/2, macroY + 20);

    // Number (Fraunces 400 52px) + unit "g" (28px)
    const valStr = Math.round(m.val).toString();
    ctx.font = "400 52px 'Fraunces', serif";
    ctx.fillStyle = ink;
    ctx.textAlign = 'right';
    const vw = ctx.measureText(valStr).width;
    ctx.fillText(valStr, mx + macroW/2 + vw/2, macroY + 72);
    ctx.font = "300 26px 'Fraunces', serif";
    ctx.fillStyle = ink3;
    ctx.textAlign = 'left';
    ctx.fillText('g', mx + macroW/2 + vw/2 + 3, macroY + 72);

    // Meta text
    const metaText = over
      ? '+' + Math.round(m.val - m.goal) + 'g over · ' + m.goal + 'g target'
      : Math.round(Math.max(0, m.goal - m.val)) + 'g remain · ' + m.goal + 'g target';
    ctx.textAlign = 'center';
    ctx.font = "italic 400 13px 'Instrument Serif', serif";
    ctx.fillStyle = ink3;
    ctx.fillText(metaText, mx + macroW/2, macroY + 88);

    // Macro bar (130px × 2px)
    const bx = mx + macroW/2 - 65;
    ctx.fillStyle = paper3;
    ctx.fillRect(bx, macroY + 100, 130, 2);
    if (mpct > 0) {
      ctx.fillStyle = over ? rust : gold2;
      ctx.fillRect(bx, macroY + 100, Math.round(130 * mpct), 2);
    }
  }
  ctx.fillStyle = gold2;
  ctx.fillRect(padX, macroY + macroH, W-2*padX, 0.6);

  // ── SECONDARY ROW — Activity · Hydration · Sleep ──
  const secY = macroY + macroH + 26, secH = 112, secW = (W - 2*padX) / 3;
  ctx.fillStyle = 'rgba(138,106,40,0.3)';
  ctx.fillRect(padX, secY, W-2*padX, 0.5);

  const secItems = [
    {
      tag: 'ACTIVITY',
      bigNum: (steps||0).toLocaleString(), bigUnit: ' steps',
      meta: activeBurned > 0 ? activeBurned + ' kcal active' : '—',
      water: false,
    },
    {
      tag: 'HYDRATION',
      bigNum: (water||0).toString(), bigUnit: ' cups',
      meta: (water||0) + ' of ' + (waterGoal||8) + ' · sipped in thirds',
      water: true,
    },
    {
      tag: 'SLEEP',
      bigNum: sleepMins ? Math.floor(sleepMins/60).toString() : '—',
      bigUnit: 'h',
      bigNum2: sleepMins && sleepMins%60 > 0 ? (sleepMins%60).toString() : '',
      bigUnit2: sleepMins && sleepMins%60 > 0 ? 'm' : '',
      meta: (deepMins > 0 ? fmt(deepMins) + ' deep' : '') + (remMins > 0 ? ' · ' + fmt(remMins) + ' REM' : '') || '—',
      water: false,
    },
  ];

  for (let i = 0; i < 3; i++) {
    const sx = padX + i*secW;
    const sd = secItems[i];

    if (i > 0) { ctx.fillStyle = 'rgba(138,106,40,0.3)'; ctx.fillRect(sx, secY, 0.5, secH); }

    ctx.textAlign = 'center';
    ctx.font = "500 11px 'JetBrains Mono', monospace";
    ctx.fillStyle = gold2;
    ctx.fillText(sd.tag, sx + secW/2, secY + 20);

    // Big number (Fraunces 400 40px)
    ctx.font = "400 40px 'Fraunces', serif";
    ctx.fillStyle = ink;
    if (sd.bigNum2) {
      // Sleep: "8h 10m" — build centered composite
      const hW = ctx.measureText(sd.bigNum).width;
      ctx.font = "300 20px 'Fraunces', serif";
      const huW = ctx.measureText(sd.bigUnit).width;
      ctx.font = "400 40px 'Fraunces', serif";
      const mW = ctx.measureText(sd.bigNum2).width;
      ctx.font = "300 20px 'Fraunces', serif";
      const muW = ctx.measureText(sd.bigUnit2).width;
      ctx.font = "400 40px 'Fraunces', serif";
      const total = hW + huW + 6 + mW + muW;
      let cx2 = sx + secW/2 - total/2;
      ctx.fillStyle = ink;
      ctx.textAlign = 'left';
      ctx.fillText(sd.bigNum, cx2, secY + 62);
      cx2 += hW;
      ctx.font = "300 20px 'Fraunces', serif";
      ctx.fillStyle = ink3;
      ctx.fillText(sd.bigUnit, cx2, secY + 62);
      cx2 += huW + 6;
      ctx.font = "400 40px 'Fraunces', serif";
      ctx.fillStyle = ink;
      ctx.fillText(sd.bigNum2, cx2, secY + 62);
      cx2 += mW;
      ctx.font = "300 20px 'Fraunces', serif";
      ctx.fillStyle = ink3;
      ctx.fillText(sd.bigUnit2, cx2, secY + 62);
    } else {
      ctx.textAlign = 'right';
      const nW = ctx.measureText(sd.bigNum).width;
      ctx.fillText(sd.bigNum, sx + secW/2 + nW/2, secY + 62);
      ctx.font = "300 20px 'Fraunces', serif";
      ctx.fillStyle = ink3;
      ctx.textAlign = 'left';
      ctx.fillText(sd.bigUnit, sx + secW/2 + nW/2 + 2, secY + 62);
    }

    // Meta
    ctx.textAlign = 'center';
    ctx.font = "italic 400 13px 'Instrument Serif', serif";
    ctx.fillStyle = ink3;
    ctx.fillText(sd.meta, sx + secW/2, secY + 80);

    // Water cups visualization
    if (sd.water) {
      const total = waterGoal||8, filled = Math.min(water||0, total);
      const cupW = 11, cupH = 15, gap = 5;
      const rowW = total*(cupW+gap) - gap;
      let cx2 = sx + secW/2 - rowW/2;
      const cy2 = secY + 92;
      for (let ci = 0; ci < total; ci++) {
        ctx.strokeStyle = gold2;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(cx2, cy2);
        ctx.lineTo(cx2 + cupW, cy2);
        ctx.lineTo(cx2 + cupW, cy2 + cupH - 3);
        ctx.arcTo(cx2 + cupW, cy2 + cupH, cx2 + cupW - 3, cy2 + cupH, 3);
        ctx.lineTo(cx2 + 3, cy2 + cupH);
        ctx.arcTo(cx2, cy2 + cupH, cx2, cy2 + cupH - 3, 3);
        ctx.closePath();
        ctx.stroke();
        if (ci < filled) {
          ctx.save();
          ctx.clip();
          ctx.fillStyle = gold2;
          ctx.fillRect(cx2, cy2 + cupH*0.22, cupW, cupH);
          ctx.restore();
        }
        cx2 += cupW + gap;
      }
    }
  }

  ctx.fillStyle = 'rgba(138,106,40,0.3)';
  ctx.fillRect(padX, secY + secH, W-2*padX, 0.5);

  // ── HADITH PLATE ──
  const hY = secY + secH + 32, hH = 166, hW = W - 2*padX;
  const hadithBg = ctx.createLinearGradient(0, hY, 0, hY + hH);
  hadithBg.addColorStop(0, 'rgba(138,106,40,0.06)');
  hadithBg.addColorStop(1, 'rgba(138,106,40,0.02)');
  ctx.fillStyle = hadithBg;
  ctx.fillRect(padX, hY, hW, hH);

  ctx.strokeStyle = gold;
  ctx.lineWidth = 0.6;
  ctx.strokeRect(padX, hY, hW, hH);

  // Corner brackets on hadith plate
  const bL = 14;
  ctx.strokeStyle = gold2;
  ctx.lineWidth = 1.2;
  ctx.lineCap = 'square';
  [[padX, hY, 1, 1], [padX+hW, hY, -1, 1], [padX, hY+hH, 1, -1], [padX+hW, hY+hH, -1, -1]].forEach(([bx, by, sx2, sy2]) => {
    ctx.beginPath();
    ctx.moveTo(bx, by + sy2*bL); ctx.lineTo(bx, by); ctx.lineTo(bx + sx2*bL, by);
    ctx.stroke();
  });
  ctx.lineCap = 'butt';

  // Hadith tag with side segments
  const tagTxt = 'Hadith · The Thirds';
  ctx.textAlign = 'center';
  ctx.font = "500 11px 'JetBrains Mono', monospace";
  ctx.fillStyle = gold2;
  ctx.fillText(tagTxt, W/2, hY + 22);
  const tagW2 = ctx.measureText(tagTxt).width;
  ctx.fillStyle = gold2;
  ctx.fillRect(W/2 - tagW2/2 - 38, hY + 17, 28, 0.6);
  ctx.fillRect(W/2 + tagW2/2 + 10, hY + 17, 28, 0.6);

  // Arabic
  ctx.font = "400 32px 'Amiri', serif";
  ctx.fillStyle = ink;
  ctx.fillText('فَثُلُثٌ لِطَعَامِهِ، وَثُلُثٌ لِشَرَابِهِ، وَثُلُثٌ لِنَفَسِهِ', W/2, hY + 72);

  // English
  ctx.font = "italic 400 20px 'Instrument Serif', serif";
  ctx.fillStyle = ink2;
  ctx.fillText('“A third for food, a third for drink, a third for breath.”', W/2, hY + 110);

  // Cite
  ctx.font = "500 11px 'JetBrains Mono', monospace";
  ctx.fillStyle = ink3;
  ctx.fillText('— Tirmidhi · 2380', W/2, hY + 138);

  // ── FOOTER ──
  const footLabelY = H - 48 - 46, footUrlY = H - 48 - 14;

  // Left: No. date-stamp
  ctx.textAlign = 'left';
  ctx.font = "500 10px 'JetBrains Mono', monospace";
  ctx.fillStyle = ink3;
  const dateStamp = (date||'').replace(/\s/g, '·').toUpperCase();
  ctx.fillText('No. ' + dateStamp, padX, footLabelY);

  // Right: Edition I
  ctx.textAlign = 'right';
  ctx.fillText('Edition I', W - padX, footLabelY);

  // Center label
  ctx.textAlign = 'center';
  ctx.fillText('From the Sirat companion', W/2, footLabelY);

  // Center URL — Fraunces italic 600 28px with gold underline + em-dashes
  ctx.font = "italic 600 28px 'Fraunces', serif";
  ctx.fillStyle = ink;
  ctx.fillText('— sirat.co.za —', W/2, footUrlY);
  const urlW = ctx.measureText('— sirat.co.za —').width;
  ctx.fillStyle = gold2;
  ctx.fillRect(W/2 - urlW/2, footUrlY + 3, urlW, 1);

  return c;
}

// ─────────────────────────────────────────────────────────────
// EXERCISE SUMMARY CARD
// ─────────────────────────────────────────────────────────────
export function drawExerciseShareCard({
  workoutName, dateStr, startTime, endTime, durationMin,
  calories, avgHr, maxHr, hrZoneSeconds,
  volume, totalSets, totalExercises, withTrainer,
  topSets,
}) {
  const W = 1080, H = 1080;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');

  const teal   = '#3ECFB8';
  const tealGl = 'rgba(62,207,184,0.45)';
  const amber  = '#E0A030';
  const amberGl= 'rgba(224,160,48,0.5)';
  const coral  = '#E05A40';
  const coralGl= 'rgba(224,90,64,0.5)';
  const purple = '#A080E0';
  const purpGl = 'rgba(160,128,224,0.5)';
  const ink1   = '#F0ECE0';
  const ink2   = '#B0A898';
  const ink3   = '#706860';
  const ink4   = '#3C3830';
  const lineC  = 'rgba(255,255,255,0.07)';
  const lineHi = 'rgba(255,255,255,0.13)';

  const padX = 72, padTop = 56, padBot = 52;
  const cW = W - padX * 2;

  const colorForMuscle = (mg) => {
    const m = (mg || '').toLowerCase();
    if (m === 'chest' || m === 'back') return [teal, tealGl];
    if (m === 'shoulders' || m === 'biceps') return [amber, amberGl];
    if (m === 'triceps' || m === 'core') return [coral, coralGl];
    return [purple, purpGl];
  };

  const mono = (sz, wt) => `${wt || 400} ${sz}px 'JetBrains Mono', monospace`;
  const fraun = (sz, wt, it) => `${it ? 'italic ' : ''}${wt || 300} ${sz}px 'Fraunces', serif`;
  const instr = (sz) => `italic 400 ${sz}px 'Instrument Serif', serif`;

  // ── BG ──
  const bgGrad = ctx.createLinearGradient(0, 0, W * 0.8, H);
  bgGrad.addColorStop(0, '#0A0C12');
  bgGrad.addColorStop(0.6, '#06060A');
  bgGrad.addColorStop(1, '#080A10');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, W, H);

  // Grid
  ctx.strokeStyle = 'rgba(62,207,184,0.022)';
  ctx.lineWidth = 1;
  for (let gx = 0; gx <= W; gx += 60) {
    ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, H); ctx.stroke();
  }
  for (let gy = 0; gy <= H; gy += 60) {
    ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(W, gy); ctx.stroke();
  }

  // Bloom
  [[W*0.2, H*0.2, W*0.38, 'rgba(62,207,184,0.13)'],
   [W*0.8, H*0.75, W*0.4, 'rgba(224,160,48,0.10)'],
   [W*0.55, H*0.95, W*0.35, 'rgba(160,128,224,0.07)']].forEach(([bx, by, br, bc]) => {
    const bl = ctx.createRadialGradient(bx, by, 0, bx, by, br);
    bl.addColorStop(0, bc); bl.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = bl; ctx.fillRect(0, 0, W, H);
  });

  // Scanlines
  for (let sl = 0; sl < H; sl += 4) {
    ctx.fillStyle = 'rgba(0,0,0,0.024)';
    ctx.fillRect(0, sl + 3, W, 1);
  }

  // Vignette
  const vig = ctx.createRadialGradient(W/2, H/2, W*0.4, W/2, H/2, W*0.78);
  vig.addColorStop(0, 'rgba(0,0,0,0)');
  vig.addColorStop(1, 'rgba(0,0,0,0.55)');
  ctx.fillStyle = vig; ctx.fillRect(0, 0, W, H);

  // HUD corners
  const hudSz = 22, hudOff = 28;
  [[hudOff, hudOff, 1, 1], [W-hudOff, hudOff, -1, 1],
   [hudOff, H-hudOff, 1, -1], [W-hudOff, H-hudOff, -1, -1]].forEach(([hx, hy, dx, dy]) => {
    ctx.save();
    ctx.strokeStyle = tealGl; ctx.lineWidth = 1.5; ctx.lineCap = 'square';
    ctx.beginPath();
    ctx.moveTo(hx, hy + hudSz * dy); ctx.lineTo(hx, hy); ctx.lineTo(hx + hudSz * dx, hy);
    ctx.stroke();
    ctx.restore();
  });

  let y = padTop;

  // ── TOP BAR ──
  ctx.letterSpacing = '0.28em';
  ctx.font = mono(13, 400);
  ctx.fillStyle = ink3;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('SIRAT · GHIDHA · EXERCISE LOG', padX, y + 18);

  const badgeLabel = (withTrainer ? 'PT Session' : 'Strength') + ' · ' + dateStr;
  ctx.letterSpacing = '0.24em';
  ctx.font = mono(13, 500);
  const dotR = 3.5, dotGap = 10;
  const bTW = ctx.measureText(badgeLabel).width;
  const bPX = 18, bPY = 10;
  const badgeW = bPX * 2 + dotR * 2 + dotGap + bTW;
  const badgeH = bPY * 2 + 16;
  const badgeX = W - padX - badgeW;
  const badgeY = y;
  ctx.fillStyle = 'rgba(62,207,184,0.06)';
  ctx.strokeStyle = 'rgba(62,207,184,0.35)';
  ctx.lineWidth = 0.5;
  ctx.beginPath(); ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 6); ctx.fill(); ctx.stroke();
  ctx.fillStyle = teal; ctx.shadowColor = teal; ctx.shadowBlur = 10;
  ctx.beginPath(); ctx.arc(badgeX + bPX + dotR, badgeY + badgeH/2, dotR, 0, Math.PI*2); ctx.fill();
  ctx.shadowBlur = 0;
  ctx.fillStyle = teal; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
  ctx.fillText(badgeLabel, badgeX + bPX + dotR*2 + dotGap, badgeY + badgeH/2);

  y += Math.max(36, badgeH) + 26;

  // ── WORKOUT TITLE ──
  ctx.letterSpacing = '-0.04em';
  ctx.font = fraun(64, 300);
  ctx.fillStyle = ink1;
  ctx.shadowColor = 'rgba(62,207,184,0.15)'; ctx.shadowBlur = 60;
  ctx.textAlign = 'left'; ctx.textBaseline = 'top';
  ctx.fillText(workoutName, padX, y);
  ctx.shadowBlur = 0;
  y += 58;

  // Meta row
  ctx.letterSpacing = '0.2em'; ctx.textBaseline = 'middle';
  const metaY = y + 7;
  const metaItems = [];
  if (startTime && endTime) metaItems.push({ t: startTime + ' – ' + endTime, b: false });
  if (durationMin) {
    const dh = Math.floor(durationMin/60), dm = durationMin % 60;
    metaItems.push({ t: (dh > 0 ? dh + 'h ' : '') + dm + 'm duration', b: true });
  }
  if (totalExercises > 0) metaItems.push({ t: totalExercises + ' exercises', b: true });
  if (totalSets > 0) metaItems.push({ t: totalSets + ' sets', b: true });
  let mx = padX;
  metaItems.forEach((item, i) => {
    if (i > 0) {
      ctx.fillStyle = ink4;
      ctx.beginPath(); ctx.arc(mx + 10, metaY, 2, 0, Math.PI*2); ctx.fill();
      mx += 20;
    }
    ctx.font = item.b ? mono(13, 600) : mono(13, 400);
    ctx.fillStyle = item.b ? ink1 : ink2;
    ctx.letterSpacing = '0.2em'; ctx.textAlign = 'left';
    ctx.fillText(item.t, mx, metaY);
    mx += ctx.measureText(item.t).width;
  });
  y += 14 + 26;

  // ── HERO STAT CARDS ──
  const hGap = 16, hW = (cW - hGap*3)/4, hH = 126, hR = 12;
  const heroCards = [
    { tag: 'TOTAL VOLUME', val: volume >= 1000 ? (volume/1000).toFixed(1) : String(Math.round(volume)), unit: volume >= 1000 ? ' t' : ' kg', sub: 'lifted today', col: teal, glow: tealGl },
    { tag: 'CALORIES', val: String(calories || 0), unit: ' kcal', sub: 'active burn', col: amber, glow: amberGl },
    { tag: 'AVG HEART RATE', val: avgHr > 0 ? String(Math.round(avgHr)) : '—', unit: avgHr > 0 ? ' bpm' : '', sub: maxHr > 0 ? 'peak ' + Math.round(maxHr) + ' bpm' : 'no HR data', col: coral, glow: coralGl },
    { tag: 'SETS', val: String(totalSets), unit: '', sub: totalExercises + ' exercises', col: purple, glow: purpGl },
  ];
  heroCards.forEach((card, i) => {
    const hx = padX + i*(hW+hGap), hy = y;
    ctx.fillStyle = 'rgba(255,255,255,0.025)'; ctx.strokeStyle = lineHi; ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.roundRect(hx, hy, hW, hH, hR); ctx.fill(); ctx.stroke();
    ctx.strokeStyle = card.col; ctx.shadowColor = card.col; ctx.shadowBlur = 12; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(hx+14, hy); ctx.lineTo(hx+hW-14, hy); ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.font = mono(10, 500); ctx.letterSpacing = '0.3em';
    ctx.fillStyle = card.col; ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    ctx.fillText(card.tag, hx+18, hy+18);
    const valY = hy + 18 + 12 + 8;
    ctx.font = fraun(50, 300); ctx.letterSpacing = '-0.04em';
    ctx.fillStyle = ink1; ctx.shadowColor = card.glow; ctx.shadowBlur = 28; ctx.textBaseline = 'top';
    ctx.fillText(card.val, hx+18, valY); ctx.shadowBlur = 0;
    if (card.unit) {
      const vw = ctx.measureText(card.val).width;
      ctx.font = fraun(20, 300); ctx.letterSpacing = '0'; ctx.fillStyle = ink3;
      ctx.fillText(card.unit, hx+18+vw, valY+8);
    }
    ctx.font = mono(10, 400); ctx.letterSpacing = '0.18em';
    ctx.fillStyle = ink3; ctx.textBaseline = 'bottom';
    ctx.fillText(card.sub.toUpperCase(), hx+18, hy+hH-14);
  });
  y += hH + 26;

  // ── RULE ──
  const rGrad = ctx.createLinearGradient(padX, 0, padX+cW, 0);
  rGrad.addColorStop(0, 'rgba(0,0,0,0)'); rGrad.addColorStop(0.2, teal);
  rGrad.addColorStop(0.5, lineHi); rGrad.addColorStop(0.8, amber); rGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.strokeStyle = rGrad; ctx.lineWidth = 0.5;
  ctx.beginPath(); ctx.moveTo(padX, y); ctx.lineTo(padX+cW, y); ctx.stroke();
  ctx.fillStyle = ink1; ctx.shadowColor = 'rgba(255,255,255,0.5)'; ctx.shadowBlur = 8;
  ctx.beginPath(); ctx.arc(W/2, y, 2.5, 0, Math.PI*2); ctx.fill(); ctx.shadowBlur = 0;
  y += 1 + 20;

  // ── SECTION HEADER ──
  ctx.font = mono(11, 600); ctx.letterSpacing = '0.24em';
  ctx.fillStyle = teal; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
  ctx.fillText('01', padX, y+8);
  ctx.strokeStyle = lineHi; ctx.lineWidth = 0.5;
  ctx.beginPath(); ctx.moveTo(padX+28, y+8); ctx.lineTo(padX+cW-310, y+8); ctx.stroke();
  ctx.font = mono(10, 400); ctx.letterSpacing = '0.26em'; ctx.fillStyle = ink3; ctx.textAlign = 'right';
  ctx.fillText('TOP SETS · BEST PERFORMANCE PER MOVEMENT', padX+cW, y+8);
  y += 18 + 12;

  // ── EXERCISE GRID ──
  const maxEx = Math.min((topSets || []).length, 6);
  const exCols = 3, exGap = 10;
  const exW = (cW - exGap*(exCols-1))/exCols, exH = 76;

  for (let i = 0; i < maxEx; i++) {
    const ex = topSets[i];
    const ec = i % exCols, er = Math.floor(i/exCols);
    const ex_x = padX + ec*(exW+exGap), ex_y = y + er*(exH+exGap);
    const [exCol, exGlow] = colorForMuscle(ex.muscleGroup);

    ctx.fillStyle = 'rgba(255,255,255,0.02)'; ctx.strokeStyle = lineC; ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.roundRect(ex_x, ex_y, exW, exH, 10); ctx.fill(); ctx.stroke();

    ctx.fillStyle = exCol; ctx.shadowColor = exGlow; ctx.shadowBlur = 8;
    ctx.beginPath(); ctx.roundRect(ex_x, ex_y+12, 2, exH-24, 1); ctx.fill(); ctx.shadowBlur = 0;

    ctx.font = fraun(17, 400); ctx.letterSpacing = '-0.01em';
    ctx.fillStyle = ink1; ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    const nameX = ex_x + 14, nameMaxW = exW - 92;
    let nm = ex.name || '';
    while (ctx.measureText(nm).width > nameMaxW && nm.length > 8) nm = nm.slice(0, -2) + '…';
    ctx.fillText(nm, nameX, ex_y + 12);

    ctx.font = mono(11, 400); ctx.letterSpacing = '0.12em'; ctx.fillStyle = ink3;
    const dp = [ex.setsCount + ' sets'];
    if (ex.avgWeight) dp.push(ex.avgWeight + ' kg avg');
    ctx.fillText(dp.join(' · '), nameX, ex_y + 12 + 20 + 3);

    const rx = ex_x + exW - 12;
    if (ex.bestWeight != null && ex.bestWeight > 0) {
      ctx.font = fraun(28, 300); ctx.letterSpacing = '-0.03em';
      ctx.fillStyle = ink1; ctx.textAlign = 'right'; ctx.textBaseline = 'top';
      ctx.fillText(String(ex.bestWeight), rx-18, ex_y+11);
      ctx.font = fraun(14, 300); ctx.letterSpacing = '0'; ctx.fillStyle = ink3;
      ctx.textAlign = 'right'; ctx.fillText('kg', rx, ex_y+17);
      if (ex.bestReps) {
        ctx.font = mono(11, 400); ctx.letterSpacing = '0.12em'; ctx.fillStyle = ink3;
        ctx.textAlign = 'right'; ctx.fillText('× ' + ex.bestReps + ' reps', rx, ex_y+40);
      }
    } else if (ex.bestReps) {
      ctx.font = fraun(26, 300); ctx.letterSpacing = '-0.03em';
      ctx.fillStyle = ink1; ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
      ctx.fillText('× ' + ex.bestReps, rx, ex_y + exH/2);
    }

    if (ex.isNewPR) {
      ctx.font = mono(9, 500); ctx.letterSpacing = '0.22em';
      const prText = 'NEW PR', prTW = ctx.measureText(prText).width + 12;
      ctx.fillStyle = 'rgba(224,160,48,0.08)'; ctx.strokeStyle = 'rgba(224,160,48,0.45)'; ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.roundRect(rx-prTW, ex_y+exH-23, prTW, 13, 2); ctx.fill(); ctx.stroke();
      ctx.fillStyle = amber; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(prText, rx - prTW/2, ex_y+exH-16.5);
    }
  }

  const exRows = maxEx > 0 ? Math.ceil(maxEx/exCols) : 0;
  y += exRows*(exH+exGap) - (maxEx > 0 ? exGap : 0) + 20;

  // ── BOTTOM: HR CARD + HADITH CARD ──
  const bottomH = H - padBot - y;
  const halfW = (cW - 16) / 2;

  // HR card
  const hrX = padX, hrY = y, hrPad = 20;
  ctx.fillStyle = 'rgba(255,255,255,0.02)'; ctx.strokeStyle = lineHi; ctx.lineWidth = 0.5;
  ctx.beginPath(); ctx.roundRect(hrX, hrY, halfW, bottomH, 12); ctx.fill(); ctx.stroke();

  ctx.font = mono(11, 600); ctx.letterSpacing = '0.24em';
  ctx.fillStyle = teal; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
  ctx.fillText('02', hrX+hrPad, hrY+hrPad+5);
  ctx.strokeStyle = lineHi; ctx.lineWidth = 0.5;
  ctx.beginPath(); ctx.moveTo(hrX+hrPad+24, hrY+hrPad+5); ctx.lineTo(hrX+halfW-hrPad, hrY+hrPad+5); ctx.stroke();
  ctx.font = mono(10, 400); ctx.letterSpacing = '0.26em'; ctx.fillStyle = ink3; ctx.textAlign = 'right';
  ctx.fillText('HEART RATE', hrX+halfW-hrPad, hrY+hrPad+5);

  let hrCursor = hrY + hrPad + 24;
  ctx.font = mono(10, 400); ctx.letterSpacing = '0.28em';
  ctx.fillStyle = ink3; ctx.textAlign = 'left'; ctx.textBaseline = 'top';
  ctx.fillText('AVERAGE', hrX+hrPad, hrCursor);
  hrCursor += 14;

  if (avgHr > 0) {
    ctx.font = fraun(62, 300); ctx.letterSpacing = '-0.04em';
    ctx.fillStyle = coral; ctx.shadowColor = coralGl; ctx.shadowBlur = 30;
    ctx.textBaseline = 'top'; ctx.textAlign = 'left';
    ctx.fillText(String(Math.round(avgHr)), hrX+hrPad, hrCursor); ctx.shadowBlur = 0;
    const avgW = ctx.measureText(String(Math.round(avgHr))).width;
    ctx.font = fraun(22, 300); ctx.letterSpacing = '0'; ctx.fillStyle = ink3;
    ctx.fillText(' bpm', hrX+hrPad+avgW, hrCursor+11);
    if (maxHr > 0) {
      const subX = hrX + hrPad + avgW + 36;
      ctx.font = mono(10, 400); ctx.letterSpacing = '0.2em'; ctx.fillStyle = ink4;
      ctx.fillText('PEAK', subX, hrCursor+6);
      ctx.font = fraun(26, 300); ctx.letterSpacing = '-0.03em'; ctx.fillStyle = ink1;
      ctx.fillText(String(Math.round(maxHr)), subX, hrCursor+20);
      const pkW = ctx.measureText(String(Math.round(maxHr))).width;
      ctx.font = fraun(12, 300); ctx.letterSpacing = '0'; ctx.fillStyle = ink3;
      ctx.fillText(' bpm', subX+pkW, hrCursor+28);
    }
    hrCursor += 62 + 14;
  } else {
    ctx.font = fraun(44, 300); ctx.fillStyle = ink3; ctx.textBaseline = 'top';
    ctx.fillText('—', hrX+hrPad, hrCursor); hrCursor += 52;
  }

  // HR zones
  const zones = [
    { label: 'PEAK',     col: coral, pct: 0 },
    { label: 'HARD',     col: amber, pct: 0 },
    { label: 'MODERATE', col: teal,  pct: 0 },
    { label: 'EASY',     col: ink2,  pct: 0 },
  ];
  if (hrZoneSeconds) {
    const z = hrZoneSeconds;
    const tot = (z.z1||0)+(z.z2||0)+(z.z3||0)+(z.z4||0)+(z.z5||0) || 1;
    zones[0].pct = Math.round((z.z5||0)/tot*100);
    zones[1].pct = Math.round((z.z4||0)/tot*100);
    zones[2].pct = Math.round((z.z3||0)/tot*100);
    zones[3].pct = Math.round(((z.z1||0)+(z.z2||0))/tot*100);
  }

  const zH = 18, zGp = 9, zBarH = 5, zLblW = 90, zPctW = 38;
  const zBarW = halfW - hrPad*2 - zLblW - zPctW - 8;
  zones.forEach((zone, i) => {
    const zy = hrCursor + i*(zH+zGp);
    if (zy + zH > hrY + bottomH - hrPad) return;
    ctx.font = mono(11, 400); ctx.letterSpacing = '0.14em';
    ctx.fillStyle = zone.col; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    ctx.fillText(zone.label, hrX+hrPad, zy+zH/2);
    const bx = hrX + hrPad + zLblW;
    ctx.fillStyle = 'rgba(255,255,255,0.06)';
    ctx.beginPath(); ctx.roundRect(bx, zy+(zH-zBarH)/2, zBarW, zBarH, 3); ctx.fill();
    if (zone.pct > 0) {
      const fw = Math.max(4, zBarW*zone.pct/100);
      ctx.fillStyle = zone.col; ctx.shadowColor = zone.col; ctx.shadowBlur = 6;
      ctx.beginPath(); ctx.roundRect(bx, zy+(zH-zBarH)/2, fw, zBarH, 3); ctx.fill(); ctx.shadowBlur = 0;
    }
    ctx.font = mono(11, 500); ctx.letterSpacing = '0.12em';
    ctx.fillStyle = ink2; ctx.textAlign = 'right'; ctx.shadowBlur = 0;
    ctx.fillText(zone.pct + '%', hrX+halfW-hrPad, zy+zH/2);
  });

  // Hadith card
  const hdX = padX + halfW + 16, hdY = y, hdW = halfW, hdPad = 24;
  const hdGrad = ctx.createLinearGradient(hdX, hdY, hdX+hdW, hdY+bottomH);
  hdGrad.addColorStop(0, 'rgba(62,207,184,0.05)'); hdGrad.addColorStop(1, 'rgba(224,160,48,0.03)');
  ctx.fillStyle = hdGrad; ctx.strokeStyle = lineHi; ctx.lineWidth = 0.5;
  ctx.beginPath(); ctx.roundRect(hdX, hdY, hdW, bottomH, 12); ctx.fill(); ctx.stroke();

  ctx.save();
  ctx.globalAlpha = 0.5;
  const hdLGrad = ctx.createLinearGradient(hdX, 0, hdX+hdW, 0);
  hdLGrad.addColorStop(0, teal); hdLGrad.addColorStop(1, amber);
  ctx.strokeStyle = hdLGrad; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(hdX+12, hdY); ctx.lineTo(hdX+hdW-12, hdY); ctx.stroke();
  ctx.restore();

  ctx.font = mono(10, 500); ctx.letterSpacing = '0.3em';
  ctx.fillStyle = teal; ctx.textAlign = 'left'; ctx.textBaseline = 'top';
  ctx.fillText('STRENGTH OF FAITH · MUSLIM 2664', hdX+hdPad, hdY+hdPad);

  const arY = hdY + hdPad + 16 + 14;
  ctx.font = "400 26px 'Amiri', serif"; ctx.letterSpacing = '0';
  ctx.fillStyle = ink1; ctx.textAlign = 'center'; ctx.textBaseline = 'top';
  ctx.fillText('المؤمن القويّ خيرٌ وأحبُ إلى الله', hdX+hdW/2, arY);
  ctx.fillText('منا لمؤمن الضعيف', hdX+hdW/2, arY+36);

  const enText = '"The strong believer is better and more beloved to Allah than the weak believer."';
  ctx.font = instr(16); ctx.letterSpacing = '0';
  ctx.fillStyle = ink2; ctx.textAlign = 'center'; ctx.textBaseline = 'top';
  const enLines = wrap(ctx, enText, instr(16), hdW - hdPad*2);
  const enY = arY + 36 + 32;
  enLines.slice(0, 3).forEach((line, i) => ctx.fillText(line, hdX+hdW/2, enY+i*23));

  // Footer strip inside hadith card
  const hdFT = hdY + bottomH - hdPad - 46;
  ctx.strokeStyle = 'rgba(255,255,255,0.07)'; ctx.lineWidth = 0.5;
  ctx.beginPath(); ctx.moveTo(hdX+hdPad, hdFT); ctx.lineTo(hdX+hdW-hdPad, hdFT); ctx.stroke();

  ctx.font = mono(10, 400); ctx.letterSpacing = '0.22em';
  ctx.fillStyle = ink3; ctx.textAlign = 'left'; ctx.textBaseline = 'top';
  ctx.fillText('TRACK YOUR DEEN', hdX+hdPad, hdFT+10);
  ctx.font = fraun(20, 300, true); ctx.letterSpacing = '-0.01em';
  ctx.fillStyle = teal; ctx.fillText('sirat.co.za', hdX+hdPad, hdFT+27);

  // Mini waveform
  const wBars = [8, 16, 24, 20, 28, 18, 12, 7];
  const wBCols = [teal,teal,teal,teal,amber,amber,amber,amber];
  const wBAlpha = [0.5,0.7,1.0,0.8,1.0,0.8,0.6,0.4];
  const wBW = 3, wBGap = 3;
  const wfW = wBars.length*(wBW+wBGap)-wBGap;
  const wfRX = hdX + hdW - hdPad;
  const wfBY = hdFT + 46;
  wBars.forEach((bh, i) => {
    ctx.save();
    ctx.globalAlpha = wBAlpha[i];
    ctx.fillStyle = wBCols[i];
    if (i === 2 || i === 4) { ctx.shadowColor = wBCols[i]; ctx.shadowBlur = 8; }
    ctx.beginPath(); ctx.roundRect(wfRX-wfW+i*(wBW+wBGap), wfBY-bh, wBW, bh, 2); ctx.fill();
    ctx.restore();
  });

  return c;
}
